import type { Bilingual } from '../lib/LanguageContext';
import type { LogoutReasonKey } from '../lib/logout-reason';

export interface LoginCopy {
  welcome: Bilingual;
  salutation: Bilingual;
  subtitle: Bilingual;
  emailLabel: Bilingual;
  passwordLabel: Bilingual;
  identifierLabel: Bilingual;
  identifierPlaceholder: Bilingual;
  emailInvalid: Bilingual;
  identifierRequired: Bilingual;
  passwordRequired: Bilingual;
  rememberLabel: Bilingual;
  submitLabel: Bilingual;
  forgotLabel: Bilingual;
  resetTitle: Bilingual;
  resetSubtitle: Bilingual;
  sendLink: Bilingual;
  backToLogin: Bilingual;
  switcherText: Bilingual;
  switchToParticipant: Bilingual;
  resetSuccess: Bilingual;
  authFailed: Bilingual;
  migrationVerificationRequired: Bilingual;
}

export interface BrandingCopy {
  title: Bilingual;
  tagline: Bilingual;
  motto: Bilingual;
}

export interface DashboardCopy {
  salam: Bilingual;
  ahlan: Bilingual;
  tagline: Bilingual;
  description: Bilingual;
}

export const brandingCopy: BrandingCopy = {
  title: {
    id: 'TSL Learning Portal',
    ar: 'بوابة التعلّم',
  },
  tagline: {
    id: 'Mengelola pembelajaran Islam secara terstruktur, amanah, dan berkelanjutan melalui sistem digital yang rapi, mudah digunakan, dan bermakna.',
    ar: 'إدارة التعلم الإسلامي بطريقة منظمة وموثوقة ومستدامة عبر نظام رقمي أنيق وسهل الاستخدام وهادف.',
  },
  motto: {
    id: 'Ilmu • Amanah • Keteraturan',
    ar: 'العلم • الأمانة • الترتيب',
  },
};

export const loginCopy: Record<'admin' | 'pengajar', LoginCopy> = {
  admin: {
    welcome: {
      id: 'Selamat Datang Kembali',
      ar: 'مرحباً بك مجدداً',
    },
    salutation: {
      id: 'Ahlan wa Sahlan, Admin TSL',
      ar: 'أهلاً وسهلاً، مدير النظام',
    },
    subtitle: {
      id: 'Akses portal untuk mengelola program, peserta, pengajar, materi, evaluasi, dan laporan pembelajaran.',
      ar: 'الدخول ke البوابة لإدارة البرامج، المشاركين، المعلمين، المواد، التقييمات، والتقارير.',
    },
    emailLabel: {
      id: 'Alamat Email',
      ar: 'البريد الإلكتروني',
    },
    passwordLabel: {
      id: 'Kata Sandi',
      ar: 'كلمة المرور',
    },
    identifierLabel: {
      id: 'Email / NIP / No. HP',
      ar: 'البريد / الرقم الوظيفي / الهاتف',
    },
    identifierPlaceholder: {
      id: 'email@domain.com, 1985xxxx, atau 0812...',
      ar: 'email@domain.com، 1985xxxx، أو 0812...',
    },
    emailInvalid: {
      id: 'Masukkan alamat email yang valid.',
      ar: 'أدخل عنوان بريد إلكتروني صالحًا.',
    },
    identifierRequired: {
      id: 'Email, NIP, atau nomor HP wajib diisi.',
      ar: 'البريد أو الرقم الوظيفي أو رقم الهاتف مطلوب.',
    },
    passwordRequired: { id: 'Kata sandi wajib diisi.', ar: 'كلمة المرور مطلوبة.' },
    rememberLabel: {
      id: 'Ingat saya',
      ar: 'تذكرني',
    },
    submitLabel: {
      id: 'Masuk ke Dashboard Admin',
      ar: 'تسجيل الدخول إلى لوحة التحكم',
    },
    forgotLabel: {
      id: 'Lupa kata sandi?',
      ar: 'نسيت كلمة المرور؟',
    },
    resetTitle: {
      id: 'Reset Kata Sandi',
      ar: 'إعادة تعيين كلمة المرور',
    },
    resetSubtitle: {
      id: 'Masukkan email Anda untuk menerima tautan pemulihan.',
      ar: 'أدخل بريدك الإلكتروني لتلقي رابط الاسترداد.',
    },
    sendLink: {
      id: 'Kirim Tautan Pemulihan',
      ar: 'إرسال رابط الاسترداد',
    },
    backToLogin: {
      id: 'Kembali ke Halaman Masuk',
      ar: 'العودة إلى تسجيل الدخول',
    },
    switcherText: {
      id: 'Apakah Anda peserta atau pengajar?',
      ar: 'هل أنت مشارك أم معلم؟',
    },
    switchToParticipant: {
      id: 'Beralih ke Portal Peserta',
      ar: 'الانتقال إلى بوابة المشاركين',
    },
    resetSuccess: {
      id: 'Tautan reset kata sandi telah dikirim ke email Anda.',
      ar: 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.',
    },
    authFailed: {
      id: 'Autentikasi gagal',
      ar: 'فشل المصادقة',
    },
    migrationVerificationRequired: {
      id: 'Akun Anda telah dipindahkan ke sistem login baru. Periksa email untuk verifikasi, lalu masuk kembali.',
      ar: 'تم نقل حسابك إلى نظام تسجيل الدخول الجديد. يرجى التحقق من بريدك الإلكتروني ثم تسجيل الدخول مرة أخرى.',
    },
  },
  pengajar: {
    welcome: {
      id: 'Selamat Datang Kembali',
      ar: 'مرحباً بك مجدداً',
    },
    salutation: {
      id: 'Ahlan wa Sahlan, Ustadz/Ustadzah',
      ar: 'أهلاً وسهلاً، المعلم / المعلمة',
    },
    subtitle: {
      id: 'Akses ruang pengajar untuk mengelola kelas, materi, presensi, tugas, kuis, dan perkembangan peserta.',
      ar: 'الدخول إلى مساحة المعلم لإدارة الفصول، المواد، الحضور، الواجبات، الاختبارات وتقدم المشاركين.',
    },
    emailLabel: {
      id: 'Alamat Email',
      ar: 'البريد الإلكتروني',
    },
    passwordLabel: {
      id: 'Kata Sandi',
      ar: 'كلمة المرور',
    },
    identifierLabel: {
      id: 'Email / NIP / No. HP',
      ar: 'البريد / الرقم الوظيفي / الهاتف',
    },
    identifierPlaceholder: {
      id: 'email@domain.com, 1985xxxx, atau 0812...',
      ar: 'email@domain.com، 1985xxxx، أو 0812...',
    },
    emailInvalid: {
      id: 'Masukkan alamat email yang valid.',
      ar: 'أدخل عنوان بريد إلكتروني صالحًا.',
    },
    identifierRequired: {
      id: 'Email, NIP, atau nomor HP wajib diisi.',
      ar: 'البريد أو الرقم الوظيفي أو رقم الهاتف مطلوب.',
    },
    passwordRequired: { id: 'Kata sandi wajib diisi.', ar: 'كلمة المرور مطلوبة.' },
    rememberLabel: {
      id: 'Tetap masuk',
      ar: 'تذكرني',
    },
    submitLabel: {
      id: 'Masuk ke Ruang Pengajar',
      ar: 'الدخول إلى مساحة المعلم',
    },
    forgotLabel: {
      id: 'Lupa kata sandi?',
      ar: 'نسيت كلمة المرور؟',
    },
    resetTitle: {
      id: 'Reset Kata Sandi',
      ar: 'إعادة تعيين كلمة المرور',
    },
    resetSubtitle: {
      id: 'Masukkan email Anda untuk menerima tautan pemulihan.',
      ar: 'أدخل بريدك الإلكتروني لتلقي رابط الاسترداد.',
    },
    sendLink: {
      id: 'Kirim Tautan Pemulihan',
      ar: 'إرسال رابط الاسترداد',
    },
    backToLogin: {
      id: 'Kembali ke Halaman Masuk',
      ar: 'العودة إلى تسجيل الدخول',
    },
    switcherText: {
      id: 'Apakah Anda peserta atau pengajar?',
      ar: 'هل أنت مشارك أم معلم؟',
    },
    switchToParticipant: {
      id: 'Beralih ke Portal Peserta',
      ar: 'الانتقال إلى بوابة المشاركين',
    },
    resetSuccess: {
      id: 'Tautan reset kata sandi telah dikirim ke email Anda.',
      ar: 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.',
    },
    authFailed: {
      id: 'Autentikasi gagal',
      ar: 'فشل المصادقة',
    },
    migrationVerificationRequired: {
      id: 'Akun Anda telah dipindahkan ke sistem login baru. Periksa email untuk verifikasi, lalu masuk kembali.',
      ar: 'تم نقل حسابك إلى نظام تسجيل الدخول الجديد. يرجى التحقق من بريدك الإلكتروني ثم تسجيل الدخول مرة أخرى.',
    },
  },
};

export const dashboardCopy: Record<'admin' | 'pengajar', DashboardCopy> = {
  admin: {
    salam: {
      id: '',
      ar: 'السلام عليكم ورحمة الله وبركاته',
    },
    ahlan: {
      id: 'Ahlan wa Sahlan, Admin TSL',
      ar: 'أهلاً وسهلاً، مدير النظام',
    },
    tagline: {
      id: 'Semoga Allah mudahkan amanah pengelolaan pembelajaran hari ini.',
      ar: 'نسأل الله أن ييسر لكم أمانة إدارة التعlem اليوم.',
    },
    description: {
      id: 'Silakan pantau program, kelas, peserta, pengajar, dan evaluasi melalui dashboard ini.',
      ar: 'يرجى مراقبة البرامج والفصول والمشاركين والمعلمين والتقييمات عبر هذه اللوحة.',
    },
  },
  pengajar: {
    salam: {
      id: '',
      ar: 'السلام عليكم ورحمة الله وبركاته',
    },
    ahlan: {
      id: 'Marhaban, Ustadz/Ustadzah',
      ar: 'مرحباً يا أستاذ / أستاذة',
    },
    tagline: {
      id: 'Semoga Allah memberkahi ilmu dan waktu antum.',
      ar: 'بارك الله في علمكم ووقتكم.',
    },
    description: {
      id: 'Silakan kelola kelas, materi, tugas, kuis, dan perkembangan peserta melalui ruang pengajar ini.',
      ar: 'يرجى إدارة الفصول والمواد والواجبات والاختبارات وتقدم المشاركين عبر مساحة المعلم هذه.',
    },
  },
};

/**
 * Bilingual copy shown by the `LogoutReasonBanner` component on the
 * login / reset-password / verify-email screens when a 401 reason has
 * been stashed into sessionStorage by the api-client's `on401`
 * handler. Keys must match `LogoutReasonKey` in
 * `../lib/logout-reason.ts`. Exported as a separate named export
 * (not inside the `copy` object) to match the web-admin
 * `LogoutReasonBanner` import shape.
 */
export const loginBanner: Record<LogoutReasonKey, Bilingual> = {
  account_inactive: {
    id: 'Akun Anda tidak aktif. Silakan hubungi admin untuk mengaktifkannya kembali.',
    ar: 'حسابك غير نشط. يرجى التواصل مع المسؤول لإعادة التفعيل.',
  },
  token_invalid: {
    id: 'Sesi Anda tidak valid. Silakan masuk kembali.',
    ar: 'جلستك غير صالحة. يرجى تسجيل الدخول مرة أخرى.',
  },
  session_expired: {
    id: 'Sesi Anda telah berakhir. Silakan masuk kembali untuk melanjutkan.',
    ar: 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى للمتابعة.',
  },
  idle_timeout: {
    id: 'Sesi Anda telah berakhir otomatis karena tidak ada aktivitas untuk menghemat sumber daya server. Silakan masuk kembali.',
    ar: 'انتهت صلاحية جلستك تلقائيًا بسبب عدم وجود نشاط لتوفير موارد الخادم. يرجى تسجيل الدخول مرة أخرى.',
  },
  unknown: {
    id: 'Anda telah keluar dari sesi. Silakan masuk kembali untuk melanjutkan.',
    ar: 'تم تسجيل خروجك. يرجى تسجيل الدخول مرة أخرى للمتابعة.',
  },
};

export const copy = {
  common: {
    loading: { id: 'Memuat...', ar: 'جاري التحميل...' },
    cancel: { id: 'Batal', ar: 'إلغاء' },
    save: { id: 'Simpan', ar: 'حفظ' },
    error: { id: 'Terjadi Kesalahan', ar: 'حدث خطأ' },
    status: { id: 'Status', ar: 'الحالة' },
    code: { id: 'Kode', ar: 'الرمز' },
    previous: { id: 'Sebelumnya', ar: 'السابق' },
    next: { id: 'Selanjutnya', ar: 'التالي' },
    total: { id: 'Total', ar: 'الإجمالي' },
    material: { id: 'materi', ar: 'مادة' },
    close: { id: 'Tutup', ar: 'إغلاق' },
    delete: { id: 'Hapus', ar: 'حذف' },
    edit: { id: 'Edit', ar: 'تعديل' },
    notSet: { id: 'Belum diisi', ar: 'لم يتم الإدخال' },
  },
  sidebar: {
    brandName: { id: 'Tarbiyah Panel', ar: 'لوحة التربية' },
    brandSub: { id: 'Heritage Learning', ar: 'التعليم التراثي' },
    dashboard: { id: 'Dashboard', ar: 'لوحة القيادة' },
    registrations: { id: 'Pendaftaran', ar: 'التسجيلات' },
    programs: { id: 'Program Belajar', ar: 'البرامج الدراسية' },
    assessments: { id: 'Evaluasi Belajar', ar: 'التقييمات' },
    communication: { id: 'Grup WhatsApp', ar: 'مجموعات الواتساب' },
    announcements: { id: 'Pengumuman', ar: 'الإعلانات' },
    monitoring: { id: 'Pemantauan', ar: 'المراقبة' },
    crm: { id: 'CRM & Retensi Peserta', ar: 'إدارة ومتابعة الدارسين' },
    users: { id: 'Manajemen Pengguna', ar: 'إدارة المستخدمين' },
    intelligence: { id: 'Laporan & Statistik', ar: 'التقارير والإحصاءات' },
    reviewQueue: { id: 'Antrean Verifikasi', ar: 'طابور المراجعة' },
    curriculum: { id: 'Kurikulum & Materi', ar: 'المناهj الدراسية' },
    settings: { id: 'Pengaturan', ar: 'الإعدادات' },
    logout: { id: 'Keluar', ar: 'تسجيل الخروج' },
  },
  dashboard: {
    today: { id: 'Hari ini', ar: 'اليوم' },
    newProgram: { id: 'Program Baru', ar: 'برنامج جديد' },
    createEvaluation: { id: 'Buat Evaluasi', ar: 'إنشاء تقييم' },
    approveRegistration: { id: 'Setujui Pendaftaran', ar: 'الموافقة على التسجيل' },
    operationalKPIs: { id: 'KPI Operasional', ar: 'مؤشرات الأداء التشغيلية' },
    totalParticipants: { id: 'Total Peserta', ar: 'إجمالي المشاركين' },
    pendingRegistrations: { id: 'Pendaftaran Tertunda', ar: 'التسجيلات المعلقة' },
    activeParticipants: { id: 'Peserta Aktif', ar: 'المشاركون النشطون' },
    materialsActive: { id: 'Materi Aktif', ar: 'المواد النشطة' },
    inProgress: { id: 'Sedang Berjalan', ar: 'قيد التنفيذ' },
    needsAction: { id: 'Perlu Tindakan', ar: 'بحاجة إلى اتخاذ إجراء' },
    viewAllTasks: { id: 'Lihat Semua Tugas', ar: 'عرض جميع المهام' },
    allCaughtUp: {
      id: 'Semua tugas selesai! Tidak ada tugas tertunda.',
      ar: 'تم الانتهاء من كل شيء! لا توجد مهام معلقة.',
    },
    completionRate: { id: 'Tingkat Penyelesaian', ar: 'معدل الإنجاز' },
    finalEvaluationsDone: { id: 'Evaluasi Akhir Selesai', ar: 'التقييمات النهائية المكتملة' },
    totalComprehensive: { id: 'Total Komprehensif', ar: 'إجمالي الشامل' },
    programProgress: { id: 'Perkembangan Program', ar: 'تقدم البرامج' },
    noActivePrograms: { id: 'Belum ada program aktif.', ar: 'لا توجد برامج نشطة بعد.' },
    systemStatus: { id: 'Status Sistem', ar: 'حالة النظام' },
    details: { id: 'Detail', ar: 'تفاصيل' },
    adminGuidance: { id: 'Panduan Admin', ar: 'توجيهات المسؤول' },
    adminGuidanceDesc: {
      id: "Selamat datang di Dashboard Admin LMS. Gunakan KPI di atas untuk memantau status program dan daftar 'Perlu Tindakan' untuk memproses permohonan peserta.",
      ar: "مرحباً بك في لوحة تحكم مدير النظام. استخدم مؤشرات الأداء أعلاه لمراقبة حالة البرامج وقائمة 'بحاجة إلى اتخاذ إجراء' لمعالجة طلبات المشاركين.",
    },
    loadingDashboardTitle: {
      id: 'Memuat Ringkasan Dashboard & Statistik...',
      ar: 'جاري تحميل ملخص لوحة القيادة والإحصائيات...',
    },
    loadingDashboardDesc: {
      id: 'Mengambil data peserta aktif, progres materi, evaluasi, dan ringkasan sistem.',
      ar: 'جاري جلب بيانات المشاركين النشطين والتقدم في المواد والتقييمات وحالة النظام.',
    },
    loadingRealtimeSync: {
      id: 'Sinkronisasi Data Realtime',
      ar: 'مزامنة البيانات الحية',
    },
    failedLoad: { id: 'Gagal memuat data dashboard', ar: 'فشل في تحميل بيانات لوحة القيادة' },
    quickActions: { id: 'Aksi Cepat', ar: 'الإجراءات السريعة' },
    operationalFocus: { id: 'Fokus operasional hari ini', ar: 'تركيز العمليات اليوم' },
    operationalFocusDesc: {
      id: 'Mulai dari tugas yang membutuhkan keputusan Anda.',
      ar: 'ابدأ بالمهام التي تحتاج إلى قرارك.',
    },
    noPendingPriority: {
      id: 'Tidak ada prioritas yang menunggu saat ini.',
      ar: 'لا توجد أولويات معلقة حالياً.',
    },
    refreshOverview: { id: 'Segarkan ringkasan', ar: 'تحديث الملخص' },
    overviewRefreshing: { id: 'Memperbarui data', ar: 'جارٍ تحديث البيانات' },
    reviewPriority: { id: 'Tinjau prioritas', ar: 'مراجعة الأولويات' },
    reportingCenter: { id: 'Buka laporan', ar: 'فتح التقارير' },
    operationalSnapshot: { id: 'Ringkasan operasional', ar: 'الملخص التشغيلي' },
    activeProgramPortfolio: { id: 'Portofolio aktif', ar: 'المحفظة النشطة' },
    participantDirectory: { id: 'Kelola peserta', ar: 'إدارة المشاركين' },
    announcementHub: { id: 'Kelola pengumuman', ar: 'إدارة الإعلانات' },
    activeProgramsCard: { id: 'Program Pembelajaran Aktif', ar: 'البرامج الدراسية النشطة' },
    manageProgram: { id: 'Kelola Program', ar: 'إدارة البرنامج' },
    newAnnouncement: { id: 'Buat Pengumuman', ar: 'إنشاء إعلان' },
    emailSettings: { id: 'Pengaturan Email & Sistem', ar: 'إعدادات البريد والنظام' },
    storageStatus: { id: 'Penyimpanan Media', ar: 'تخزين الوسائط' },
    storageS3: { id: 'Cloud S3 (Contabo)', ar: 'التخزين السحابي S3' },
    storageLocal: { id: 'Disk Lokal (Fallback)', ar: 'القرص المحلي' },
    smtpStatus: { id: 'Layanan Email', ar: 'خدمة البريد الإلكتروني' },
    smtpActive: {
      id: 'Aktif melalui Mailketing (no-reply@yts.web.id)',
      ar: 'نشط عبر Mailketing (no-reply@yts.web.id)',
    },
    viewAllPrograms: { id: 'Lihat Semua Program', ar: 'عرض جميع البرامج' },
    participantsCount: { id: '{count} Peserta', ar: '{count} مشارك' },
  },
  programs: {
    listTitle: { id: 'Portofolio Kurikulum', ar: 'حقيبة المناهج الدراسية' },
    listSubtitle: {
      id: 'Kelola program pembelajaran aktif dan konfigurasi akademik.',
      ar: 'إدارة وتنظيم جميع برامج التعلم النشطة والتكوينات الأكاديمية.',
    },
    createBtn: { id: 'Tambah Program', ar: 'إضافة برنامج' },
    tableHeaderMatrix: { id: 'Daftar Program', ar: 'قائمة البرامج' },
    tableHeaderStatus: { id: 'Status', ar: 'الحالة' },
    tableHeaderCode: { id: 'Kode', ar: 'الرمز' },
    tableHeaderEnrollments: { id: 'Peserta Terdaftar', ar: 'المسجلون' },
    tableHeaderCommencement: { id: 'Tanggal Mulai', ar: 'تاريخ البدء' },
    tableHeaderCreated: { id: 'Tanggal Dibuat', ar: 'تاريخ الإنشاء' },
    noPrograms: {
      id: 'Belum ada program yang dibuat. Silakan buat program baru untuk memulai.',
      ar: 'لم يتم إنشاء أي برامج بعد. يرجى إنشاء برنامج للبدء.',
    },
    loadingPrograms: { id: 'Memuat program belajar...', ar: 'جاري تحميل البرامج...' },
    errorLoadingPrograms: { id: 'Gagal memuat program', ar: 'خطأ في تحميل البرامج' },
    btnDelete: { id: 'Hapus', ar: 'حذف' },
    confirmDelete: {
      id: 'Apakah Anda yakin ingin menghapus program ini?',
      ar: 'هل أنت متأكد من حذف هذا البرنامج؟',
    },
    noDescription: {
      id: 'Tidak ada penjelasan program',
      ar: 'لم يتم توفير وصف',
    },
    searchPlaceholder: {
      id: 'Cari program...',
      ar: 'البحث عن برنامج...',
    },
    searchSuggestTitle: {
      id: 'Saran Program',
      ar: 'اقتراحات البرامج',
    },
    clearSearch: {
      id: 'Hapus pencarian',
      ar: 'مسح البحث',
    },
    noSuggestions: {
      id: 'Tidak ada program yang cocok',
      ar: 'لا توجد برامج مطابقة',
    },
    createdLabel: {
      id: 'Dibuat:',
      ar: 'تم الإنشاء:',
    },
    workspaceLabel: { id: 'Ruang Kerja Kurikulum', ar: 'مساحة عمل المنهج' },
    filterStatusLabel: { id: 'Filter status program', ar: 'تصفية حالة البرنامج' },
    filterAll: { id: 'Semua', ar: 'الكل' },
    filterPublished: { id: 'Diterbitkan', ar: 'منشور' },
    filterDraft: { id: 'Draft', ar: 'مسودة' },
    filterArchived: { id: 'Diarsipkan', ar: 'مؤرشف' },
    statusPublished: { id: 'Diterbitkan', ar: 'منشور' },
    statusDraft: { id: 'Draft', ar: 'مسودة' },
    statusArchived: { id: 'Diarsipkan', ar: 'مؤرشف' },
    viewCards: { id: 'Kartu', ar: 'بطاقات' },
    viewRows: { id: 'Daftar', ar: 'قائمة' },
    programsShown: { id: 'program ditampilkan', ar: 'برامج معروضة' },
    registrationOpen: { id: 'Pendaftaran aktif', ar: 'التسجيل نشط' },
    registrationClosed: { id: 'Pendaftaran tutup', ar: 'التسجيل مغلق' },
    dateNotSet: { id: 'Belum ditentukan', ar: 'لم يتم التحديد' },
    viewDetails: { id: 'Kelola program', ar: 'إدارة البرنامج' },
    emptyTitle: { id: 'Belum ada program untuk ditampilkan', ar: 'لا توجد برامج للعرض' },
    retry: { id: 'Muat ulang', ar: 'إعادة التحميل' },
  },
  createProgram: {
    breadcrumbPrograms: { id: 'Program Belajar', ar: 'البرامج الدراسية' },
    breadcrumbCreate: { id: 'Buat Baru', ar: 'إنشاء جديد' },
    createTitle: { id: 'Buat Program Baru', ar: 'إنشاء برنامج جديد' },
    labelCode: { id: 'Kode Program', ar: 'رمز البرنامج' },
    placeholderCode: { id: 'Misal: BID-A', ar: 'مثال: BID-A' },
    descCode: { id: 'Pengidentifikasi unik untuk program ini', ar: 'معرف فريد للبرنامج' },
    labelName: { id: 'Nama Program', ar: 'اسم البرنامج' },
    placeholderName: { id: 'Misal: Belajar Islam Dasar', ar: 'مثال: تعلم الإسلام الأساسي' },
    labelDesc: { id: 'Deskripsi', ar: 'الوصف' },
    descPlaceholder: {
      id: 'Tulis penjelasan mengenai program ini...',
      ar: 'اكتب تفاصيل عن هذا البرنامج...',
    },
    labelApproval: { id: 'Mode Persetujuan', ar: 'وضع الموافقة' },
    optionManual: { id: 'Review Manual', ar: 'مراجعة يدوية' },
    optionAuto: { id: 'Persetujuan Otomatis', ar: 'موافقة تلقائية' },
    labelStatus: { id: 'Status Awal', ar: 'الحالة الأولية' },
    optionDraft: { id: 'Draft', ar: 'مسودة' },
    optionPublished: { id: 'Diterbitkan', ar: 'منشور' },
    labelRegActivation: { id: 'Aktivasi Pendaftaran', ar: 'تفعيل التسجيل' },
    descRegActivation: {
      id: 'Kontrol visibilitas formulir pendaftaran untuk umum',
      ar: 'التحكم في ظهور استمارة التسجيل للعامة',
    },
    labelAutomaticNip: {
      id: 'Nomor Induk Peserta (NIP) otomatis',
      ar: 'الرقم الأكاديمي التلقائي للمشارك',
    },
    descAutomaticNip: {
      id: 'Opsional. Jika aktif, peserta yang belum memiliki NIP akan mendapat satu NIP global saat mendaftar. NIP lama tidak diubah dan tetap dipakai di program berikutnya.',
      ar: 'اختياري. عند التفعيل، يحصل المشارك الذي لا يملك رقماً أكاديمياً على رقم عالمي واحد عند التسجيل. لا تتغير الأرقام القديمة وتُستخدم في البرامج التالية.',
    },
    nipFormatHelp: {
      id: '11 digit: gender + semester masuk + 2 digit tahun + 7 digit nomor urut. Contoh Ikhwan semester 2 tahun 2026: 12260000001.',
      ar: '١١ رقماً: الجنس + فصل الدخول + رقمان للسنة + ٧ أرقام تسلسلية. مثال: 12260000001.',
    },
    labelStartDate: { id: 'Tanggal Mulai', ar: 'تاريخ البدء' },
    labelEndDate: { id: 'Tanggal Selesai', ar: 'تاريخ الانتهاء' },
    labelRegistrationOpenDate: { id: 'Pendaftaran Dibuka', ar: 'فتح التسجيل' },
    labelRegistrationCloseDate: { id: 'Pendaftaran Ditutup', ar: 'إغلاق التسجيل' },
    labelCoverImage: { id: 'Cover / Banner Program', ar: 'صورة غلاف / لافتة البرنامج' },
    uploadCoverPrompt: {
      id: 'Pilih gambar untuk mengunggah cover program',
      ar: 'اختر صورة لتحميل غلاف البرنامج',
    },
    coverRecommendation: {
      id: 'JPG, PNG, WEBP, atau GIF · Maks. 50 MB · Rasio 16:9 disarankan',
      ar: 'JPG أو PNG أو WEBP أو GIF · الحد الأقصى 50 ميجابايت · يوصى بنسبة 16:9',
    },
    changeCover: { id: 'Ganti Gambar', ar: 'تغيير الصورة' },
    removeCover: { id: 'Hapus Gambar', ar: 'حذف الصورة' },
    uploadingCover: { id: 'Mengunggah gambar...', ar: 'جارٍ رفع الصورة...' },
    coverPreviewUnavailable: {
      id: 'Cover saat ini tidak dapat dimuat. Pilih gambar baru untuk menggantinya.',
      ar: 'تعذر تحميل الغلاف الحالي. اختر صورة جديدة لاستبداله.',
    },
    sectionIdentity: { id: 'Identitas program', ar: 'هوية البرنامج' },
    sectionIdentityDesc: {
      id: 'Tetapkan nama, kode unik, deskripsi, dan cover yang akan dikenali peserta.',
      ar: 'حدّد الاسم والرمز الفريد والوصف والغلاف الذي سيتعرف عليه المشاركون.',
    },
    sectionAdmission: { id: 'Pendaftaran peserta', ar: 'تسجيل المشاركين' },
    sectionAdmissionDesc: {
      id: 'Atur cara peserta masuk serta kapan formulir pendaftaran dapat diakses.',
      ar: 'اضبط طريقة دخول المشاركين ووقت إتاحة استمارة التسجيل.',
    },
    sectionSchedule: { id: 'Jadwal belajar', ar: 'جدول الدراسة' },
    sectionScheduleDesc: {
      id: 'Tambahkan rentang waktu bila program memiliki jadwal yang sudah ditetapkan.',
      ar: 'أضف المدة الزمنية إذا كان للبرنامج جدول محدد.',
    },
    labelCohort: { id: 'Angkatan', ar: 'الدفعة' },
    placeholderCohort: { id: 'Misal: 1', ar: 'مثال: 1' },
    descCohort: {
      id: 'Opsional. Gunakan angka untuk membedakan angkatan program yang sama.',
      ar: 'اختياري. استخدم رقماً لتمييز دفعات البرنامج نفسه.',
    },
    summaryTitle: { id: 'Ringkasan sebelum dibuat', ar: 'ملخص قبل الإنشاء' },
    summaryEmpty: { id: 'Belum diisi', ar: 'لم يتم الإدخال' },
    summaryRegistrationOpen: { id: 'Formulir dibuka', ar: 'الاستمارة مفتوحة' },
    summaryRegistrationClosed: { id: 'Formulir belum dibuka', ar: 'الاستمارة غير مفتوحة' },
    summaryNipAutomatic: { id: 'Dibuat otomatis bila belum ada', ar: 'يُنشأ تلقائياً إن لم يوجد' },
    summaryNipDisabled: { id: 'Tidak dibuat (opsional)', ar: 'لا يُنشأ (اختياري)' },
    invalidRegistrationSchedule: {
      id: 'Tanggal pembukaan pendaftaran harus sebelum tanggal penutupan.',
      ar: 'يجب أن يسبق تاريخ فتح التسجيل تاريخ الإغلاق.',
    },
    invalidLearningSchedule: {
      id: 'Tanggal mulai program harus sebelum tanggal selesai.',
      ar: 'يجب أن يسبق تاريخ بدء البرنامج تاريخ الانتهاء.',
    },
  },
  registrations: {
    listTitle: { id: 'Manajemen Pendaftaran', ar: 'إدارة التسجيلات' },
    listSubtitle: {
      id: 'Kelola proses pendaftaran calon peserta di berbagai program studi.',
      ar: 'إدارة عملية تسجيل المشاركين الجدد في البرامج الدراسية المختلفة.',
    },
    searchPlaceholder: {
      id: 'Cari nama, email, WhatsApp, NIP, atau kode peserta...',
      ar: 'البحث بالاسم أو البريد أو واتساب أو الرقم الأكاديمي أو رمز المشارك...',
    },
    filterAll: { id: 'Semua', ar: 'الكل' },
    filterPending: { id: 'Menunggu', ar: 'قيد الانتظار' },
    filterApproved: { id: 'Disetujui', ar: 'مقبول' },
    filterRejected: { id: 'Ditolak', ar: 'مرفوض' },
    filterRevision: { id: 'Butuh Revisi', ar: 'بحاجة للمراجعة' },
    filterResubmitted: { id: 'Pengajuan Ulang', ar: 'إعادة تقديم' },
    filterProgram: { id: 'Filter program', ar: 'تصفية البرنامج' },
    exportCsv: { id: 'Unduh CSV', ar: 'تنزيل CSV' },
    exportCsvSuccess: {
      id: 'Data pendaftaran berhasil diunduh sebagai CSV.',
      ar: 'تم تنزيل بيانات التسجيل بصيغة CSV بنجاح.',
    },
    exportCsvError: {
      id: 'CSV pendaftaran belum dapat diunduh. Coba lagi.',
      ar: 'تعذر تنزيل ملف CSV للتسجيلات. حاول مرة أخرى.',
    },
    filterResubmittedHint: {
      id: 'Tampilkan hanya pendaftaran yang pernah diajukan ulang setelah ditolak / revisi / dibatalkan.',
      ar: 'عرض التسجيلات التي أُعيد تقديمها فقط بعد الرفض / المراجعة / الإلغاء.',
    },
    resubmittedBadge: { id: 'Diajukan Ulang', ar: 'أُعيد تقديمه' },
    tableHeaderParticipant: { id: 'Peserta', ar: 'المشارك' },
    tableHeaderEmail: { id: 'Alamat Email', ar: 'البريد الإلكتروني' },
    tableHeaderProgram: { id: 'Program', ar: 'البرنامج' },
    tableHeaderCode: { id: 'NIP / Kode Program', ar: 'الرقم الأكاديمي / رمز البرنامج' },
    tableHeaderStatus: { id: 'Status', ar: 'الحالة' },
    tableHeaderTimestamp: { id: 'Waktu Pendaftaran', ar: 'وقت التسجيل' },
    tableHeaderActions: { id: 'Aksi', ar: 'الإجراءات' },
    btnApprove: { id: 'Setujui', ar: 'الموافقة' },
    btnReject: { id: 'Tolak', ar: 'رفض' },
    emptyState: {
      id: 'Tidak ada data pendaftaran yang ditemukan.',
      ar: 'لم يتم العثور على أي بيانات تسجيل.',
    },
    whatsAppGroupTitle: { id: 'Manajemen Grup WhatsApp', ar: 'إدارة مجموعات الواتساب' },
    whatsAppGroupDesc: {
      id: 'Manajemen grup WhatsApp terintegrasi melalui API klien. Pembagian jenis kelamin dan batas kapasitas diberlakukan di sisi server.',
      ar: 'إدارة مجموعات الواتساب متكاملة عبر واجهة برمجة التطبيقات. يتم فرض فصل الجنسين وحدود السعة على مستوى الخادم.',
    },
    tabList: { id: 'Pendaftar & Verifikasi Berkas', ar: 'المسجلون والتحقق من الملفات' },
    tabProgramSettings: {
      id: 'Pengaturan Form & Status Program',
      ar: 'إعدادات الاستمارة وحالة البرنامج',
    },
    tabCommunityGroups: {
      id: 'Tautan Grup WhatsApp & Telegram',
      ar: 'روابط مجموعات الواتساب والتليجرام',
    },
    programSettingsTitle: {
      id: 'Pengaturan Form & Status Pendaftaran',
      ar: 'إعدادات الاستمارة وحالة التسجيل',
    },
    programSettingsDesc: {
      id: 'Buka atau tutup pendaftaran peserta, atur mode seleksi berkas, dan bagikan tautan form pendaftaran publik.',
      ar: 'فتح أو إغلاق التسجيل، وتحديد وضع مراجعة الملفات، ومشاركة رابط استمارة التسجيل العامة.',
    },
    registrationStatus: { id: 'Status Pendaftaran', ar: 'حالة التسجيل' },
    statusOpen: { id: 'Pendaftaran Dibuka', ar: 'التسجيل مفتوح' },
    statusClosed: { id: 'Pendaftaran Ditutup', ar: 'التسجيل مغلق' },
    approvalMode: { id: 'Mode Persetujuan', ar: 'وضع الموافقة' },
    approvalManual: { id: 'Persetujuan Manual (Review Berkas)', ar: 'موافقة يدوية (مراجعة الملف)' },
    approvalAuto: { id: 'Otomatis (Langsung Disetujui)', ar: 'تلقائي (موافقة مباشرة)' },
    nipSettingLabel: { id: 'Pengaturan NIP', ar: 'إعداد الرقم الأكاديمي' },
    nipAutomatic: { id: 'NIP otomatis aktif', ar: 'الرقم الأكاديمي التلقائي مفعّل' },
    nipOptionalDisabled: { id: 'Tanpa NIP otomatis', ar: 'بدون رقم أكاديمي تلقائي' },
    nipSettingDescription: {
      id: 'Peserta baru yang belum mempunyai NIP akan menerima satu NIP global saat mendaftar. Menonaktifkan fitur tidak menghapus NIP yang sudah ada.',
      ar: 'يحصل المشارك الجديد الذي لا يملك رقماً أكاديمياً على رقم عالمي واحد عند التسجيل. إيقاف الميزة لا يحذف الأرقام الموجودة.',
    },
    nipFormatShort: {
      id: 'Format: G S YY 0000001 · 1/2 gender · 1/2 semester · tahun · urutan',
      ar: 'الصيغة: G S YY 0000001 · الجنس · الفصل · السنة · التسلسل',
    },
    enableNip: { id: 'Aktifkan NIP otomatis', ar: 'تفعيل الرقم التلقائي' },
    disableNip: { id: 'Nonaktifkan untuk pendaftar baru', ar: 'إيقافه للمسجلين الجدد' },
    nipEnabledSuccess: {
      id: 'NIP otomatis aktif untuk pendaftaran baru program ini.',
      ar: 'تم تفعيل الرقم الأكاديمي التلقائي للتسجيلات الجديدة في هذا البرنامج.',
    },
    nipDisabledSuccess: {
      id: 'NIP otomatis dinonaktifkan. NIP peserta yang sudah ada tetap tersimpan.',
      ar: 'تم إيقاف الرقم التلقائي، وتبقى أرقام المشاركين الحالية محفوظة.',
    },
    publicFormLink: { id: 'Tautan Form Pendaftaran Publik', ar: 'رابط استمارة التسجيل العامة' },
    btnCopyLink: { id: 'Salin Tautan Form', ar: 'نسخ رابط الاستمارة' },
    btnCopiedLink: { id: 'Tautan Disalin!', ar: 'تم نسخ الرابط!' },
    btnOpenForm: { id: 'Buka Form Pendaftaran', ar: 'فتح استمارة التسجيل' },
    btnEditProgram: { id: 'Edit Konfigurasi', ar: 'تعديل الإعدادات' },
    btnCreateGroup: { id: 'Tambah Tautan Grup', ar: 'إضافة رابط مجموعة' },
    filterProgramAll: { id: 'Semua Program', ar: 'جميع البرامج' },
    filterGenderAll: { id: 'Semua Gender', ar: 'جميع الفئات' },
    pageIndicator: { id: 'Halaman {page} dari {totalPages}', ar: 'الصفحة {page} من {totalPages}' },
    btnPrevious: { id: 'Sebelumnya', ar: 'السابق' },
    btnNext: { id: 'Selanjutnya', ar: 'التالي' },
    today: { id: 'Hari Ini', ar: 'اليوم' },
  },
  registrationDetail: {
    title: { id: 'Detail Pendaftaran', ar: 'تفاصيل التسجيل' },
    subtitle: {
      id: 'Verifikasi berkas, pembayaran, dan status pendaftaran peserta.',
      ar: 'التحقق من المستندات، الدفع، وحالة تسجيل المشارك.',
    },
    sectionPersonal: { id: 'Informasi Pribadi', ar: 'المعلومات الشخصية' },
    sectionDocument: { id: 'Dokumen Persyaratan', ar: 'المستندات المطلوبة' },
    sectionPayment: { id: 'Bukti Pembayaran', ar: 'إثبات الدفع' },
    sectionVerification: { id: 'Panel Verifikasi', ar: 'لوحة التحقق' },
    paymentStatus: { id: 'Status Pembayaran', ar: 'حالة الدفع' },
    adminNotes: { id: 'Catatan Admin', ar: 'ملاحظات المشرف' },
    rejectionReason: { id: 'Alasan Penolakan', ar: 'سبب الرفض' },
    placeholderNotes: {
      id: 'Tulis catatan verifikasi di sini...',
      ar: 'اكتب ملاحظات التحقق هنا...',
    },
    emptyTitle: { id: 'Berkas Tidak Tersedia', ar: 'الملف غير متوفر' },
    emptyDesc: {
      id: 'Berkas pendaftaran yang diminta tidak dapat ditemukan.',
      ar: 'لا يمكن استرداد ملف التسجيل المطلوب.',
    },
    returnToList: { id: 'Kembali ke Daftar', ar: 'العودة إلى القائمة' },
    breadcrumbDossier: { id: 'Berkas', ar: 'ملف' },
    btnAuthorize: { id: 'Otorisasi Peserta', ar: 'اعتماد المشارك' },
    sectionProfile: { id: 'Profil Identitas Lengkap', ar: 'ملف الهوية الشامل' },
    profileDesc: {
      id: 'Kredensial utama dan latar belakang terverifikasi.',
      ar: 'المؤهلات الأساسية والخلفية المعتمدة.',
    },
    labelFullName: { id: 'Nama Lengkap sesuai Identitas', ar: 'الهوية القانونية الكاملة' },
    labelEmail: { id: 'Komunikasi Utama (Email)', ar: 'الاتصالات الرئيسية' },
    labelPhone: { id: 'Nomor WhatsApp', ar: 'رقم الهاتف المحمول' },
    labelGender: { id: 'Jenis Kelamin', ar: 'الملف البيولوجي' },
    labelProgram: { id: 'Program Target', ar: 'البرنامج المستهدف' },
    labelDomisili: { id: 'Wilayah Domisili', ar: 'منطقة الإقامة' },
    labelReason: { id: 'Motivasi Utama', ar: 'الدافع الرئيسي' },
    labelNotes: { id: 'Catatan Peserta', ar: 'ملاحظات المشارak' },
    sectionOnboarding: { id: 'Siklus Onboarding', ar: 'مسار التهيئة' },
    stepInitial: { id: 'Pendaftaran Dikirim', ar: 'تم تقديم الطلب' },
    stepInitialDesc: {
      id: 'Informasi awal pendaftaran berhasil disimpan.',
      ar: 'تم تسجيل معلومات التسجيل الأولية.',
    },
    stepWA: { id: 'Grup WA Terintegrasi', ar: 'مجموعة الواتساب مدمجة' },
    stepWADesc: {
      id: 'Telah dimasukkan ke dalam saluran komunikasi kelas.',
      ar: 'تم الدمج في قناة الاتصال.',
    },
    stepProfile: { id: 'Profil Diverifikasi', ar: 'تم التحقق من الملف' },
    stepProfileDesc: {
      id: 'Jenis kelamin dan kontak data telah dikonfirmasi.',
      ar: 'تم تأكيد معلومات الجنس والاتصال.',
    },
    stepConsent: { id: 'Pernyataan Disetujui', ar: 'تم اعتماد الموافقة' },
    stepConsentDesc: {
      id: 'Protokol komitmen pembelajaran digital telah ditandatangani.',
      ar: 'تم توقيع بروتوكول الالتزام الرقمي.',
    },
    stepOrientation: { id: 'Sinkronisasi Orientasi', ar: 'مزامنة التوجيه' },
    stepOrientationDesc: {
      id: 'Alur operasional pembelajaran telah diselaraskan.',
      ar: 'تم مزامنة مسار التشغيل.',
    },
    stepCompleted: { id: 'Tersertifikasi Penuh', ar: 'مهيأ بالكامل' },
    stepCompletedDesc: {
      id: 'Siap untuk memulai kegiatan akademik.',
      ar: 'جاهز للتنفيذ الأكاديمي.',
    },
    stepStatusLengkap: { id: 'Lengkap', ar: 'مكتمل' },
    stepStatusTertunda: { id: 'Tertunda', ar: 'معلق' },
    stepStatusTerverifikasi: { id: 'Terverifikasi', ar: 'مؤكد' },
    stepStatusBelumLengkap: { id: 'Belum Lengkap', ar: 'غير مكتمل' },
    stepStatusDitandatangani: { id: 'Ditandatangani', ar: 'موقع' },
    stepStatusBelumDitandatangani: { id: 'Belum Ditandatangani', ar: 'غير موقع' },
    stepStatusSinkron: { id: 'Sinkron', ar: 'متزامن' },
    stepStatusMenunggu: { id: 'Menunggu', ar: 'انتظار' },
    stepStatusAktif: { id: 'Aktif', ar: 'نشط' },
    stepStatusTerkunci: { id: 'Terkunci', ar: 'مغلق' },
    sectionProtocol: { id: 'Protokol Tarbiyah', ar: 'بروتوكول التربية' },
    labelCandidate: { id: 'Calon Peserta', ar: 'مرشح البرنامج' },
    labelCredential: { id: 'Kredensial', ar: 'الاعتماد' },
    labelValidity: { id: 'Masa Berlaku', ar: 'الصلاحية' },
    btnDownloadProof: { id: 'Unduh Bukti', ar: 'تحميل الإثبات' },
    sectionCommunication: { id: 'Pusat Komunikasi', ar: 'مركز الاتصالات' },
    communicationDesc: { id: 'Penyelarasan Kelas', ar: 'محاذاة الدفعة' },
    labelPlacementStatus: { id: 'Status Penempatan', ar: 'حالة التعيين' },
    statusAssigned: { id: 'TERTATA', ar: 'معين' },
    statusUnassigned: { id: 'BELUM DITEMPATKAN', ar: 'غير معين' },
    inspect: { id: 'Periksa', ar: 'معاينة' },
    msgAssigned: {
      id: "Peserta berhasil dimasukkan ke dalam grup WhatsApp '{name}'.",
      ar: 'تم دمج المشارك بنجاح في المجموعة.',
    },
    msgUnassigned: {
      id: 'Peserta memerlukan penempatan manual ke grup WhatsApp.',
      ar: 'يتطلب تعيين يدوي في مجموعة اتصال.',
    },
    labelSelectWAChannel: { id: 'Pilih Saluran Grup WhatsApp', ar: 'اختر قناة التواصل' },
    optionSelectChannel: { id: 'Pilih Saluran...', ar: 'اختر القناة...' },
    btnAssignSubmit: { id: 'Tempatkan & Kirim', ar: 'تعيين وإرسال' },
    sectionInternalAudit: { id: 'Tindakan Audit Internal', ar: 'إجراءات التدقيق الداخلي' },
    labelVerifyNotes: { id: 'Catatan Evaluasi Verifikasi', ar: 'ملاحظats التقييم' },
    placeholderVerifyNotes: {
      id: 'Catatan untuk persetujuan atau penolakan...',
      ar: 'ملاحظات للاعتماد أو الرفض...',
    },
    btnLogAudit: { id: 'Log Audit', ar: 'سجل التدقيق' },
    btnExport: { id: 'Ekspor', ar: 'تصدير' },
    btnInspect: { id: 'Periksa', ar: 'فحص' },
    btnFutureActions: { id: 'Aksi Mendatang', ar: 'إجراءات مستقبلية' },
  },
  announcements: {
    listTitle: { id: 'Pusat Pengumuman', ar: 'مركز الإعلانات' },
    listSubtitle: {
      id: 'Kelola siaran informasi, pemberitahuan akademik, dan pengumuman lintas portal.',
      ar: 'إدارة وتوجيه الإعلانات الأكاديمية والتنبيهات العامة لكافة بوابات النظام.',
    },
    createBtn: { id: 'Buat Pengumuman', ar: 'إنشاء إعلان جديد' },
    editBtn: { id: 'Edit Pengumuman', ar: 'تعديل الإعلان' },
    searchPlaceholder: {
      id: 'Cari judul atau isi pengumuman...',
      ar: 'البحث في عناوين أو محتوى الإعلانات...',
    },
    filterAllCategories: { id: 'Semua Kategori', ar: 'جميع الفئات' },
    filterAllPriorities: { id: 'Semua Prioritas', ar: 'جميع الأولويات' },
    filterAllAudiences: { id: 'Semua Target', ar: 'كافة الفئات المستهدفة' },
    tableHeaderTitle: { id: 'Judul & Isi', ar: 'العنوان والمحتوى' },
    tableHeaderCategory: { id: 'Kategori', ar: 'الفئة' },
    tableHeaderPriority: { id: 'Prioritas', ar: 'الأولوية' },
    tableHeaderTarget: { id: 'Target Audiens', ar: 'الجمهور المستهدف' },
    tableHeaderStatus: { id: 'Status', ar: 'الحالة' },
    tableHeaderDate: { id: 'Tanggal Siar', ar: 'تاريخ النشر' },
    catGeneral: { id: 'Umum', ar: 'عام' },
    catAcademic: { id: 'Akademik', ar: 'أكاديمي' },
    catMaintenance: { id: 'Pemeliharaan', ar: 'صيانة النظام' },
    catImportant: { id: 'Penting', ar: 'هام' },
    catEvent: { id: 'Kegiatan / Event', ar: 'فعالية' },
    prioNormal: { id: 'Normal', ar: 'عادي' },
    prioHigh: { id: 'Tinggi', ar: 'مرتفع' },
    prioUrgent: { id: 'Mendesak (Urgent)', ar: 'عاجل جداً' },
    targetAll: { id: 'Semua Portal (Publik)', ar: 'جميع البوابات (عام)' },
    targetAdmin: { id: 'Khusus Admin', ar: 'المسؤولون فقط' },
    targetParticipant: { id: 'Khusus Peserta', ar: 'المشاركون فقط' },
    targetTeacher: { id: 'Khusus Pengajar', ar: 'المعلمون فقط' },
    statusActive: { id: 'Aktif', ar: 'نشط' },
    statusInactive: { id: 'Nonaktif', ar: 'معطل' },
    noAnnouncements: {
      id: 'Belum ada pengumuman yang dibuat.',
      ar: 'لم يتم إنشاء أي إعلانات بعد.',
    },
    deleteConfirm: {
      id: 'Apakah Anda yakin ingin menghapus pengumuman ini?',
      ar: 'هل أنت متأكد من حذف هذا الإعلان نهائياً؟',
    },
    modalCreateTitle: { id: 'Buat Pengumuman Baru', ar: 'إنشاء إعلان جديد' },
    modalEditTitle: { id: 'Edit Data Pengumuman', ar: 'تعديل بيانات الإعلان' },
    labelTitle: { id: 'Judul Pengumuman', ar: 'عنوان الإعلان' },
    placeholderTitle: {
      id: 'Masukkan judul pengumuman yang jelas...',
      ar: 'أدخل عنوان الإعلان...',
    },
    labelContent: { id: 'Isi Pengumuman', ar: 'محتوى ونَص الإعلان' },
    placeholderContent: {
      id: 'Tuliskan informasi lengkap pengumuman di sini...',
      ar: 'اكتب نص الإعلان والتفاصيل هنا...',
    },
    labelCategory: { id: 'Kategori Pengumuman', ar: 'تصنيف الإعلان' },
    labelPriority: { id: 'Tingkat Prioritas', ar: 'مستوى الأهمية' },
    labelTarget: { id: 'Target Penerima', ar: 'الفئة المستهدفة' },
    labelProgram: { id: 'Kaitkan Program Spesifik (Opsional)', ar: 'البرنامج المرتبط (اختياري)' },
    selectProgramPlaceholder: { id: 'Semua Program (Umum)', ar: 'كافة البرامج (عام)' },
    labelStatusActive: { id: 'Status Penayangan Aktif', ar: 'تفعيل النشر الفوري' },
    labelStartsAt: { id: 'Mulai Tayang', ar: 'تاريخ بدء النشر' },
    labelExpiresAt: { id: 'Berakhir Pada (Opsional)', ar: 'تاريخ انتهاء النشر (اختياري)' },
    btnSave: { id: 'Simpan Pengumuman', ar: 'حفظ الإعلان' },
    btnCancel: { id: 'Batal', ar: 'إلغاء' },
    toastCreated: { id: 'Pengumuman berhasil dibuat.', ar: 'تم إنشاء الإعلان بنجاح.' },
    toastUpdated: { id: 'Pengumuman berhasil diperbarui.', ar: 'تم تحديث الإعلان بنجاح.' },
    toastDeleted: { id: 'Pengumuman berhasil dihapus.', ar: 'تم حذف الإعلان بنجاح.' },
    toastStatusToggled: { id: 'Status penayangan diubah.', ar: 'تم تغيير حالة النشر.' },
    latestAnnouncements: { id: 'Pengumuman & Siaran', ar: 'الإعلانات والبيانات' },
    viewAll: { id: 'Kelola Pengumuman', ar: 'إدارة الإعلانات' },
    readMore: { id: 'Baca Selengkapnya', ar: 'قراءة المزيد' },
    urgentNotice: { id: 'PENGUMUMAN PENTING', ar: 'تنبيه عاجل' },
    dismissBanner: { id: 'Tutup Siaran', ar: 'إغلاق الإشعار' },
    dismissNotice: { id: 'Tutup Siaran', ar: 'إغلاق الإشعار' },
    notificationsTitle: { id: 'Notifikasi & Pengumuman', ar: 'الإشعارات والإعلانات' },
    noNotifications: { id: 'Tidak ada pengumuman baru.', ar: 'لا توجد إعلانات جديدة حالياً.' },
  },
  evaluations: {
    title: { id: 'Bank Soal & Evaluasi', ar: 'مستودع التقييمات' },
    workspaceLabel: { id: 'Ruang kerja evaluasi', ar: 'مساحة عمل التقييم' },
    workspaceDescription: {
      id: 'Susun instrumen, temukan evaluasi, lalu lanjutkan pengolahan soal dari satu ruang kerja.',
      ar: 'أنشئ أدوات التقييم، واعثر عليها، ثم تابع إدارة الأسئلة من مساحة عمل واحدة.',
    },
    subtitle: {
      id: 'Rancang kuis diagnostik dan ujian akhir untuk memvalidasi pemahaman peserta.',
      ar: 'تصميم ونشر التقييمات التشخيصية للتحقق من كفاءة المشاركين.',
    },
    listTitle: { id: 'Bank Soal & Evaluasi', ar: 'مستودع التقييمات' },
    listSubtitle: {
      id: 'Rancang kuis diagnostik dan ujian akhir untuk memvalidasi pemahaman peserta.',
      ar: 'تصميم ونشر التقييمات التشخيصية للتحقق من كفاءة المشاركين.',
    },
    createButton: { id: 'Buat Evaluasi', ar: 'إنشاء تقييم' },
    questionBankButton: { id: 'Buka Bank Soal', ar: 'فتح بنك الأسئلة' },
    refreshButton: { id: 'Segarkan data', ar: 'تحديث البيانات' },
    refreshingButton: { id: 'Memperbarui', ar: 'جارٍ التحديث' },
    searchLabel: { id: 'Cari evaluasi', ar: 'البحث عن تقييم' },
    pageSizeLabel: { id: 'Baris per halaman', ar: 'صفوف لكل صفحة' },
    totalCount: { id: 'Total evaluasi', ar: 'إجمالي التقييمات' },
    quizCount: { id: 'Kuis', ar: 'الاختبارات القصيرة' },
    examCount: { id: 'Ujian', ar: 'الامتحانات' },
    surveyCount: { id: 'Survei', ar: 'الاستبيانات' },
    resultRange: {
      id: 'Menampilkan {start}–{end} dari {count} evaluasi',
      ar: 'عرض {start}–{end} من {count} تقييم',
    },
    resultFiltered: {
      id: 'Disaring dari {total} evaluasi',
      ar: 'تمت التصفية من {total} تقييم',
    },
    pageIndicator: { id: 'Halaman {page} dari {total}', ar: 'الصفحة {page} من {total}' },
    noDescription: { id: 'Belum ada deskripsi.', ar: 'لا يوجد وصف بعد.' },
    noProgram: { id: 'Belum ditautkan ke program', ar: 'غير مرتبط ببرنامج بعد' },
    openEvaluation: { id: 'Buka evaluasi', ar: 'فتح التقييم' },
    btnCreate: { id: 'Buat Evaluasi', ar: 'إنشاء تقييم' },
    tableHeaderTitle: { id: 'Nama Evaluasi', ar: 'اسم التقييم' },
    tableHeaderNode: { id: 'Nama Evaluasi', ar: 'اسم التقييم' },
    tableHeaderCategory: { id: 'Kategori', ar: 'الفئة' },
    tableHeaderProgram: { id: 'Program', ar: 'البرنامج' },
    tableHeaderPassingScore: { id: 'KKM (Minimum)', ar: 'درجة النجاح' },
    tableHeaderAttempts: { id: 'Maksimal Percobaan', ar: 'المحاولات' },
    tableHeaderCreated: { id: 'Tanggal Dibuat', ar: 'تاريخ الإنشاء' },
    searchPlaceholder: {
      id: 'Cari nama evaluasi, deskripsi, atau program...',
      ar: 'ابحث عن اسم التقييم، الوصف، أو البرنامج...',
    },
    searchSuggestTitle: { id: 'Saran Evaluasi', ar: 'اقتراحات التقييم' },
    clearSearch: { id: 'Hapus pencarian', ar: 'مسح البحث' },
    filterAllTypes: { id: 'Semua Tipe', ar: 'جميع الأنواع' },
    filterAllPrograms: { id: 'Semua Program', ar: 'جميع البرامج' },
    filterType: { id: 'Tipe Evaluasi', ar: 'نوع التقييم' },
    filterProgram: { id: 'Program Terkait', ar: 'البرنامج المرتبط' },
    filterReset: { id: 'Reset Filter', ar: 'إعادة ضبط' },
    filterCount: {
      id: 'Menampilkan {count} dari {total} evaluasi',
      ar: 'عرض {count} من أصل {total} تقييم',
    },
    emptyState: {
      id: 'Belum ada evaluasi yang dibuat. Mulai dengan membuat kuis pertama.',
      ar: 'لم يتم العثور على تقييمات. ابدأ بإنشاء أول اختبار لك.',
    },
    emptyFilteredState: {
      id: 'Tidak ada evaluasi yang sesuai dengan kriteria pencarian atau filter.',
      ar: 'لا توجد تقييمات مطابقة لمعايير البحث أو التصفية.',
    },
    loadingState: { id: 'Memuat data evaluasi...', ar: 'جاري تحميل التقييمات...' },
    errorState: { id: 'Gagal memuat data evaluasi.', ar: 'خطأ في تحميل التقييمات' },
    attemptInfinite: { id: 'Tanpa Batas', ar: 'غير محدود' },
    attemptMax: { id: '{max} Kali', ar: 'حد أقصى {max}' },
  },
  createEvaluation: {
    breadcrumbTitle: { id: 'Evaluasi Belajar', ar: 'التقييمات' },
    breadcrumbCreate: { id: 'Buat Baru', ar: 'إنشاء جديد' },
    title: { id: 'Rancang Evaluasi Baru', ar: 'تصميم تقييم جديد' },
    subtitle: {
      id: 'Buat instrumen evaluasi untuk mengukur tingkat pemahaman materi.',
      ar: 'إنشاء أداة تقييم لقياس مستوى فهم المادة.',
    },
    labelTitle: { id: 'Judul Evaluasi', ar: 'عنوان التقييم' },
    placeholderTitle: { id: 'Contoh: Kuis Aqidah Dasar', ar: 'مثال: اختبار العقيدة الأساسية' },
    labelProgram: { id: 'Program Studi Terkait', ar: 'البرنامج الدراسي المستهدف' },
    placeholderProgram: { id: 'Pilih Program Target...', ar: 'اختر البرنامج المستهدف...' },
    syncingProgram: { id: 'Menyelaraskan daftar program...', ar: 'جاري مزامنة سجل البرامج...' },
    labelDesc: { id: 'Deskripsi Kuis', ar: 'الوصف المفاهيمي' },
    placeholderDesc: {
      id: 'Tulis penjelasan singkat mengenai cakupan evaluasi ini...',
      ar: 'اكتب وصفاً موجزاً لمعايير هذا التقييم...',
    },
    labelType: { id: 'Tipe Evaluasi', ar: 'نوع التقييم' },
    optionQuiz: { id: 'Kuis Diagnostik', ar: 'اختبار تشخيصي' },
    optionExam: { id: 'Ujian Akhir', ar: 'الامتحان النهائي' },
    optionSurvey: { id: 'Survei Umpan Balik', ar: 'استطلاع الرأي' },
    labelPassingScore: { id: 'Standar Kelulusan (KKM %)', ar: 'درجة النجاح (%)' },
    labelAttempts: { id: 'Batas Percobaan', ar: 'أقصى عدد محاولات' },
    descAttempts: {
      id: 'Isi 0 untuk memberikan kesempatan tanpa batas.',
      ar: 'أدخل 0 للمحاولات غير المحدودة.',
    },
    labelTimeLimit: { id: 'Batas Waktu (Menit)', ar: 'الحد الزمني (بالدقائق)' },
    descTimeLimit: {
      id: 'Batas durasi pengerjaan. Isi 0 jika tidak dibatasi.',
      ar: 'الحد الزمني للمحاولة. ضع 0 لعدم وجود حد.',
    },
    labelRandomPool: {
      id: 'Ambil acak N soal dari bank',
      ar: 'اختيار عشوائي N سؤال من المستودع',
    },
    placeholderRandomPool: {
      id: 'Kosongkan = pakai semua soal',
      ar: 'اتركه فارغاً لاستخدام جميع الأسئلة',
    },
    descRandomPool: {
      id: 'Jika diisi, sistem akan mengambil soal secara acak dari bank soal sejumlah N untuk setiap percobaan.',
      ar: 'في حال التعبئة، سيقوم النظام بسحب عدد N من الأسئلة بشكل عشوائي من المستودع لكل محاولة.',
    },
    searchProgramPlaceholder: {
      id: 'Ketik nama atau kode program untuk mencari...',
      ar: 'اكتب اسم البرنامج أو الرمز للبحث...',
    },
    changeProgram: { id: 'Ganti Program', ar: 'تغيير البرنامج' },
    noProgramFound: { id: 'Program tidak ditemukan.', ar: 'لم يتم العثور على برامج.' },
    kkmNote: {
      id: 'KKM Evaluasi menentukan kelulusan kuis/ujian ini secara mandiri. Berjalan berjenjang dan selaras dengan KKM Tahapan/Mata Pelajaran (standar: 70%).',
      ar: 'تحدد درجة نجاح التقييم اجتياز هذا الاختبار بشكل مستقل، وتعمل بالتكامل مع درجة نجاح المرحلة.',
    },
    btnSubmit: { id: 'Otorisasi Evaluasi', ar: 'اعتماد التقييم' },
    cardInfoTitle: { id: 'Akurasi Didaktik', ar: 'الدقة التعليمية' },
    cardInfoDesc: {
      id: 'Setelah menyetujui evaluasi ini, Anda akan diarahkan ke terminal pertanyaan untuk menyusun butir soal.',
      ar: 'بعد اعتماد هذا التقييم، سيتم توجيهك إلى شاشة الأسئلة لتحديد الأسئلة والخيارات.',
    },
    attachWeekly: {
      id: 'Ditempelkan pada sub-alur tertentu (Evaluasi Mingguan).',
      ar: 'ربط بمسار فرعي محدد (التقييم الأسبوعي).',
    },
    attachMonthly: {
      id: 'Ditempelkan pada fase tertentu (Evaluasi Bulanan / Tengah Semester).',
      ar: 'ربط بمرحلة محددة (التقييم الشهري / النصفي).',
    },
    attachMaterial: {
      id: 'Ditempelkan pada aset pembelajaran tertentu (Evaluasi Materi).',
      ar: 'ربط بأصل تعليمي محدد (تقييم المواد).',
    },
  },
  evaluationDetail: {
    title: { id: 'Konfigurasi Evaluasi', ar: 'إعدادات التقييم' },
    tabQuestions: { id: 'Daftar Pertanyaan', ar: 'الأسئلة' },
    tabSettings: { id: 'Pengaturan', ar: 'الإعدادات' },
    btnAddQuestion: { id: 'Tambah Pertanyaan', ar: 'إضافة سؤال' },
    labelQuestionText: { id: 'Teks Pertanyaan', ar: 'نص السؤال' },
    labelQuestionType: { id: 'Tipe Pertanyaan', ar: 'نوع السؤال' },
    optionMultipleChoice: { id: 'Pilihan Ganda', ar: 'اختيار من متعدد' },
    optionEssay: { id: 'Esai / Jawaban Singkat', ar: 'سؤال مقالي' },
    loading: { id: 'Memproses Data Evaluasi...', ar: 'جاري قراءة مصفوفة التقييم...' },
    error: { id: 'Gagal Membaca Data', ar: 'فشل في استرداد المصفوفة' },
    backToBank: { id: 'Kembali ke Bank Soal', ar: 'العودة إلى المستودع' },
    labelPlacement: { id: 'Penempatan:', ar: 'الموضع:' },
    labelProgram: { id: 'Program:', ar: 'البرنامج:' },
    labelLevel: { id: 'Fase:', ar: 'المرحلة:' },
    labelFlow: { id: 'Alur:', ar: 'المسار:' },
    labelMaterial: { id: 'Materi:', ar: 'المادة:' },
    emptyQuestions: {
      id: 'Belum ada soal dalam evaluasi ini. Mulai rancang pertanyaan baru.',
      ar: 'المصفوفة خالية حاليًا من وحدات المنطق. ابدأ بإضافة الأسئلة.',
    },
    editorDesc: {
      id: 'Rancang butir soal untuk instrumen evaluasi ini.',
      ar: 'قم ببناء سؤال تشخيصي لمصفوفة التقييم هذه.',
    },
    emptySelectedQuestions: {
      id: 'Tidak ada soal dalam evaluasi terpilih.',
      ar: 'لا توجد أسئلة في مصفوفة التقييم هذه.',
    },
    importInstructions: {
      id: 'Salin dan tempelkan teks dari Word / Google Docs (dengan format nomor dan abjad untuk pilihan ganda).',
      ar: 'انسخ والصق النص من مستندات جوجل/وورد (يجب أن يحتوي على أرقام وحروف للخيارات).',
    },
    btnImportQuestions: { id: 'Impor Pertanyaan', ar: 'استيراد الأسئلة' },
    btnDelete: { id: 'Hapus', ar: 'حذف' },
    labelPassing: { id: 'Kelulusan:', ar: 'درجة النجاح:' },
    labelDuration: { id: 'Waktu:', ar: 'الحد:' },
    labelTotal: { id: 'Total:', ar: 'الإجمالي:' },
    labelQuestionNode: { id: 'Soal', ar: 'عقد' },
    questionHeader: { id: 'Soal', ar: 'السؤال' },
    labelPoints: { id: 'PTS', ar: 'نقاط' },
    editorTitle: { id: 'Pengolah Soal', ar: 'مهندس الأسئلة' },
    labelQuestionPoints: { id: 'Bobot Nilai', ar: 'النقاط المخصصة' },
    labelExpectedAnswer: { id: 'Jawaban yang Diharapkan', ar: 'الإجابة المتوقعة' },
    labelOptions: { id: 'Pilihan Jawaban', ar: 'خيارات الإجابة' },
    btnAddAlternative: { id: 'Tambah Alternatif', ar: 'إضافة بديل' },
    btnAddOption: { id: 'Tambah Opsi', ar: 'إضافة خيار' },
    btnSaveQuestion: { id: 'Simpan Soal', ar: 'حفظ السؤال' },
    btnImportQuestionNodes: { id: 'Impor Butir Soal', ar: 'استيراد الأسئلة' },
    btnFromOtherEval: { id: 'Dari Evaluasi Lain', ar: 'من تقييم آخر' },
    btnFromTextFile: { id: 'Dari Teks / File', ar: 'من نص / ملف' },
    labelSelectSourceEval: { id: 'Pilih Evaluasi Sumber', ar: 'اختر التقييم المصدر' },
    labelPreviewQuestions: { id: 'Pratinjau Soal', ar: 'معاينة الأسئلة' },
    btnDownloadTemplate: { id: 'Download Template', ar: 'تحميل القالب' },
    labelPreviewResults: { id: 'Pratinjau Hasil', ar: 'معاينة النتيجة' },
    labelPreviewImport: { id: 'Pratinjau Impor', ar: 'معاينة الاستيراد' },
    labelItems: { id: 'butir', ar: 'سؤال' },
    btnEditText: { id: 'Edit Teks', ar: 'تعديل النص' },
    btnImportNow: { id: 'Impor Sekarang', ar: 'استيراد الآن' },
    btnSaveAll: { id: 'Simpan Semua', ar: 'حفظ الكل' },
    btnRetry: { id: 'Coba Lagi', ar: 'إعادة المحاولة' },
    // Fallback copy shown inside the import-error banner when the
    // server did not include a `message` on the thrown error. Kept in
    // sync with `runBulkAddFromText` and `runImportFromExisting`. The
    // try-block's `err.message ?? <fallback>` chain means anything
    // Prisma-shaped (like the `$queryRaw void` P2010 crash) still
    // surfaces in the user's active locale.
    importErrorBulk: {
      id: 'Gagal menyimpan soal dari teks. Coba lagi.',
      ar: 'فشل حفظ الأسئلة المستخرجة من النص. حاول مجدداً.',
    },
    importErrorExisting: {
      id: 'Gagal mengimpor soal dari evaluasi sumber. Coba lagi.',
      ar: 'فشل استيراد الأسئلة من تقييم آخر. حاول مجدداً.',
    },
    btnDeleteEval: { id: 'Hapus Evaluasi', ar: 'حذف التقييم' },
    btnDeletePermanently: { id: 'Hapus Permanen', ar: 'حذف نهائي' },
    confirmDeleteTitle: {
      id: 'Apakah Anda yakin ingin menghapus permanen',
      ar: 'هل أنت متأكد من حذف هذا التقييم نهائياً؟',
    },
    confirmDeleteDesc: {
      id: 'Semua pertanyaan, pilihan, dan riwayat pengerjaan akan dihapus.',
      ar: 'سيتم حذف جميع الأسئلة والخيارات وسجلات المحاولات.',
    },
    confirmDeleteActionText: {
      id: 'Tindakan ini tidak dapat dibatalkan.',
      ar: 'هذا الإجراء لا يمكن التراجع عنه.',
    },
    // i18n-cleanup additions for EvaluationDetail.tsx (PR follow-up to
    // PR #116). All keys below replace hardcoded
    // `language === 'ar' ? X : Y` ternaries that were left as last-mile
    // literals in the question editor + import modal. Keys are grouped
    // for edit-order readability but the runtime order is irrelevant.
    errorNotFound: {
      id: 'Evaluasi tidak ditemukan',
      ar: 'لم يتم العثور على عقدة التقييم',
    },
    defaultDescription: {
      id: 'Sistem evaluasi diagnostik terintegrasi.',
      ar: 'عقدة تقييم تشخيصية معتمدة من النظام.',
    },
    optionTrueFalse: {
      id: 'Benar / Salah',
      ar: 'صواب / خطأ',
    },
    optionShortAnswer: {
      id: 'Jawaban Singkat',
      ar: 'إجابة قصيرة',
    },
    placeholderQuestionText: {
      id: 'Masukkan teks pertanyaan...',
      ar: 'أدخل نص السؤال هنا...',
    },
    placeholderTrue: { id: 'Benar', ar: 'صواب' },
    placeholderFalse: { id: 'Salah', ar: 'خطأ' },
    placeholderExpectedAnswer: {
      id: 'Masukkan jawaban yang diharapkan...',
      ar: 'أدخل الإجابة المتوقعة...',
    },
    // Single-word prefix consumed as `${optionMultipleChoiceNth[language]} ${idx + 1}`
    // inside the Question Editor's multiple-choice option placeholder.
    optionMultipleChoiceNth: { id: 'Opsi', ar: 'خيار' },
    placeholderSourceEval: {
      id: '-- Pilih Evaluasi Sumber --',
      ar: '-- اختر التقييم المصدر --',
    },
    // Suffix for source-evaluation count, e.g. `(5 soal)` / `(5 سؤال)`.
    // Distinct from `labelItems` ('butir' / سؤال') which renders in
    // modal preview headers — those use 'butir' on purpose, so we keep
    // both keys.
    optionCountSuffix: { id: 'soal', ar: 'سؤال' },
    searchSourceEvalPlaceholder: {
      id: 'Cari judul evaluasi, program, atau materi...',
      ar: 'ابحث عن عنوان التقييم، البرنامج، أو المادة...',
    },
    filterAllTypes: { id: 'Semua Tipe', ar: 'جميع الأنواع' },
    filterHasQuestionsOnly: { id: 'Hanya yang Berisi Soal (>0)', ar: 'فقط التي تحتوي على أسئلة' },
    emptyEvaluationsFound: {
      id: 'Tidak ada evaluasi yang sesuai dengan kriteria pencarian.',
      ar: 'لم يتم العثور على أي تقييم يطابق معايير البحث.',
    },
    btnSelectAll: { id: 'Pilih Semua', ar: 'تحديد الكل' },
    btnDeselectAll: { id: 'Batalkan Pilihan', ar: 'إلغاء التحديد' },
    selectedQuestionsCount: { id: 'soal dipilih', ar: 'سؤال محدد' },
    tabBankSoal: { id: 'Bank Soal Evaluasi Lain', ar: 'بنك أسئلة من تقييم آخر' },
    tabDocument: { id: 'Dokumen / Berkas Teks', ar: 'مستند / ملف نصي' },
    dragDropTitle: {
      id: 'Tarik & lepas berkas ke sini, atau klik untuk memilih',
      ar: 'اسحب وأفلت الملف هنا، أو انقر للاختيار',
    },
    dragDropFormats: {
      id: 'Format berkas: .txt, .md, .csv, .json (Maks. 5MB)',
      ar: 'صيغ الملفات المدعومة: .txt, .md, .csv, .json (بحد أقصى 5 ميغابايت)',
    },
    directPasteTab: { id: 'Tempel Teks Manual', ar: 'لصق النص يدوياً' },
    fileUploadTab: { id: 'Unggah Berkas', ar: 'رفع ملف' },
    defaultPointsLabel: { id: 'Bobot Nilai Default per Soal', ar: 'النقاط الافتراضية لكل سؤال' },
    btnParseAndPreview: { id: 'Analisis & Pratinjau Soal', ar: 'تحليل ومعاينة الأسئلة' },
    previewSummaryTitle: { id: 'Ringkasan Hasil Analisis Dokumen', ar: 'ملخص تحليل المستند' },
    totalParsed: { id: 'Total Soal Terdeteksi', ar: 'إجمالي الأسئلة المستخرجة' },
    totalPoints: { id: 'Total Bobot Nilai', ar: 'إجمالي النقاط' },
    validStatus: { id: 'Siap Diimpor', ar: 'جاهز للاستيراد' },
    warningMissingCorrect: { id: 'Perlu Kunci Jawaban', ar: 'بحاجة لتحديد الإجابة الصحيحة' },
    btnEditQuestion: { id: 'Edit Soal', ar: 'تعديل السؤال' },
    btnDeleteQuestion: { id: 'Hapus dari Daftar', ar: 'حذف من القائمة' },
    btnBackToEditor: { id: 'Kembali Edit Teks', ar: 'العودة لتعديل النص' },
    btnImportSelected: { id: 'Impor {count} Soal Terpilih', ar: 'استيراد {count} سؤال محدد' },
    btnImportAll: { id: 'Impor Semua ({count}) Soal', ar: 'استيراد جميع الأسئلة ({count})' },
    optionAnswerKey: { id: 'Kunci Jawaban', ar: 'مفتاح الإجابة' },
    noQuestionsSelectedWarn: {
      id: 'Pilih minimal 1 butir soal untuk diimpor.',
      ar: 'يرجى اختيار سؤال واحد على الأقل للاستيراد.',
    },
    explanationLabel: { id: 'Pembahasan / Penjelasan', ar: 'الشرح / التوضيح' },
  },
  whatsAppGroups: {
    listTitle: { id: 'Integrasi Grup WhatsApp', ar: 'مجموعات الواتساب' },
    listSubtitle: {
      id: 'Sinkronisasi grup komunikasi peserta berdasarkan kategori kelas dan jenis kelamin.',
      ar: 'مزامنة مجموعات التواصل للمشاركين بناءً على فئة الفصل والجنس.',
    },
    btnAddGroup: { id: 'Tambah Grup', ar: 'إضافة مجموعة' },
    loadingGroups: { id: 'Memuat grup WhatsApp...', ar: 'جاري تحميل مجموعات الواتساب...' },
    errorTitle: { id: 'Gagal Memuat Grup', ar: 'خطأ في تحميل المجموعات' },
    noDescription: { id: 'Belum ada deskripsi grup', ar: 'لا يوجد وصف للمجموعة' },
    inspect: { id: 'Lihat', ar: 'فحص' },
    emptyTitle: { id: 'Tidak Ada Grup', ar: 'لا توجد مجموعات' },
    emptyState: {
      id: 'Buat grup WhatsApp pertama Anda untuk memulai koordinasi.',
      ar: 'أنشئ مجموعة واتساب الأولى لبدء التنسيق.',
    },
  },
  whatsAppGroupDetail: {
    title: { id: 'Detail Grup WhatsApp', ar: 'تفاصيل مجموعة الواتساب' },
    btnSync: { id: 'Sinkronkan Sekarang', ar: 'مزامنة الآن' },
    syncSuccess: {
      id: 'Sinkronisasi anggota grup berhasil.',
      ar: 'تمت مزامنة أعضاء المجموعة بنجاح.',
    },
    loadingChannel: { id: 'Mengambil data grup...', ar: 'جاري استرداد بيانات المجموعة...' },
    errorTitle: { id: 'Gagal Mengambil Grup', ar: 'خطأ في استرداد المجموعة' },
    errorFallback: { id: 'Terjadi kesalahan.', ar: 'حدث خطأ ما.' },
    returnToChannels: { id: 'Kembali ke Daftar Grup', ar: 'العودة إلى قائمة المجموعات' },
    backToChannels: { id: 'Kembali ke Daftar Grup', ar: 'العودة إلى المجموعات' },
    defaultDesc: {
      id: 'Grup yang dirancang khusus untuk interaksi komunal dalam matriks kurikulum.',
      ar: 'مجموعة مصممة خصيصًا للتفاعل الجماعي ضمن مصفوفة المنهج.',
    },
    whatsAppLink: { id: 'Tautan WhatsApp', ar: 'رابط الواتساب' },
    btnAddMember: { id: 'Tambah Anggota', ar: 'إضافة عضو' },
    statTotalEntities: { id: 'Total Anggota', ar: 'إجمالي الأعضاء' },
    statPopulation: { id: 'Populasi saat ini', ar: 'العدد الحالي' },
    statCapacity: { id: 'Kapasitas', ar: 'السعة' },
    statMaxAllocation: { id: 'Alokasi maksimal', ar: 'الحد الأقصى' },
    statSaturation: { id: 'Kepadatan', ar: 'الكثافة' },
    statChannelDensity: { id: 'Kepadatan grup', ar: 'كثافة المجموعة' },
    statAgeRange: { id: 'Rentang Usia', ar: 'الفئة العمرية' },
    statDemographic: { id: 'Filter demografis', ar: 'التصفية الديموغرافية' },
    statOpen: { id: 'Terbuka', ar: 'مفتوح' },
    memberMatrix: { id: 'Matriks Anggota Grup', ar: 'مصفوفة أعضاء المجموعة' },
    tableHeaderIdentity: { id: 'Identitas', ar: 'الهوية' },
    tableHeaderPhone: { id: 'Nomor Telepon', ar: 'رقم الهاتف' },
    tableHeaderEngagement: { id: 'Status Keterlibatan', ar: 'حالة المشاركة' },
    statusActive: { id: 'AKTIF', ar: 'نشط' },
    statusTerminated: { id: 'NONAKTIF', ar: 'غير نشط' },
    confirmRemove: {
      id: 'Apakah Anda yakin ingin menghapus anggota ini?',
      ar: 'هل أنت متأكد من إزالة هذا العضو؟',
    },
    emptyMembers: {
      id: 'Belum ada anggota dalam grup ini.',
      ar: 'لا يوجد أعضاء في هذه المجموعة حاليًا.',
    },
    modalAddTitle: { id: 'Tambah Anggota', ar: 'إضافة عضو' },
    modalAddDesc: {
      id: 'Daftarkan peserta secara manual ke dalam grup komunikasi ini.',
      ar: 'تسجيل مشارك يدويًا في مجموعة التواصل هذه.',
    },
    labelName: { id: 'Nama Anggota', ar: 'اسم العضو' },
    labelPhone: { id: 'Nomor Telepon', ar: 'رقم الهاتف' },
    placeholderName: { id: 'Masukkan nama anggota...', ar: 'أدخل اسم العضو...' },
    placeholderPhone: { id: 'Masukkan nomor telepon...', ar: 'أدخل رقم الهاتف...' },
    btnCancel: { id: 'Batal', ar: 'إلغاء' },
    btnConfirm: { id: 'Konfirmasi', ar: 'تأكيد' },
  },
  whatsAppGroupModal: {
    titleEdit: { id: 'Ubah Detail Grup', ar: 'تعديل تفاصيل المجموعة' },
    titleCreate: { id: 'Integrasi Grup Baru', ar: 'دمج مجموعة جديدة' },
    subtitle: {
      id: 'Konfigurasi parameter komunikasi dan demografi peserta.',
      ar: 'تكوين معلمات الاتصال وديموغرافية المشاركين.',
    },
    labelName: { id: 'Nama Grup', ar: 'اسم المجموعة' },
    placeholderName: { id: 'Misal: Kelas A - Ikhwan', ar: 'مثال: الفصل أ - إخوان' },
    labelProgram: { id: 'Program Terkait', ar: 'البرنامج المرتبط' },
    selectProgram: { id: 'Pilih program...', ar: 'اختر البرنامج...' },
    labelGender: { id: 'Filter Gender', ar: 'تصفية الجنس' },
    genderMixed: { id: 'Campuran (Semua)', ar: 'مختلط (الكل)' },
    genderIkhwan: { id: 'Ikhwan (Laki-laki)', ar: 'إخوان (ذكور)' },
    genderAkhwat: { id: 'Akhwat (Perempuan)', ar: 'أخوات (إناث)' },
    labelInviteLink: { id: 'Tautan Undangan WhatsApp', ar: 'رابط دعوة الواتساب' },
    placeholderInviteLink: {
      id: 'https://chat.whatsapp.com/...',
      ar: 'https://chat.whatsapp.com/...',
    },
    labelCapacity: { id: 'Kapasitas Anggota', ar: 'سعة الأعضاء' },
    capacityHelp: {
      id: 'Atur kapasitas maksimal grup. Saat penuh, peserta baru dapat diarahkan ke grup berikutnya.',
      ar: 'حدد السعة القصوى للمجموعة. عند الامتلاء، يمكن توجيه المشاركين الجدد إلى المجموعة التالية.',
    },
    labelAgeMin: { id: 'Usia Minimal', ar: 'الحد الأدنى للعمر' },
    placeholderAge: { id: 'Bebas', ar: 'بدون حد' },
    labelAgeMax: { id: 'Usia Maksimal', ar: 'الحد الأقصى للعمر' },
    labelDescription: { id: 'Catatan Internal / Deskripsi', ar: 'ملاحظة داخلية / الوصف' },
    placeholderDescription: { id: 'Tulis deskripsi grup...', ar: 'اكتب وصف المجموعة...' },
    programLocked: {
      id: 'Program tidak dapat diubah setelah grup dibuat agar penempatan peserta tetap konsisten.',
      ar: 'لا يمكن تغيير البرنامج بعد إنشاء المجموعة للحفاظ على اتساق توزيع المشاركين.',
    },
    requiredFieldsError: {
      id: 'Isi nama grup dan pilih program terkait.',
      ar: 'أدخل اسم المجموعة واختر البرنامج المرتبط.',
    },
    capacityError: {
      id: 'Kapasitas harus berupa angka bulat minimal 1 anggota.',
      ar: 'يجب أن تكون السعة عددًا صحيحًا لا يقل عن عضو واحد.',
    },
    ageRangeError: {
      id: 'Usia minimal tidak boleh lebih besar dari usia maksimal.',
      ar: 'لا يجوز أن يكون الحد الأدنى للعمر أكبر من الحد الأقصى.',
    },
    inviteLinkError: {
      id: 'Masukkan tautan undangan WhatsApp yang valid dan menggunakan https.',
      ar: 'أدخل رابط دعوة واتساب صالحًا يستخدم https.',
    },
    saveError: {
      id: 'Konfigurasi grup belum dapat disimpan. Periksa kembali isian Anda.',
      ar: 'تعذر حفظ إعدادات المجموعة. يرجى مراجعة البيانات المدخلة.',
    },
    abort: { id: 'Batal', ar: 'إلغاء' },
    submitEdit: { id: 'Simpan Perubahan', ar: 'حفظ التغييرات' },
    submitCreate: { id: 'Daftarkan Grup', ar: 'تسجيل المجموعة' },
  },
  participants: {
    listTitle: { id: 'Pemantauan Peserta', ar: 'مراقبة المشاركين' },
    listSubtitle: {
      id: 'Temukan peserta yang perlu perhatian, lalu buka dossier belajar untuk menindaklanjutinya.',
      ar: 'مراقبة تقدم المشاركين وحالتهم الأكاديمية عبر جميع البرامج المؤسسية.',
    },
    directoryLabel: { id: 'Direktori pembelajaran', ar: 'دليل التعلم' },
    refresh: { id: 'Segarkan data', ar: 'تحديث البيانات' },
    refreshing: { id: 'Menyegarkan…', ar: 'جاري التحديث…' },
    searchPlaceholder: {
      id: 'Cari berdasarkan nama atau program...',
      ar: 'البحث عن طريق الاسم أو البرنامج...',
    },
    searchLabel: { id: 'Cari peserta atau program', ar: 'البحث عن مشارك أو برنامج' },
    filterLabel: { id: 'Status pendaftaran', ar: 'حالة التسجيل' },
    allStatuses: { id: 'Semua status', ar: 'كل الحالات' },
    activeStatus: { id: 'Aktif', ar: 'نشط' },
    pausedStatus: { id: 'Dijeda', ar: 'موقوف' },
    completedStatus: { id: 'Selesai', ar: 'مكتمل' },
    droppedStatus: { id: 'Tidak dilanjutkan', ar: 'منقطع' },
    totalEnrollments: { id: 'Pendaftaran ditemukan', ar: 'تسجيلات تم العثور عليها' },
    averageProgress: { id: 'Rata-rata kemajuan', ar: 'متوسط التقدم' },
    activeEnrollments: { id: 'Peserta aktif', ar: 'المشاركون النشطون' },
    attentionOnPage: { id: 'Perlu perhatian di halaman ini', ar: 'يحتاج إلى اهتمام في هذه الصفحة' },
    attentionDescription: {
      id: 'Aktif dengan kemajuan di bawah 50%.',
      ar: 'نشط بتقدم أقل من ٥٠٪.',
    },
    directoryTitle: { id: 'Daftar pemantauan', ar: 'قائمة المراقبة' },
    resultRange: {
      id: 'Menampilkan {from}–{to} dari {total} pendaftaran',
      ar: 'عرض {from}–{to} من {total} تسجيل',
    },
    openDossier: { id: 'Buka dossier peserta', ar: 'فتح ملف المشارك' },
    retry: { id: 'Coba lagi', ar: 'حاول مرة أخرى' },
    failedLoad: { id: 'Data pemantauan tidak dapat dimuat.', ar: 'تعذر تحميل بيانات المراقبة.' },
    resetFilters: { id: 'Atur ulang filter', ar: 'إعادة تعيين عوامل التصفية' },
    syncingEnrollments: {
      id: 'Menyelaraskan data pendaftaran...',
      ar: 'جاري مزامنة بيانات التسجيل...',
    },
    tableHeaderParticipant: { id: 'Peserta', ar: 'المشارك' },
    tableHeaderActiveProgram: { id: 'Program Aktif', ar: 'البرنامج النشط' },
    tableHeaderProgressPath: { id: 'Kemajuan Belajar', ar: 'مسار التقدم' },
    tableHeaderCurrentStatus: { id: 'Status Saat Ini', ar: 'الحالة الحالية' },
    selectEnrollment: { id: 'Pilih akses program', ar: 'اختيار وصول البرنامج' },
    selectAllOnPage: { id: 'Pilih semua di halaman ini', ar: 'اختيار الكل في هذه الصفحة' },
    selectedCount: { id: '{count} akses dipilih', ar: 'تم اختيار {count} وصول' },
    removeAccess: { id: 'Hapus akses program', ar: 'إزالة وصول البرنامج' },
    removeAccessTitle: { id: 'Hapus akses program peserta?', ar: 'إزالة وصول برنامج المشارك؟' },
    removeAccessDescription: {
      id: 'Progres materi, hasil belajar, ujian baru, refleksi, dan akses grup peserta akan dihapus. Transkrip dari sistem lama tetap tersimpan.',
      ar: 'سيتم حذف تقدم المواد ونتائج التعلم والاختبارات الجديدة والتأملات ووصول المجموعة. تبقى كشوف النظام السابق محفوظة.',
    },
    removeAccessReason: { id: 'Alasan penghapusan akses', ar: 'سبب إزالة الوصول' },
    removeAccessReasonPlaceholder: {
      id: 'Contoh: Peserta mengulang program pada angkatan berikutnya.',
      ar: 'مثال: سيعيد المشارك البرنامج في دفعة لاحقة.',
    },
    removeAccessConfirm: {
      id: 'Saya memahami data belajar versi baru akan dihapus dan peserta harus mendaftar ulang.',
      ar: 'أفهم أن بيانات التعلم الجديدة ستحذف وأن المشارك يجب أن يسجل مجدداً.',
    },
    removeAccessConfirmAction: { id: 'Hapus akses terpilih', ar: 'إزالة الوصول المحدد' },
    removeAccessSuccess: {
      id: 'Akses program berhasil dihapus.',
      ar: 'تمت إزالة وصول البرنامج بنجاح.',
    },
    removeAccessFailure: {
      id: 'Akses program tidak dapat dihapus.',
      ar: 'تعذر إزالة وصول البرنامج.',
    },
    removeAccessLimit: {
      id: 'Pilih maksimal 50 akses program dalam satu tindakan.',
      ar: 'اختر بحد أقصى ٥٠ وصولاً للبرنامج في إجراء واحد.',
    },
    emptyState: {
      id: 'Tidak ada data pendaftaran yang terkalibrasi.',
      ar: 'لم يتم العثور على سجلات تسجيل متطابقة.',
    },
  },
  participantDetail: {
    loadingTelemetry: {
      id: 'Menyelaraskan telemetri peserta...',
      ar: 'جاري مزامنة بيانات القياس للمشارك...',
    },
    notFoundTitle: { id: 'Peserta Tidak Ditemukan', ar: 'لم يتم العثور على المشارك' },
    notFoundDesc: {
      id: 'Daftar pendaftaran yang diminta tidak dapat ditemukan di database operasional.',
      ar: 'تعذر استرداد ملف التسجيل المطلوب من قاعدة البيانات التشغيلية.',
    },
    btnReturnDirectory: { id: 'Kembali ke Direktori', ar: 'العودة إلى الدليل' },
    monitoring: { id: 'Pemantauan', ar: 'المراقبة' },
    activeStatus: { id: 'Status Aktif', ar: 'حالة نشطة' },
    btnExportDossier: { id: 'Unduh Berkas', ar: 'تصدير الملف' },
    btnModifyPath: { id: 'Ubah Alur', ar: 'تعديل المسار' },
    masteryTitle: { id: 'Penguasaan Kurikulum', ar: 'إتقان المنهج' },
    masteryDesc: {
      id: 'Tingkat penyelesaian terkalibrasi di seluruh fase aktif.',
      ar: 'مستويات الإكمال المعتمدة عبر المراحل النشطة.',
    },
    masteryOverall: { id: 'Keseluruhan:', ar: 'الإجمالي:' },
    masteryComplete: { id: 'Selesai', ar: 'مكتمل' },
    phaseLabel: { id: 'Fase', ar: 'المرحلة' },
    noAssets: { id: 'Tidak ada materi di fase ini.', ar: 'لا توجد أصول مخصصة لهذه المرحلة.' },
    requiredBadge: { id: 'Wajib', ar: 'مطلوب' },
    completedBadge: { id: 'SELESAI', ar: 'مكتمل' },
    noLevels: {
      id: 'Tidak ada tingkatan pembelajaran yang ditentukan untuk program ini.',
      ar: 'لم يتم تحديد مستويات تعليمية لهذا البرنامج.',
    },
    evaluationMatrix: { id: 'Matriks Evaluasi', ar: 'مصفوفة التقييم' },
    viewTranscript: { id: 'Lihat Transkrip', ar: 'عرض كشف الدرجات' },
    tableHeaderNode: { id: 'Node Validasi', ar: 'وحدة التحقق' },
    tableHeaderCalibration: { id: 'Kalibrasi', ar: 'التاريخ' },
    tableHeaderScore: { id: 'Nilai', ar: 'الدرجة' },
    tableHeaderStatus: { id: 'Status', ar: 'الحالة' },
    statusProficient: { id: 'Mahir', ar: 'متقن' },
    statusComplete: { id: 'Selesai', ar: 'مكتمل' },
    noResults: {
      id: 'Tidak ada hasil penilaian tervalidasi untuk berkas ini.',
      ar: 'لا توجد نتائج تقييم معتمدة متزامنة لهذا الملف.',
    },
    insightsTitle: { id: 'Analisis Kemajuan', ar: 'تحليل التقدم' },
    insightsDesc: {
      id: 'Sistem sedang merekam aktivitas belajar untuk {name}. Prediksi perkembangan akan muncul setelah evaluasi berikutnya.',
      ar: 'يقوم النظام حاليًا بتسجيل نشاط التعلم لـ {name}. ستظهر توقعات التقدم بعد بوابة التقييم التالية.',
    },
    insightsRecInterventions: { id: 'Rekomendasi Intervensi', ar: 'التدخلات الموصى بها' },
    insightsAwaitingData: {
      id: 'Menunggu data yang cukup untuk analisis...',
      ar: 'في انتظار بيانات كافية لمسارات التحليل...',
    },
    insightsBtnExecute: { id: 'Jalankan Jalur Strategis', ar: 'تنفيذ المسار الاستراتيجي' },
    reflectionsTitle: { id: 'Refleksi Pembelajaran', ar: 'الخواطر والتعليقات' },
    noReflections: { id: 'Belum ada refleksi yang dicatat.', ar: 'لم يتم توثيق أي خواطر بعد.' },
    viewAnalyticalArchive: { id: 'Lihat Arsip Analisis', ar: 'عرض الأرشيف التحليلي' },
    metricDuration: { id: 'Durasi Pembelajaran', ar: 'مدة التعلم' },
    metricActiveDays: { id: 'HARI AKTIF', ar: 'يوم نشط' },
    auditLog: {
      id: 'Log Kalibrasi Eksekutif: {admin} menyelaraskan berkas ini pada {date}',
      ar: 'سجل المعايرة التنفيذي: قام {admin} بمزامنة هذا الملف في {date}',
    },
    seniorScholarLabel: { id: 'Pelajar Senior', ar: 'باحث أول' },
    exportDossierAlert: {
      id: 'Mengekspor Berkas untuk {name}... [Fitur Segera Hadir]',
      ar: 'جاري تصدير ملف {name}... [قريباً]',
    },
    modifyPathAlert: {
      id: 'Modifikasi alur diminta. Sistem sedang mengkalibrasi ulang...',
      ar: 'تم طلب تعديل المسار. جاري إعادة معايرة النظام...',
    },
    syncTranscriptAlert: {
      id: 'Menyelaraskan transkrip resmi untuk {name}...',
      ar: 'جاري مزامنة كشف الدرجات الرسمي لـ {name}...',
    },
    recalibrateAlert: {
      id: 'Mengkalibrasi ulang jalur strategis berdasarkan kemajuan saat ini...',
      ar: 'إعادة معايرة المسار الاستراتيجي بناءً على التقدم الحالي...',
    },
    materialReflectionFallback: { id: 'Refleksi Kursus', ar: 'تعليقات المادة' },
    openArchiveAlert: {
      id: 'Membuka arsip ilmiah lengkap...',
      ar: 'جاري فتح الأرشيف العلمي الكامل...',
    },
    footerTranscriptLabel: { id: 'Transkrip Global', ar: 'كشف الدرجات العام' },
    footerAuditLabel: { id: 'Audit Utama', ar: 'التدقيق الرئيسي' },
    footerSyncLabel: { id: 'Sinkronisasi Jalur', ar: 'مزامنة المسار' },
    syncSuccessAlert: { id: '{feature} berhasil disinkronkan.', ar: 'تم مزامنة {feature} بنجاح.' },
    transcriptSemester: { id: 'Semester', ar: 'الفصل الدراسي' },
    transcriptPredicate: { id: 'Predikat', ar: 'التقدير' },
    transcriptScore: { id: 'Nilai', ar: 'الدرجة' },
    transcriptPercentage: { id: '%', ar: '%' },
    transcriptStatus: { id: 'Status', ar: 'الحالة' },
    transcriptCompleted: { id: 'Selesai', ar: 'مكتمل' },
    transcriptInProgress: { id: 'Belum', ar: 'قيد التنفيذ' },
  },
  priorLearning: {
    unfinishedSourceWarning: {
      id: 'Arsip lama bertanda belum selesai. Status ini tidak diubah. Pengakuan hanya berlaku untuk mata pelajaran yang setara dengan bukti nilai >70 dan memenuhi KKM, bukan kelulusan seluruh level atau program.',
      ar: 'السجل السابق معلّم بأنه غير مكتمل، ولن تتغير حالته. الاعتراف للمادة المكافئة فقط مع دليل درجة تتجاوز 70 وتحقق الحد المطلوب، وليس نجاح المستوى أو البرنامج كله.',
    },
    sourceSearch: {
      id: 'Cari nama / kode pelajaran lama',
      ar: 'ابحث عن اسم أو رمز المادة السابقة',
    },
    estimatedWarning: {
      id: 'Angka arsip ini dapat berasal dari konversi predikat/IPK, bukan nilai ujian asli. Periksa bukti nilai mata pelajaran asli: harus >70 dan memenuhi KKM tujuan. Tulis dasar verifikasi pada alasan pengesahan.',
      ar: 'قد تكون هذه الدرجة تحويلًا للتقدير أو المعدل وليست درجة الاختبار الأصلية. تحقق من دليل الدرجة الأصلية: يجب أن تزيد عن 70 وتحقق الحد المستهدف. سجّل أساس التحقق في السبب.',
    },
    title: { id: 'Pengakuan Belajar Lama', ar: 'الاعتراف بالتعلم السابق' },
    intro: {
      id: 'Nilai lama di atas 70 dapat diakui jika mata pelajaran setara dan memenuhi KKM, termasuk arsip berstatus belum selesai. Jika KKM evaluasi terkait terpenuhi, pengakuan juga menjadi dasar melanjutkan level tersebut tanpa mengulang level sebelumnya; prasyarat pelajaran dan ujian tetap berlaku. Ini bukan kelulusan otomatis dan tidak mengubah nilai/status arsip, menghapus riwayat, maupun membuat jawaban ujian baru.',
      ar: 'يمكن الاعتراف بالدرجات السابقة فوق 70 إذا كانت المادة مكافئة وتحقق الحد المطلوب، حتى للسجلات غير المكتملة. إذا استوفيت حدود الاختبارات ذات الصلة، يتيح الاعتراف متابعة ذلك المستوى دون إعادة المستويات السابقة، مع بقاء متطلبات المواد والاختبارات. هذا ليس نجاحاً تلقائياً، ولا يغيّر درجات السجل أو حالته أو يحذف التاريخ أو ينشئ إجابات اختبار جديدة.',
    },
    loading: { id: 'Memuat nilai lama…', ar: 'جارٍ تحميل الدرجات السابقة…' },
    loadError: {
      id: 'Nilai lama belum dapat dimuat. Coba kembali.',
      ar: 'تعذر تحميل الدرجات السابقة. حاول مجدداً.',
    },
    retry: { id: 'Muat ulang', ar: 'إعادة التحميل' },
    search: {
      id: 'Cari mata pelajaran / kode / tahapan',
      ar: 'ابحث عن المادة أو الرمز أو المرحلة',
    },
    subject: { id: 'Mata pelajaran tujuan', ar: 'المادة المستهدفة' },
    choose: { id: 'Pilih mata pelajaran', ar: 'اختر المادة' },
    source: { id: 'Nilai lama peserta', ar: 'الدرجة السابقة للطالب' },
    chooseSource: { id: 'Pilih nilai lama', ar: 'اختر الدرجة السابقة' },
    reason: {
      id: 'Alasan dan dasar kesetaraan (5–1000 karakter)',
      ar: 'سبب الاعتراف وأساس التكافؤ (5–1000 حرف)',
    },
    confirmation: { id: 'Ketik {word} untuk konfirmasi', ar: 'اكتب {word} للتأكيد' },
    approve: { id: 'Sahkan pengakuan', ar: 'اعتماد الاعتراف' },
    revoke: { id: 'Cabut pengakuan', ar: 'إلغاء الاعتراف' },
    automatic: { id: 'Diakui otomatis', ar: 'اعتراف تلقائي' },
    manual: { id: 'Disahkan admin', ar: 'معتمد من المشرف' },
    revoked: { id: 'Dicabut admin', ar: 'ملغى بواسطة المشرف' },
    unmatched: { id: 'Belum diakui', ar: 'لم يُعترف بعد' },
    reset: { id: 'Wajib ujian ulang setelah reset', ar: 'يجب إعادة الاختبار بعد إعادة الضبط' },
    noMaterials: { id: 'Belum ada materi tujuan', ar: 'لا توجد مواد مستهدفة' },
    empty: {
      id: 'Tidak ada mata pelajaran yang sesuai pencarian.',
      ar: 'لا توجد مواد تطابق البحث.',
    },
    noSources: {
      id: 'Tidak ada nilai lama yang memenuhi syarat. Nilai kosong/predikat saja tidak diperlakukan sebagai nilai lulus.',
      ar: 'لا توجد درجات سابقة مؤهلة. الدرجة الفارغة أو التقدير وحده لا يُعد نجاحاً.',
    },
    verify: {
      id: 'Saya sudah memeriksa bukti nilai mata pelajaran >70, KKM tujuan, dan kesetaraan pelajaran. Nilai ini bukan sekadar rata-rata semester. Dasar verifikasi tercantum pada alasan.',
      ar: 'تحققت من دليل درجة المادة فوق 70 والحد المستهدف وتكافؤ المادتين، وأنها ليست مجرد متوسط الفصل. أساس التحقق موضح في السبب.',
    },
    saved: { id: 'Pengakuan dan jejak audit tersimpan.', ar: 'حُفظ الاعتراف وسجل التدقيق.' },
    syncPending: {
      id: 'Pengakuan tersimpan, tetapi ringkasan progres belum diperbarui. Muat ulang; akses materi tetap mengikuti keputusan tersimpan.',
      ar: 'حُفظ الاعتراف لكن تحديث ملخص التقدم لم يكتمل. أعد التحميل؛ الوصول يتبع القرار المحفوظ.',
    },
    history: {
      id: 'Keputusan terakhir: {date} • Admin {admin}',
      ar: 'آخر قرار: {date} • المشرف {admin}',
    },
    showMore: { id: 'Tampilkan mata pelajaran lainnya', ar: 'عرض مواد إضافية' },
    sourceHint: {
      id: 'KKM tujuan {score}%. Nilai harus >70 dan setidaknya memenuhi KKM. Nilai lama tidak ditimpa.',
      ar: 'درجة النجاح المستهدفة {score}٪. الدرجة يجب أن تزيد عن 70 وتحقق الحد الأدنى. لا يتم استبدال الدرجة السابقة.',
    },
    blocks: {
      SOURCE_RESET: {
        id: 'Nilai asal telah direset untuk ujian ulang',
        ar: 'أُعيد ضبط الدرجة الأصلية لإعادة الاختبار',
      },
      NO_SCORE: { id: 'Nilai angka belum tersedia', ar: 'الدرجة الرقمية غير متاحة' },
      LOW_SCORE: { id: 'Nilai tidak di atas 70', ar: 'الدرجة لا تزيد عن 70' },
      INVALID_SCORE: { id: 'Nilai tidak valid', ar: 'درجة غير صالحة' },
      FAILED: { id: 'Predikat tidak lulus', ar: 'تقدير غير ناجح' },
      UNFINISHED: { id: 'Pelajaran lama belum selesai', ar: 'المادة السابقة لم تكتمل' },
      INCOMPATIBLE_PROGRAM: { id: 'Program tidak sesuai', ar: 'برنامج غير متوافق' },
    },
  },
  users: {
    listTitle: { id: 'Manajemen Pengguna', ar: 'إدارة المستخدمين' },
    listSubtitle: {
      id: 'Kelola peran, hak akses, dan akun administrator serta pengajar.',
      ar: 'إدارة الأدوار، الصلاحيات، وحسابات المشرفين والمعلمين.',
    },
    btnAddUser: { id: 'Tambah Pengguna', ar: 'إضافة مستخدم' },
    searchPlaceholder: {
      id: 'Cari nama, email, atau Nomor Induk Peserta...',
      ar: 'البحث بالاسم أو البريد أو الرقم الأكاديمي للمشارك...',
    },
    btnFilterRoles: { id: 'Filter Peran', ar: 'تصفية الأدوار' },
    filterProgramPlaceholder: { id: 'Filter berdasarkan Program...', ar: 'تصفية حسب البرنامج...' },
    filterProgramLabel: { id: 'Program:', ar: 'البرنامج:' },
    noProgramsFound: { id: 'Program tidak ditemukan', ar: 'لم يتم العثور على البرنامج' },
    clearProgramFilter: { id: 'Hapus filter program', ar: 'إزالة تصفية البرنامج' },
    searchProgramPlaceholder: {
      id: 'Ketik nama / kode program...',
      ar: 'اكتب اسم / رمز البرنامج...',
    },
    syncingDirectory: {
      id: 'Menyelaraskan direktori pengguna...',
      ar: 'جاري مزامنة دليل المستخدمين...',
    },
    tableHeaderUser: { id: 'Identitas Pengguna', ar: 'هوية المستخدم' },
    tableHeaderRoles: { id: 'Penetapan Peran', ar: 'تعيين الأدوار' },
    tableHeaderStatus: { id: 'Status Akun', ar: 'حالة الحساب' },
    btnDeactivate: { id: 'Nonaktifkan', ar: 'إلغاء التفعيل' },
    btnActivate: { id: 'Aktifkan', ar: 'تفعيل' },
    btnEditIdentity: { id: 'Edit Identitas', ar: 'تعديل الهوية' },
    btnAssignRole: { id: 'Tetapkan Peran', ar: 'تعيين دور' },
    emptyState: { id: 'Tidak ada pengguna yang ditemukan.', ar: 'لم يتم العثور على مستخدمين.' },
    modalTitle: { id: 'Tambah Pengguna Baru', ar: 'إضافة مستخدم جديد' },
    modalSubtitle: { id: 'Autentikasi Pengguna Baru', ar: 'مصادقة المستخدم الجديد' },
    labelName: { id: 'Nama Lengkap', ar: 'الاسم الكامل' },
    labelEmail: { id: 'Protokol Komunikasi (Email)', ar: 'بروتوكول التواصل (البريد الإلكتروني)' },
    labelPassword: { id: 'Kata Sandi Akses', ar: 'كلمة مرور الوصول' },
    labelRole: { id: 'Peran Hak Akses', ar: 'دور صلاحيات الوصول' },
    roleParticipant: { id: 'Peserta', ar: 'مشارك' },
    roleAdmin: { id: 'Administrator', ar: 'مشرف' },
    roleTeacher: { id: 'Pengajar', ar: 'معلم' },
    btnCancel: { id: 'Batal', ar: 'إلغاء' },
    btnAuthorize: { id: 'Otorisasi Pengguna', ar: 'اعتماد المستخدم' },
    btnDelete: { id: 'Hapus Pengguna', ar: 'حذف المستخدم' },
    confirmDelete: {
      id: 'Apakah Anda yakin ingin menghapus pengguna ini?',
      ar: 'هل أنت متأكد من حذف هذا المستخدم؟',
    },
    editUserTitle: { id: 'Edit Profil Pengguna', ar: 'تعديل ملف المستخدم' },
    editUserSubtitle: {
      id: 'Lengkapi data profil sebelum melanjutkan persetujuan.',
      ar: 'أكمل بيانات الملف الشخصي قبل المتابعة بالاعتماد.',
    },
    editUserMissingFieldsBanner: {
      id: 'Persetujuan tertahan: data profil berikut wajib diisi.',
      ar: 'تم تعليق الاعتماد: الحقول التالية في الملف الشخصي مطلوبة.',
    },
    fieldName: { id: 'Nama Lengkap', ar: 'الاسم الكامل' },
    fieldPhone: { id: 'Nomor WhatsApp', ar: 'رقم الواتساب' },
    fieldPhonePlaceholder: { id: 'Contoh: 081234567890', ar: 'مثال: 0812xxxxxxxx' },
    fieldGender: { id: 'Jenis Kelamin', ar: 'الجنس' },
    genderMale: { id: 'Laki-laki (Ikhwan)', ar: 'ذكر (إخوان)' },
    genderFemale: { id: 'Perempuan (Akhwat)', ar: 'أنثى (أخوات)' },
    btnSaveAndApprove: { id: 'Simpan & Setujui', ar: 'حفظ واعتماد' },
    btnSaveOnly: { id: 'Simpan Profil', ar: 'حفظ الملف' },
    requiredField: { id: 'Wajib diisi', ar: 'مطلوب' },
    detailTitle: { id: 'Detail Pengguna', ar: 'تفاصيل المستخدم' },
    detailProfile: { id: 'Profil', ar: 'الملف الشخصي' },
    detailActivities: { id: 'Aktivitas Terbaru', ar: 'الأنشطة الأخيرة' },
    detailEnrollments: { id: 'Program yang Diikuti', ar: 'البرامج المسجلة' },
    detailLastActivity: { id: 'Aktivitas Terakhir', ar: 'النشاط الأخير' },
    detailNoActivity: { id: 'Belum ada aktivitas tercatat.', ar: 'لا توجد أنشطة مسجلة حتى الآن.' },
    detailNoEnrollments: {
      id: 'Belum terdaftar di program apapun.',
      ar: 'غير مسجل في أي برنامج حتى الآن.',
    },
    btnClose: { id: 'Tutup', ar: 'إغلاق' },
    actionLabel: { id: 'Aksi', ar: 'الإجراء' },
    entityLabel: { id: 'Entitas', ar: 'الكيان' },
    roleUserFallback: { id: 'USER', ar: 'مستخدم' },
    statusActive: { id: 'AKTIF', ar: 'نشط' },
    statusInactive: { id: 'NONAKTIF', ar: 'غير نشط' },
    placeholderName: { id: 'Misal: John Doe', ar: 'مثال: جون دو' },
    placeholderEmail: { id: 'identitas@institusi.edu', ar: 'identity@institution.edu' },
    labelNameShort: { id: 'Nama', ar: 'الاسم' },
    labelEmailShort: { id: 'Email', ar: 'البريد الإلكتروني' },
    labelPhoneShort: { id: 'Telepon', ar: 'الهاتف' },
    labelDateOfBirth: { id: 'Tanggal Lahir', ar: 'تاريخ الميلاد' },
    labelOccupation: { id: 'Pekerjaan', ar: 'العمل' },
    labelEducation: { id: 'Pendidikan Terakhir', ar: 'آخر مؤهل تعليمي' },
    backgroundOptions: {
      STUDENT: { id: 'Pelajar / Mahasiswa', ar: 'طالب / طالبة' },
      TEACHER: { id: 'Pendidik', ar: 'معلّم / معلّمة' },
      PRIVATE_EMPLOYEE: { id: 'Karyawan swasta', ar: 'موظف قطاع خاص' },
      CIVIL_SERVANT: { id: 'ASN / PNS', ar: 'موظف حكومي' },
      ENTREPRENEUR: { id: 'Wirausaha', ar: 'رائد أعمال' },
      HOMEMAKER: { id: 'Ibu rumah tangga', ar: 'ربة منزل' },
      HEALTHCARE_PROFESSIONAL: { id: 'Tenaga kesehatan', ar: 'ممارس صحي' },
      NOT_CURRENTLY_WORKING: { id: 'Belum / tidak sedang bekerja', ar: 'لا أعمل حالياً' },
      NO_FORMAL_EDUCATION: { id: 'Belum / tidak ada pendidikan formal', ar: 'لا تعليم نظامي' },
      PRIMARY: { id: 'SD / sederajat', ar: 'ابتدائي أو ما يعادله' },
      JUNIOR_SECONDARY: { id: 'SMP / sederajat', ar: 'متوسط أو ما يعادله' },
      SENIOR_SECONDARY: { id: 'SMA / SMK / sederajat', ar: 'ثانوي أو ما يعادله' },
      DIPLOMA: { id: 'Diploma', ar: 'دبلوم' },
      BACHELOR: { id: 'S1 / Sarjana', ar: 'بكالوريوس' },
      MASTER: { id: 'S2 / Magister', ar: 'ماجستير' },
      DOCTORAL: { id: 'S3 / Doktor', ar: 'دكتوراه' },
      OTHER: { id: 'Lainnya', ar: 'أخرى' },
    },
    ageYears: { id: '{age} tahun', ar: '{age} سنة' },
    confirmResetAttempts: {
      id: 'Apakah Anda yakin ingin me-reset percobaan evaluasi untuk peserta ini? Semua jawaban dan skor sebelumnya akan dihapus.',
      ar: 'هل أنت متأكد من إعادة تعيين محاولات التقييم لهذا المشارك؟ سيتم حذف جميع الإجابات والدرجات السابقة.',
    },
    activeProgramBadge: { id: 'Program Aktif', ar: 'البرنامج النشط' },
    evaluationsSectionHeader: { id: 'Evaluasi & Ujian', ar: 'التقييمات والاختبارات' },
    notAttemptedStatus: { id: 'BELUM DIKERJAKAN', ar: 'لم يتم البدء بعد' },
    resetAttemptsTooltip: { id: 'Reset Percobaan', ar: 'إعادة تعيين المحاولات' },
    evaluationProgress: {
      title: { id: 'Progres Ujian & Kuis', ar: 'تقدم الاختبارات والواجبات القصيرة' },
      accessTitle: { id: 'Reset ujian & kuis peserta', ar: 'إعادة تعيين اختبارات المشارك' },
      accessDescription: {
        id: 'Buka kartu program untuk melihat progres ujian dan kuis. Gunakan ikon reset pada ujian tertentu atau tombol reset semua ujian program.',
        ar: 'افتح بطاقة البرنامج لعرض تقدم الاختبارات والواجبات القصيرة. استخدم رمز إعادة التعيين لاختبار محدد أو زر إعادة جميع اختبارات البرنامج.',
      },
      total: { id: 'Total evaluasi', ar: 'إجمالي التقييمات' },
      attempted: { id: 'Sudah dikerjakan', ar: 'تمت المحاولة' },
      passed: { id: 'Lulus', ar: 'ناجح' },
      average: { id: 'Rata-rata skor', ar: 'متوسط الدرجات' },
      searchLabel: { id: 'Cari ujian atau lingkup', ar: 'البحث عن اختبار أو نطاق' },
      searchPlaceholder: {
        id: 'Ketik judul, materi, atau mata pelajaran...',
        ar: 'اكتب العنوان أو المادة أو المقرر...',
      },
      filterLabel: { id: 'Status pengerjaan', ar: 'حالة المحاولة' },
      allStatuses: { id: 'Semua status', ar: 'كل الحالات' },
      status: {
        NOT_STARTED: { id: 'Belum dikerjakan', ar: 'لم يبدأ' },
        IN_PROGRESS: { id: 'Sedang dikerjakan', ar: 'قيد التنفيذ' },
        PASSED: { id: 'Lulus', ar: 'ناجح' },
        FAILED: { id: 'Belum lulus', ar: 'لم ينجح' },
      },
      empty: {
        id: 'Tidak ada ujian atau kuis yang sesuai filter.',
        ar: 'لا توجد اختبارات مطابقة لعوامل التصفية.',
      },
      noAttempts: { id: 'Belum ada riwayat attempt.', ar: 'لا يوجد سجل محاولات.' },
      expandHistory: { id: 'Buka riwayat percobaan', ar: 'فتح سجل المحاولات' },
      collapseHistory: { id: 'Tutup riwayat percobaan', ar: 'إغلاق سجل المحاولات' },
      resetEvaluation: { id: 'Reset ujian ini', ar: 'إعادة تعيين هذا الاختبار' },
      attemptLabel: { id: 'Percobaan', ar: 'محاولة' },
      bestScoreLabel: { id: 'Skor terbaik', ar: 'أفضل نتيجة' },
      loadError: {
        id: 'Data progres ujian belum dapat dimuat.',
        ar: 'تعذر تحميل بيانات تقدم الاختبارات.',
      },
      retry: { id: 'Coba lagi', ar: 'حاول مرة أخرى' },
      resetProgram: { id: 'Reset semua ujian program', ar: 'إعادة جميع اختبارات البرنامج' },
      resetProgramTitle: {
        id: 'Reset seluruh ujian dalam program?',
        ar: 'إعادة تعيين جميع اختبارات البرنامج؟',
      },
      resetEvaluationTitle: { id: 'Reset ujian peserta?', ar: 'إعادة تعيين اختبار المشارك؟' },
      resetWarning: {
        id: 'Attempt, jawaban, skor, dan hasil pembelajaran versi sistem saat ini akan dihapus lalu progres program dihitung ulang. Riwayat nilai sistem lama tetap tersimpan sebagai arsip, tetapi tidak lagi dihitung sebagai lulus atau pembuka materi. Peserta wajib mengerjakan ulang.',
        ar: 'سيتم حذف المحاولات والإجابات والدرجات ونتائج التعلم في النظام الحالي ثم إعادة حساب تقدم البرنامج. ستبقى سجلات الدرجات القديمة محفوظة كأرشيف، لكنها لن تُحتسب بعد الآن كنجاح أو لفتح المادة. يجب على المشارك إعادة الاختبار.',
      },
      reasonLabel: { id: 'Alasan reset', ar: 'سبب إعادة التعيين' },
      reasonPlaceholder: {
        id: 'Contoh: kendala teknis saat ujian berlangsung',
        ar: 'مثال: مشكلة تقنية أثناء الاختبار',
      },
      confirmationLabel: {
        id: 'Ketik RESET untuk mengonfirmasi',
        ar: 'اكتب RESET للتأكيد',
      },
      confirmReset: { id: 'Reset sekarang', ar: 'إعادة التعيين الآن' },
      resetValidation: {
        id: 'Isi alasan minimal 5 karakter dan ketik RESET untuk melanjutkan.',
        ar: 'أدخل سببًا من 5 أحرف على الأقل واكتب RESET للمتابعة.',
      },
      resetSuccessTitle: { id: 'Ujian berhasil di-reset', ar: 'تمت إعادة الاختبار' },
      resetSuccessMessage: {
        id: '{count} attempt dihapus. {legacy} riwayat nilai lama diarsipkan dan tidak lagi dihitung sebagai kelulusan.',
        ar: 'تم حذف {count} محاولة. تم أرشفة {legacy} سجل قديم ولم تعد تُحتسب كنجاح.',
      },
      resetError: {
        id: 'Reset belum dapat diproses. Muat ulang data lalu coba kembali.',
        ar: 'تعذر تنفيذ إعادة التعيين. حدّث البيانات وحاول مرة أخرى.',
      },
    },
    labelGender: { id: 'Jenis Kelamin', ar: 'الجنس' },
    genderIkhwan: { id: 'Ikhwan (Laki-laki)', ar: 'إخوان (ذكر)' },
    genderAkhwat: { id: 'Akhwat (Perempuan)', ar: 'أخوات (أنثى)' },
    labelDomisili: { id: 'Kota / Kabupaten Domisili', ar: 'مدينة / منطقة الإقامة' },
    btnContactWA: { id: 'Hubungi WhatsApp', ar: 'تواصل عبر واتساب' },
    btnContactEmail: { id: 'Kirim Email', ar: 'إرسال بريد' },
    passwordReset: {
      eyebrow: { id: 'Keamanan akun', ar: 'أمان الحساب' },
      quickAction: { id: 'Reset kata sandi', ar: 'إعادة تعيين كلمة المرور' },
      title: { id: 'Kirim tautan reset kata sandi', ar: 'إرسال رابط إعادة تعيين كلمة المرور' },
      description: {
        id: 'Kirimkan tautan satu kali pakai agar peserta dapat membuat kata sandi baru secara mandiri.',
        ar: 'أرسل رابطاً للاستخدام مرة واحدة حتى يتمكن المشارك من إنشاء كلمة مرور جديدة بنفسه.',
      },
      recipientLabel: { id: 'Penerima tautan reset', ar: 'مستلم رابط إعادة التعيين' },
      securityNotice: {
        id: 'Kata sandi lama tidak dapat dilihat oleh admin. Peserta membuat kata sandi baru melalui tautan aman, dan setiap permintaan dicatat pada audit log.',
        ar: 'لا يمكن للمشرف رؤية كلمة المرور السابقة. ينشئ المشارك كلمة مرور جديدة عبر رابط آمن، ويتم تسجيل كل طلب في سجل التدقيق.',
      },
      sendAction: { id: 'Kirim tautan reset', ar: 'إرسال رابط إعادة التعيين' },
      sendingAction: { id: 'Mengirim tautan...', ar: 'جارٍ إرسال الرابط...' },
      successTitle: { id: 'Tautan reset telah dikirim', ar: 'تم إرسال رابط إعادة التعيين' },
      successMessage: {
        id: 'Peserta akan menerima tautan reset satu kali pakai di {email}.',
        ar: 'سيتلقى المشارك رابط إعادة تعيين للاستخدام مرة واحدة على {email}.',
      },
      shareViaWhatsApp: { id: 'Siapkan pesan WhatsApp', ar: 'إعداد رسالة واتساب' },
      shareViaEmail: { id: 'Siapkan email pengingat', ar: 'إعداد بريد تذكيري' },
      errorMessage: {
        id: 'Tautan reset belum dapat dikirim. Coba kembali.',
        ar: 'تعذر إرسال رابط إعادة التعيين. حاول مرة أخرى.',
      },
      closeLabel: { id: 'Tutup dialog reset kata sandi', ar: 'إغلاق حوار إعادة تعيين كلمة المرور' },
    },
    passwordRecovery: {
      eyebrow: { id: 'Pemulihan terverifikasi', ar: 'استعادة موثقة' },
      quickAction: { id: 'Atur password sementara', ar: 'تعيين كلمة مرور مؤقتة' },
      title: { id: 'Atur password sementara peserta', ar: 'تعيين كلمة مرور مؤقتة للمشارك' },
      description: {
        id: 'Gunakan hanya setelah memverifikasi identitas peserta. Password lama tidak dapat dilihat dan akan langsung diganti.',
        ar: 'استخدمه فقط بعد التحقق من هوية المشارك. لا يمكن رؤية كلمة المرور السابقة وسيتم استبدالها فوراً.',
      },
      passwordLabel: { id: 'Password sementara baru', ar: 'كلمة المرور المؤقتة الجديدة' },
      confirmationLabel: { id: 'Konfirmasi password sementara', ar: 'تأكيد كلمة المرور المؤقتة' },
      minimumLength: {
        id: 'Password minimal 8 karakter.',
        ar: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.',
      },
      confirmationMismatch: {
        id: 'Konfirmasi password belum sama.',
        ar: 'تأكيد كلمة المرور غير متطابق.',
      },
      securityNotice: {
        id: 'Password disimpan sebagai hash dan tidak dicatat di audit log. Bagikan hanya melalui kanal peserta yang telah diverifikasi.',
        ar: 'يتم حفظ كلمة المرور كقيمة مشفرة ولا تسجل في سجل المراجعة. شاركها فقط عبر قناة المشارك الموثقة.',
      },
      saveAction: { id: 'Simpan password sementara', ar: 'حفظ كلمة المرور المؤقتة' },
      savingAction: { id: 'Menyimpan password...', ar: 'جارٍ حفظ كلمة المرور...' },
      successTitle: {
        id: 'Password sementara sudah diperbarui',
        ar: 'تم تحديث كلمة المرور المؤقتة',
      },
      successDescription: {
        id: 'Bagikan password ini hanya kepada {name} melalui kanal yang telah diverifikasi.',
        ar: 'شارك كلمة المرور هذه فقط مع {name} عبر القناة التي تم التحقق منها.',
      },
      copyAction: { id: 'Salin password', ar: 'نسخ كلمة المرور' },
      errorMessage: {
        id: 'Password belum dapat diperbarui. Coba kembali.',
        ar: 'تعذر تحديث كلمة المرور. حاول مرة أخرى.',
      },
      closeLabel: { id: 'Tutup dialog password sementara', ar: 'إغلاق حوار كلمة المرور المؤقتة' },
    },
    btnEditDomisili: { id: 'Ubah Domisili', ar: 'تعديل الإقامة' },
    btnSaveDomisili: { id: 'Simpan Domisili', ar: 'حفظ الإقامة' },
    nipCodeLabel: { id: 'NIP / Kode Peserta', ar: 'الرقم الأكاديمي للمشارك' },
    programCohortLabel: { id: 'Angkatan', ar: 'الدفعة' },
    enrolledDateLabel: { id: 'Mulai Belajar', ar: 'تاريخ البدء' },
    registeredDateLabel: { id: 'Terdaftar', ar: 'تاريخ التسجيل' },
    progressLabel: { id: 'Kemajuan Belajar', ar: 'تقدم التعلم' },
    statusEnrolled: { id: 'Sedang Belajar', ar: 'قيد الدراسة' },
    statusCompleted: { id: 'Lulus / Selesai', ar: 'مكتمل' },
    statusPending: { id: 'Menunggu Persetujuan', ar: 'قيد الانتظار' },
    statusCancelled: { id: 'Dibatalkan', ar: 'ملغي' },
    toastDomisiliUpdated: {
      id: 'Kota domisili peserta berhasil diperbarui!',
      ar: 'تم تحديث مدينة إقامة المشارك بنجاح!',
    },
    tabProfile: { id: 'Identitas & Profil', ar: 'الهوية والملف' },
    tabPrograms: { id: 'Program & Evaluasi', ar: 'البرامج والتقييمات' },
    tabActivities: { id: 'Log Aktivitas', ar: 'سجل الأنشطة' },
    tableHeaderContactDomicile: { id: 'Kontak & Domisili', ar: 'الاتصال والإقامة' },
    quickViewDetail: { id: 'Lihat Detail', ar: 'عرض التفاصيل' },
    btnCopyData: { id: 'Salin', ar: 'نسخ' },
    toastCopied: { id: 'Teks disalin ke clipboard', ar: 'تم نسخ النص إلى الحافظة' },
    totalUsersCount: { id: 'Total Pengguna', ar: 'إجمالي المستخدمين' },
    loginReadinessTitle: { id: 'Kesiapan Login Peserta', ar: 'جاهزية دخول المشاركين' },
    loginReadinessDescription: {
      id: 'Audit otomatis untuk hambatan struktural login. Tidak membaca atau menampilkan kata sandi.',
      ar: 'تدقيق تلقائي لعوائق تسجيل الدخول الهيكلية. لا يقرأ أو يعرض كلمات المرور.',
    },
    loginReadinessActive: { id: 'Peserta aktif', ar: 'المشاركون النشطون' },
    loginReadinessMissingPassword: { id: 'Tanpa password', ar: 'بدون كلمة مرور' },
    loginReadinessUnsupportedHash: { id: 'Hash tidak didukung', ar: 'تجزئة غير مدعومة' },
    loginReadinessMissingIdentifier: { id: 'Tanpa identitas login', ar: 'بدون معرّف دخول' },
    loginReadinessHealthy: {
      id: 'Tidak ada hambatan struktural terdeteksi.',
      ar: 'لم يتم اكتشاف عوائق هيكلية.',
    },
    loginReadinessReview: {
      id: 'Perlu ditinjau. Cari peserta terkait lalu atur password sementara setelah verifikasi identitas.',
      ar: 'تتطلب المراجعة. ابحث عن المشارك ثم عيّن كلمة مرور مؤقتة بعد التحقق من الهوية.',
    },
    loginReadinessRefresh: { id: 'Perbarui audit', ar: 'تحديث التدقيق' },
  },
  reporting: {
    listTitle: { id: 'Laporan & Statistik', ar: 'التقارير والإحصاءات' },
    listSubtitle: {
      id: 'Sintesis dan ekstrak metrik kinerja akademik dan data kemajuan peserta.',
      ar: 'تجميع واستخراج مقاييس الأداء الأكاديمي وبيانات تقدم المشاركين.',
    },
    loadingPrograms: { id: 'Mengakses data program...', ar: 'جاري تحميل بيانات البرامج...' },
    selectProgram: { id: 'Pilih Program Belajar', ar: 'اختر البرنامج الدراسي' },
    btnExport: { id: 'Unduh Laporan (CSV)', ar: 'تصدير التقرير (CSV)' },
    awaitingTitle: { id: 'Menunggu Pemilihan Program', ar: 'في انتظار اختيار البرنامج' },
    awaitingDesc: {
      id: 'Pilih program studi di atas untuk memuat laporan kemajuan belajar dan transkrip akademik.',
      ar: 'اختر البرنامج الدراسي أعلاه لتحميل تقرير تقدم التعلم وكشف الدرجات الأكاديمي.',
    },
    synthesizing: {
      id: 'Menyintesis data progres dan nilai...',
      ar: 'جاري تجميع بيانات التقدم والدرجات...',
    },
    kpiActiveNodes: { id: 'Peserta Aktif', ar: 'المشاركون النشطون' },
    kpiActiveNodesSub: { id: 'Sedang menempuh materi', ar: 'يدرسون المواد حالياً' },
    kpiConvergence: { id: 'Tingkat Kelulusan', ar: 'معدل التخرج' },
    kpiConvergenceSub: { id: 'Persentase peserta lulus program', ar: 'نسبة إكمال وتخرج المشاركين' },
    kpiIntegrity: { id: 'Nilai Rata-rata Kohort', ar: 'متوسط درجات الدفعة' },
    kpiIntegritySub: { id: 'Rata-rata evaluasi & ujian', ar: 'متوسط أداء التقييمات' },
    trajectoryTitle: { id: 'Matriks Kemajuan & Nilai Siswa', ar: 'مصفوفة تقدم ودرجات الطلاب' },
    tableHeaderEntity: { id: 'Identitas Peserta', ar: 'هوية المشارك' },
    tableHeaderContact: { id: 'Kontak', ar: 'جهة الاتصال' },
    tableHeaderState: { id: 'Status', ar: 'الحالة' },
    tableHeaderProgress: { id: 'Progres Materi', ar: 'تقدم المواد' },
    tableHeaderAverageScore: { id: 'Nilai Rata-rata', ar: 'متوسط الدرجات' },
    tableHeaderLastPulse: { id: 'Aktivitas Terakhir', ar: 'آخر نشاط' },
    tableHeaderAction: { id: 'Aksi Transkrip', ar: 'الإجراءات' },
    emptyMatrix: {
      id: 'Belum ada peserta terdaftar dalam program ini.',
      ar: 'لا يوجد مشاركون مسجلون في هذا البرنامج بعد.',
    },
    tabProgress: { id: 'Laporan Progres Belajar', ar: 'تقرير تقدم التعلم' },
    tabTranscripts: { id: 'Laporan Kelulusan & Transkrip', ar: 'تقرير التخرج وكشف الدرجات' },
    distributionTitle: { id: 'Distribusi Kemajuan Belajar Kohort', ar: 'توزيع تقدم التعلم للدفعة' },
    tier0_25: { id: 'Awal (0 - 25%)', ar: 'المرحلة الأولى (0 - 25%)' },
    tier26_50: { id: 'Menengah Awal (26 - 50%)', ar: 'المرحلة المتوسطة الأولى (26 - 50%)' },
    tier51_75: { id: 'Menengah Akhir (51 - 75%)', ar: 'المرحلة المتوسطة الثانية (51 - 75%)' },
    tier76_100: { id: 'Mendekati Lulus (76 - 100%)', ar: 'قريب من التخرج (76 - 100%)' },
    stageBottleneckTitle: { id: 'Struktur Tahapan & Ujian', ar: 'هيكل المراحل والامتحانات' },
    viewTranscriptModal: { id: 'Rapor / Transkrip Resmi', ar: 'كشف الدرجات الرسمي' },
    searchParticipant: {
      id: 'Cari nama, email, atau NIP...',
      ar: 'البحث بالاسم أو البريد أو الرقم...',
    },
    filterAll: { id: 'Semua Status', ar: 'جميع الحالات' },
    filterCompleted: { id: 'Selesai / Lulus', ar: 'مكتمل / ناجح' },
    filterActive: { id: 'Sedang Aktif', ar: 'نشط حالياً' },
    cumulativeGrade: { id: 'Nilai Kumulatif', ar: 'المعدل التراكمي' },
    printModalTitle: {
      id: 'Pratinjau Rapor & Transkrip Akademik',
      ar: 'معاينة كشف الدرجات الأكاديمي',
    },
    printTranscriptBtn: { id: 'Cetak Dokumen Resmi', ar: 'طباعة الوثيقة الرسمية' },
    overviewTitle: { id: 'Ringkasan Intelijen Global', ar: 'الملخص الاستخباراتي الشامل' },
    overviewSubtitle: {
      id: 'Statistik ekosistem pembelajaran lintas seluruh program aktif.',
      ar: 'إحصائيات المنظومة التعليمية عبر جميع البرامج النشطة.',
    },
    globalActiveStudents: { id: 'Total Siswa Aktif', ar: 'إجمالي الطلاب النشطين' },
    globalMaterials: { id: 'Materi Pembelajaran Aktif', ar: 'المواد التعليمية النشطة' },
    globalCompletion: { id: 'Rata-rata Penyelesaian', ar: 'معدل الإكمال العام' },
    globalPendingRegistrations: { id: 'Pendaftaran Menunggu', ar: 'طلبات التسجيل المعلقة' },
    topProgramsTitle: { id: 'Performa Komparatif Program', ar: 'الأداء المقارن للبرامج' },
    recentActivityTitle: { id: 'Aktivitas Belajar Terkini', ar: 'أحدث الأنشطة التعليمية' },
    btnSelectProgramPrompt: {
      id: 'Pilih Program di atas untuk melihat Laporan Kohort & Transkrip Siswa Lengkap',
      ar: 'اختر برنامجاً من الأعلى لعرض تقرير الدفعة وكشوف الدرجات التفصيلية',
    },
    rowsPerPage: { id: 'Baris per halaman:', ar: 'الصفوف لكل صفحة:' },
    showingRows: {
      id: 'Menampilkan {from} - {to} dari {total} peserta',
      ar: 'عرض {from} - {to} من {total} مشارك',
    },
    btnPrevious: { id: 'Sebelumnya', ar: 'السابق' },
    btnNext: { id: 'Berikutnya', ar: 'التالي' },
  },
  reviewQueue: {
    listTitle: { id: 'Antrean Verifikasi Berkas', ar: 'طابور مراجعة الملفات' },
    listSubtitle: {
      id: 'Daftar dokumen persyaratan pendaftaran peserta yang perlu diperiksa manual.',
      ar: 'قائمة مستندات طلبات تسجيل المشاركين التي تحتاج إلى مراجعة يدوية.',
    },
    searchPlaceholder: { id: 'Cari peserta...', ar: 'البحث عن مشارك...' },
    searchAriaLabel: { id: 'Cari peserta', ar: 'البحث عن مشارك' },
    pendingReview: { id: 'Menunggu Review', ar: 'قيد المراجعة' },
    submittedAgo: { id: 'Dikirim 2 jam lalu', ar: 'أُرسلت قبل ساعتين' },
    courseReflection: { id: 'Refleksi Materi', ar: 'تأمل المادة' },
    queueCleared: {
      id: 'Antrean kosong. Semua refleksi telah diproses.',
      ar: 'الطابور فارغ. تمت معالجة جميع التأملات.',
    },
    inquiryPrompt: { id: 'Pertanyaan Refleksi', ar: 'سؤال التأمل' },
    analyticalReflection: { id: 'Refleksi Analitis', ar: 'تأمل تحليلي' },
    scholarCandidate: { id: 'Peserta Belajar', ar: 'الطالب المتقدم' },
    lexicalVolume: { id: 'Jumlah Kata', ar: 'عدد الكلمات' },
    instructorFeedback: { id: 'Umpan Balik Pengajar', ar: 'ملاحظات المعلم العلمية' },
    feedbackPlaceholder: {
      id: 'Berikan masukan dan kritik membangun...',
      ar: 'قدّم نصيحة ونقدًا بنّاءً...',
    },
    btnRequestRevision: { id: 'Minta Revisi', ar: 'طلب مراجعة' },
    btnArchiveDraft: { id: 'Arsipkan Draf', ar: 'أرشفة المسودة' },
    btnEndorseReflection: { id: 'Setujui Refleksi', ar: 'اعتماد التأمل' },
    btnBackToList: { id: 'Kembali ke Daftar', ar: 'العودة إلى القائمة' },
    historicalPedagogy: { id: 'Riwayat Pedagogis', ar: 'السجل التربوي' },
    historicalNote: {
      id: 'Peserta menunjukkan kedalaman analitis yang konsisten di 4 modul sebelumnya. Direkomendasikan oleh Sistem.',
      ar: 'يُظهر الطالب عمقًا تحليليًا متسقًا عبر 4 وحدات سابقة. اعتمده مهندس النظام.',
    },
    selectAssignment: { id: 'Pilih Tugas', ar: 'اختر المهمة' },
    selectAssignmentDesc: {
      id: 'Pilih refleksi yang menunggu peninjauan dari antrean untuk memulai proses review.',
      ar: 'اختر تأملًا قيد الانتظار من الطابور لبدء عملية المراجعة العلمية.',
    },
    defaultEndorseFeedback: {
      id: 'Pemahaman luar biasa terhadap konsep inti.',
      ar: 'فهم استثنائي للمفاهيم الأساسية.',
    },
  },
  programDetail: {
    loadingFramework: {
      id: 'Menyelaraskan kerangka operasional...',
      ar: 'جاري مزامنة الإطار التشغيلي...',
    },
    notFoundTitle: { id: 'Matriks Program Tidak Tersedia', ar: 'مصفوفة البرنامج غير متاحة' },
    notFoundDesc: {
      id: 'Berkas jalur pembelajaran yang diminta tidak dapat diambil dari server arsitek utama.',
      ar: 'تعذّر استرداد ملف المسار التعليمي المطلوب من الخادم المعماري الرئيسي.',
    },
    btnReturnPortfolio: { id: 'Kembali ke Portofolio', ar: 'العودة إلى المحفظة' },
    breadcrumbPortfolio: { id: 'Portofolio', ar: 'المحفظة' },
    breadcrumbAcademic: { id: 'Akademik', ar: 'الأكاديمي' },
    headerEyebrow: { id: 'Berkas Jalur Pembelajaran', ar: 'ملف المسار التعليمي' },
    defaultDescription: {
      id: 'Mengkalibrasi kerangka pembelajaran transformatif.',
      ar: 'معايرة أُطر التعلم التحويلي.',
    },
    btnEditDossier: { id: 'Edit Berkas', ar: 'تعديل الملف' },
    btnDuplicateProgram: { id: 'Duplikat Program', ar: 'نسخ البرنامج' },
    btnExecuteSync: { id: 'Jalankan Sinkronisasi', ar: 'تنفيذ المزامنة' },
    notifProgramDuplicatedTitle: { id: 'Program Diduplikasi', ar: 'تم نسخ البرنامج' },
    notifProgramDuplicatedMessage: {
      id: 'Salinan program siap ditinjau dalam status draf.',
      ar: 'نسخة البرنامج جاهزة للمراجعة بحالة المسودة.',
    },
    syncFeatureName: { id: 'Sinkronisasi', ar: 'المزامنة' },
    tabCurriculum: { id: 'Alur Pembelajaran', ar: 'المسار التعليمي' },
    tabEvaluations: { id: 'Evaluasi', ar: 'التقييمات' },
    tabWhatsApp: { id: 'Form Pendaftaran & Tautan Grup', ar: 'استمارة التسجيل وروابط المجموعات' },
    tabOverview: { id: 'Peserta & Ringkasan', ar: 'المشاركون والملخص' },
    tabParticipantsActive: { id: 'Terdaftar (Active)', ar: 'مسجل (نشط)' },
    tabParticipantsPending: { id: 'Menunggu Persetujuan', ar: 'قيد الانتظار' },
    tabParticipantsOther: { id: 'Lainnya (Ditolak/Revisi)', ar: 'أخرى (مرفوض/مراجعة)' },
    btnManualEnroll: { id: 'Tambah Peserta Manual', ar: 'إضافة مشارك يدوياً' },
    modalEnrollTitle: { id: 'Pendaftaran Manual Peserta', ar: 'تسجيل يدوي للمشاركين' },
    modalEnrollDesc: {
      id: 'Cari dan pilih peserta yang sudah ada di sistem untuk didaftarkan secara paksa ke dalam program ini.',
      ar: 'ابحث واختر المشاركين المسجلين بالفعل في النظام لإضافتهم يدوياً في هذا البرنامج.',
    },
    searchUserPlaceholder: {
      id: 'Cari berdasarkan nama atau email...',
      ar: 'البحث عن طريق الاسم أو البريد الإلكتروني...',
    },
    enrollSuccess: { id: 'Berhasil mendaftarkan peserta.', ar: 'تم تسجيل المشاركين بنجاح.' },
    btnEnrollSelected: { id: 'Daftarkan Terpilih', ar: 'تسجيل المحددين' },
    noUsersFound: { id: 'Pengguna tidak ditemukan', ar: 'لم يتم العثور على مستخدمين' },
    tabMaterials: { id: 'Pustaka Aset', ar: 'مكتبة الأصول' },
    flowCalibration: { id: 'Kalibrasi Alur', ar: 'معايرة المسار' },
    flowCalibrationDesc: {
      id: 'Rancang urutan pembelajaran melalui pengaturan fase yang modular.',
      ar: 'صمّم التسلسل التعليمي من خلال هيكلة المراحل المعيارية.',
    },
    btnAddPhase: { id: 'Tambah Fase', ar: 'إضافة مرحلة' },
    phaseLabel: { id: 'Fase', ar: 'المرحلة' },
    btnInjectAsset: { id: 'Tambah Aset Baru', ar: 'إضافة أصل جديد' },
    btnImportAsset: { id: 'Impor Aset', ar: 'استيراد أصل' },
    btnScheduleWeekly: { id: 'Jadwalkan Evaluasi Mingguan', ar: 'جدولة التقييم الأسبوعي' },
    weeklyAssessments: { id: 'Evaluasi Mingguan', ar: 'التقييمات الأسبوعية' },
    weeklyLabel: { id: 'Mingguan', ar: 'أسبوعي' },
    flowAssessment: { id: 'Evaluasi Alur', ar: 'تقييم المسار' },
    btnInitializeSubFlow: { id: 'Inisialisasi Sub-Alur', ar: 'تهيئة المسار الفرعي' },
    btnScheduleMonthly: {
      id: 'Jadwalkan Evaluasi Bulanan / Tengah Semester',
      ar: 'جدولة التقييم الشهري / النصفي',
    },
    phaseAssessments: { id: 'Evaluasi Fase', ar: 'تقييمات المرحلة' },
    institutionalImpact: { id: 'Dampak Institusional', ar: 'الأثر المؤسسي' },
    knowledgeVelocity: { id: 'Kecepatan Penguasaan', ar: 'سرعة المعرفة' },
    optimizationLoop: { id: 'Optimasi Berkelanjutan', ar: 'حلقة التحسين' },
    optimizationDesc: {
      id: 'Peserta menunjukkan ritme belajar 15% lebih lambat di Fase 01. Pertimbangkan redistribusi materi dasar.',
      ar: 'يُظهر المشاركون إيقاعًا أبطأ بنسبة 15% في المرحلة 01. يُستحسن إعادة توزيع المواد الأساسية.',
    },
    btnExecuteRefinement: { id: 'Jalankan Penyempurnaan', ar: 'تنفيذ التحسين' },
    communicationChannels: { id: 'Saluran Komunikasi', ar: 'قنوات التواصل' },
    communicationChannelsDesc: {
      id: 'Kelola pemetaan grup WhatsApp untuk peserta program.',
      ar: 'إدارة تخصيصات مجموعات الواتساب لمشاركي البرنامج.',
    },
    btnNewChannel: { id: 'Saluran Baru', ar: 'قناة جديدة' },
    noInviteLink: { id: 'Tautan undangan belum diatur', ar: 'لم يتم ضبط رابط الدعوة' },
    maxMembersSuffix: { id: 'maks. anggota', ar: 'الحد الأقصى للأعضاء' },
    ageLabel: { id: 'Usia:', ar: 'العمر:' },
    btnInspect: { id: 'Lihat', ar: 'فحص' },
    btnEditSpec: { id: 'Edit Spek', ar: 'تعديل المواصفات' },
    noChannels: {
      id: 'Belum ada saluran komunikasi yang dikalibrasi untuk program ini.',
      ar: 'لم يتم معايرة أي قنوات تواصل لهذا البرنامج بعد.',
    },
    operationalConfig: { id: 'Konfigurasi Operasional', ar: 'الإعداد التشغيلي' },
    metaEntityKey: { id: 'Kode Entitas', ar: 'مفتاح الكيان' },
    metaAccessControl: { id: 'Kontrol Akses', ar: 'التحكم في الوصول' },
    metaRegStatus: { id: 'Status Pendaftaran', ar: 'حالة التسجيل' },
    metaCommencement: { id: 'Mulai', ar: 'البدء' },
    metaProjection: { id: 'Proyeksi Selesai', ar: 'تاريخ الانتهاء' },
    statusOpen: { id: 'TERBUKA (Aktif)', ar: 'مفتوح (نشط)' },
    statusClosed: { id: 'TUTUP (Nonaktif)', ar: 'مغلق (غير نشط)' },
    participantBase: { id: 'Total Peserta', ar: 'قاعدة المشاركين' },
    participantBaseDesc: {
      id: 'Pendaftaran aktif yang tersinkronisasi di seluruh batch terkalibrasi.',
      ar: 'التسجيلات النشطة المتزامنة عبر جميع الدفعات المعتمدة.',
    },
    assetRepository: { id: 'Repositori Aset', ar: 'مستودع الأصول' },
    assetRepositoryDesc: {
      id: 'Manajemen terpusat untuk seluruh media pembelajaran dan node penilaian.',
      ar: 'إدارة مركزية لجميع الوسائط التعليمية وعقد التقييم.',
    },
    filterRepository: { id: 'Filter repositori...', ar: 'تصفية المستودع...' },
    activeAsset: { id: 'Aset Aktif', ar: 'أصل نشط' },
    repositoryEmpty: {
      id: 'Repositori aset masih kosong. Sinkronkan dengan alur pembelajaran untuk mengisinya.',
      ar: 'مستودع الأصول فارغ حاليًا. زامِن مع المسار التعليمي للتعبئة.',
    },
    assessmentMatrix: { id: 'Matriks Evaluasi', ar: 'مصفوفة التقييم' },
    assessmentMatrixDesc: {
      id: 'Rancang node validasi dan ambang kelulusan untuk kurikulum ini.',
      ar: 'صمّم عُقد التحقق وعتبات النجاح لهذا المنهج.',
    },
    btnNewValidationNode: { id: 'Node Validasi Baru', ar: 'عقدة تحقق جديدة' },
    headerEvaluationNode: { id: 'Node Evaluasi', ar: 'عقدة التقييم' },
    headerContext: { id: 'Konteks', ar: 'السياق' },
    headerAttemptLimit: { id: 'Batas Percobaan', ar: 'حد المحاولات' },
    headerBenchmarking: { id: 'Tolok Ukur', ar: 'المعيار' },
    contextProgram: { id: 'Program', ar: 'البرنامج' },
    contextPhase: { id: 'Fase', ar: 'المرحلة' },
    contextFlow: { id: 'Alur', ar: 'المسار' },
    contextMaterial: { id: 'Materi', ar: 'المادة' },
    programLevelEval: { id: 'Evaluasi Tingkat Program', ar: 'تقييم على مستوى البرنامج' },
    attemptsAllowed: { id: 'kali diizinkan', ar: 'محاولة مسموحة' },
    avgSuffix: { id: '--% Rata-rata', ar: '--% المتوسط' },
    noValidationNodes: {
      id: 'Belum ada node validasi yang dikalibrasi untuk jalur pembelajaran ini.',
      ar: 'لم يتم معايرة أي عُقد تحقق لهذا المسار التعليمي.',
    },
    importTitle: { id: 'Impor Aset Pembelajaran', ar: 'استيراد أصل تعليمي' },
    importDesc: {
      id: 'Klon aset pembelajaran dari alur program manapun ke sub-alur target yang dipilih.',
      ar: 'استنسخ أصلًا تعليميًا من أي مسار برنامج إلى المسار الفرعي المستهدف.',
    },
    importLabelSourceProgram: { id: 'Program Sumber', ar: 'البرنامج المصدر' },
    importPlaceholderProgram: { id: '-- Pilih berkas Program --', ar: '-- اختر ملف البرنامج --' },
    importLabelSourceFlow: {
      id: 'Sub-Alur Pembelajaran Sumber',
      ar: 'المسار الفرعي التعليمي المصدر',
    },
    importPlaceholderFlow: { id: '-- Pilih Alur --', ar: '-- اختر المسار --' },
    importLabelSelectAsset: { id: 'Pilih Aset untuk Diklon', ar: 'اختر الأصل للاستنساخ' },
    importPlaceholderMaterial: { id: '-- Pilih Materi --', ar: '-- اختر المادة --' },
    importAssetsSuffix: { id: 'aset', ar: 'أصول' },
    importBlueprint: { id: 'Spesifikasi Aset', ar: 'مواصفات الأصل' },
    importDuration: { id: 'Estimasi Durasi Belajar', ar: 'مدة التعلم المقدّرة' },
    importMinutes: { id: 'Menit', ar: 'دقيقة' },
    importValidationMatrix: { id: 'Matriks Validasi', ar: 'مصفوفة التحقق' },
    importValidationYes: { id: 'Ya (Kuis Terlampir)', ar: 'نعم (اختبار مرفق)' },
    importValidationNone: { id: 'Tidak Ada', ar: 'لا يوجد' },
    btnCancel: { id: 'Batal', ar: 'إلغاء' },
    btnImportAssetSubmit: { id: 'Impor Aset', ar: 'استيراد الأصل' },
    badgeRequired: { id: 'Wajib', ar: 'مطلوب' },
    assetTypeSuffix: { id: 'Aset', ar: 'أصل' },
    minSuffix: { id: 'Menit', ar: 'دقيقة' },
    deleteAssetTitle: { id: 'Hapus Aset', ar: 'حذف الأصل' },
    injectedEvaluation: { id: 'Evaluasi Tersisip', ar: 'تقييم مُدرج' },
    confirmDeletePhase: {
      id: 'Apakah Anda yakin ingin menghapus fase ini? Semua alur di dalamnya akan terputus.',
      ar: 'هل أنت متأكد من إزالة هذه المرحلة؟ سيتم فصل جميع المسارات الموجودة بداخلها.',
    },
    confirmDeleteFlow: {
      id: 'Apakah Anda yakin ingin menghapus alur pembelajaran ini?',
      ar: 'هل أنت متأكد من إزالة هذا المسار التعليمي؟',
    },
    confirmDeleteMaterial: {
      id: 'Apakah Anda yakin ingin menghapus aset pembelajaran ini?',
      ar: 'هل أنت متأكد من إزالة هذا الأصل التعليمي؟',
    },
    notifSequenceCalibratedTitle: { id: 'Urutan Terkalibrasi', ar: 'تمت معايرة التسلسل' },
    notifSequenceCalibratedMsg: {
      id: 'Aset pembelajaran berhasil disusun ulang.',
      ar: 'تم إعادة ترتيب الأصول التعليمية بنجاح.',
    },
    notifReorderFailedTitle: { id: 'Gagal Menyusun Ulang', ar: 'فشل إعادة الترتيب' },
    notifReorderFailedMsg: {
      id: 'Sistem tidak dapat menyimpan urutan baru.',
      ar: 'لم يتمكن النظام من حفظ التسلسل الجديد.',
    },
    notifAssetDecommissionedTitle: { id: 'Aset Dihapus', ar: 'تم إيقاف الأصل' },
    notifAssetDecommissionedMsg: {
      id: 'Aset pembelajaran berhasil dihapus dari alur.',
      ar: 'تمت إزالة الأصل التعليمي من المسار بنجاح.',
    },
    notifDossierActionTitle: { id: 'Aksi Berkas', ar: 'إجراء على الملف' },
    notifDossierActionMsg: {
      id: 'protokol {feature} sedang dikalibrasi di arsitek utama.',
      ar: 'بروتوكول {feature} قيد المعايرة في المعمار الرئيسي.',
    },
    notifModularInjectionTitle: { id: 'Injeksi Modular', ar: 'حقن معياري' },
    notifModularInjectionMsg: {
      id: 'Pilih alur pembelajaran tertentu untuk menambahkan aset Anda.',
      ar: 'يرجى اختيار مسار تعليمي محدد لإدراج الأصل.',
    },
    notifAssetClonedTitle: { id: 'Aset Berhasil Diklon', ar: 'تم استنساخ الأصل بنجاح' },
    notifAssetClonedMsg: {
      id: 'Aset pembelajaran telah disalin secara mendalam ke sub-alur ini.',
      ar: 'تم استنساخ الأصل التعليمي بشكل كامل إلى هذا المسار الفرعي.',
    },
    notifCloneFailedTitle: { id: 'Klon Gagal', ar: 'فشل الاستنساخ' },
    notifCloneFailedFallback: {
      id: 'Terjadi kesalahan.',
      ar: 'حدث خطأ ما.',
    },
    statusActive: { id: 'Aktif', ar: 'نشط' },
    statusPending: { id: 'Menunggu (Pending)', ar: 'قيد الانتظار' },
  },
  programDuplicate: {
    eyebrow: { id: 'Salin Kurikulum', ar: 'نسخ المنهج' },
    title: { id: 'Duplikat Program', ar: 'نسخ البرنامج' },
    description: {
      id: 'Salin seluruh struktur level, tahapan, mata pelajaran, pertemuan, materi, evaluasi, dan bank soal ke program baru.',
      ar: 'انسخ هيكل المستويات والمراحل والمواد والاجتماعات والمحتوى والتقييمات وبنك الأسئلة بالكامل إلى برنامج جديد.',
    },
    exclusions: {
      id: 'Peserta, pendaftaran, grup, nilai, percobaan ujian, dan riwayat belajar tidak akan disalin. Program baru dibuat sebagai draf dengan pendaftaran tertutup.',
      ar: 'لن يتم نسخ المشاركين أو التسجيلات أو المجموعات أو الدرجات أو محاولات الاختبار أو سجل التعلم. يُنشأ البرنامج الجديد كمسودة مع إغلاق التسجيل.',
    },
    labelName: { id: 'Nama Program Baru', ar: 'اسم البرنامج الجديد' },
    labelCode: { id: 'Kode Program Baru', ar: 'رمز البرنامج الجديد' },
    cancel: { id: 'Batal', ar: 'إلغاء' },
    submit: { id: 'Duplikat Sekarang', ar: 'انسخ الآن' },
    errorFallback: { id: 'Program tidak dapat diduplikasi.', ar: 'تعذر نسخ البرنامج.' },
  },
  materials: {
    listTitle: { id: 'Kurikulum & Materi', ar: 'المناهج والمواد' },
    listSubtitle: {
      id: 'Atur struktur pembelajaran, bab, modul, dan aset materi pembelajaran.',
      ar: 'إدارة هيكل التعلم، الفصول، الوحدات، وأصول المواد التعليمية.',
    },
    btnAddMaterial: { id: 'Tambah Materi', ar: 'إضافة مادة' },
    searchPlaceholder: { id: 'Filter berdasarkan identitas...', ar: 'التصفية بالهوية...' },
    filterAll: { id: 'Semua', ar: 'الكل' },
    filterVideo: { id: 'VIDEO', ar: 'فيديو' },
    filterAudio: { id: 'AUDIO', ar: 'صوتي' },
    filterDocument: { id: 'DOKUMEN', ar: 'مستند' },
    filterQuiz: { id: 'EVALUASI', ar: 'تقييم' },
    btnRefineView: { id: 'Saring Tampilan', ar: 'تصفية العرض' },
    requiredBadge: { id: 'Wajib', ar: 'مطلوب' },
    enforcedWithin: { id: 'Diterapkan dalam:', ar: 'مطبق ضمن:' },
    unassigned: { id: 'Belum ditetapkan', ar: 'غير مخصص' },
    details: { id: 'Detail', ar: 'التفاصيل' },
    emptyTitle: { id: 'Repositori Terkalibrasi', ar: 'المستودع معاير' },
    emptyDesc: {
      id: 'Tidak ada aset terdeteksi dalam filter yang dipilih. Sesuaikan parameter Anda untuk melihat media yang tersinkronisasi.',
      ar: 'لم يتم العثور على أصول ضمن التصفية المحددة. عدّل المعلمات لعرض الوسائط المتزامنة.',
    },
    btnResetMatrix: { id: 'Atur Ulang Filter', ar: 'إعادة ضبط التصفية' },
    contextRequired: { id: 'Konteks Diperlukan', ar: 'السياق مطلوب' },
    contextMessage: {
      id: 'Untuk menambahkan aset baru, silakan navigasi ke tampilan Kurikulum Program tertentu dan pilih alur instruksional target.',
      ar: 'لإضافة أصول جديدة، يرجى الانتقال إلى عرض المنهج الخاص بالبرنامج واختيار المسار التعليمي المستهدف.',
    },
    tableHeaderTitle: { id: 'Judul Materi', ar: 'عنوان المادة' },
    tableHeaderType: { id: 'Tipe Konten', ar: 'نوع المحتوى' },
    tableHeaderProgram: { id: 'Program', ar: 'البرنامج' },
    workflowEyebrow: { id: 'Ruang kerja kurikulum', ar: 'مساحة عمل المنهج' },
    workflowTitle: {
      id: 'Atur materi dalam konteks kurikulum yang jelas.',
      ar: 'نظّم المواد ضمن سياق منهجي واضح.',
    },
    workflowDesc: {
      id: 'Mulai dari program, susun pertemuan, lalu kelola materi dan evaluasi yang menyertainya.',
      ar: 'ابدأ بالبرنامج، ونظّم اللقاءات، ثم أدر المواد والتقييمات المصاحبة.',
    },
    workflowProgram: { id: 'Pilih program', ar: 'اختر البرنامج' },
    workflowProgramDesc: {
      id: 'Tentukan tujuan pembelajaran dan struktur tingkatnya.',
      ar: 'حدّد هدف التعلم وبنية مستوياته.',
    },
    workflowCurriculum: { id: 'Susun kurikulum', ar: 'نظّم المنهج' },
    workflowCurriculumDesc: {
      id: 'Kelola mata pelajaran, pertemuan, dan urutan belajar.',
      ar: 'أدر المواد الدراسية واللقاءات وتسلسل التعلم.',
    },
    workflowMaterials: { id: 'Kelola materi', ar: 'أدر المواد' },
    workflowMaterialsDesc: {
      id: 'Tambahkan banyak materi dan evaluasi untuk setiap pertemuan.',
      ar: 'أضف مواد وتقييمات متعددة لكل لقاء.',
    },
    openPrograms: { id: 'Buka program', ar: 'افتح البرامج' },
    openReviewQueue: { id: 'Tinjau antrean', ar: 'راجع الطابور' },
    libraryEyebrow: { id: 'Repositori materi', ar: 'مستودع المواد' },
    libraryCount: { id: '{count} materi ditemukan', ar: 'تم العثور على {count} مادة' },
    libraryPage: { id: 'Halaman {page} dari {totalPages}', ar: 'الصفحة {page} من {totalPages}' },
    libraryUpdating: { id: 'Memperbarui daftar materi…', ar: 'جارٍ تحديث قائمة المواد…' },
    labelSearch: { id: 'Cari materi', ar: 'ابحث عن مادة' },
    searchHelper: {
      id: 'Cari berdasarkan judul atau ringkasan materi.',
      ar: 'ابحث حسب عنوان المادة أو ملخصها.',
    },
    filterLabel: { id: 'Jenis materi', ar: 'نوع المادة' },
    typeReflection: { id: 'REFLEKSI', ar: 'تأمل' },
    typeDiscussion: { id: 'DISKUSI', ar: 'نقاش' },
    sessionLabel: { id: 'Pertemuan', ar: 'اللقاء' },
    flowLabel: { id: 'Alur pembelajaran', ar: 'مسار التعلم' },
    noContext: { id: 'Materi belum terhubung ke kurikulum.', ar: 'المادة غير مرتبطة بعد بالمنهج.' },
    openMaterial: { id: 'Buka materi', ar: 'افتح المادة' },
    previousPage: { id: 'Halaman sebelumnya', ar: 'الصفحة السابقة' },
    nextPage: { id: 'Halaman berikutnya', ar: 'الصفحة التالية' },
    clearFilters: { id: 'Bersihkan filter', ar: 'مسح عوامل التصفية' },
    emptyFilteredTitle: { id: 'Belum ada materi yang sesuai.', ar: 'لا توجد مواد مطابقة بعد.' },
    emptyFilteredDesc: {
      id: 'Ubah kata kunci atau jenis materi untuk melihat aset lain.',
      ar: 'غيّر كلمة البحث أو نوع المادة لعرض أصول أخرى.',
    },
    emptyState: {
      id: 'Belum ada materi pembelajaran yang diunggah.',
      ar: 'لم يتم تحميل أي مواد تعليمية بعد.',
    },
  },
  settings: {
    listTitle: { id: 'Pengaturan Sistem', ar: 'إعدادات النظام' },
    listSubtitle: {
      id: 'Konfigurasi parameter sistem, feature flag, dan integrasi API.',
      ar: 'تكوين معلمات النظام، وميزات التشغيل، وتكامل واجهة البرmجة.',
    },
    brandingSection: { id: 'Identitas & Branding Portal', ar: 'هوية وشعار البوابة' },
    brandingSubtitle: {
      id: 'Kustomisasi nama portal (judul utama) dan sub-judul yang ditampilkan di header sidebar navigasi.',
      ar: 'تخصيص اسم البوابة (العنوان الرئيسي) والعنوان الفرعي المعروض في شريط التنقل الجانبي.',
    },
    brandTitleIdLabel: {
      id: 'Judul Utama Portal (Bahasa Indonesia)',
      ar: 'العنوان الرئيسي للبوابة (إندونيسي)',
    },
    brandTitleArLabel: {
      id: 'Judul Utama Portal (Bahasa Arab)',
      ar: 'العنوان الرئيسي للبوابة (عربي)',
    },
    brandSubIdLabel: {
      id: 'Sub-Judul / Tagline (Bahasa Indonesia)',
      ar: 'العنوان الفرعي / الشعار (إندونيسي)',
    },
    brandSubArLabel: {
      id: 'Sub-Judul / Tagline (Bahasa Arab)',
      ar: 'العنوان الفرعي / الشعار (عربي)',
    },
    brandingPreviewTitle: {
      id: 'Pratinjau Langsung Header Sidebar',
      ar: 'معاينة شريط التنقل الجانبي',
    },
    btnSaveBranding: { id: 'Simpan Identitas Portal', ar: 'حفظ إعدادات الهوية' },
    btnResetBranding: { id: 'Reset ke Default', ar: 'استعادة الإعدادات الافتراضية' },
    toastBrandingSaved: {
      id: 'Identitas portal berhasil diperbarui!',
      ar: 'تم تحديث هوية البوابة بنجاح!',
    },
    toastBrandingReset: {
      id: 'Identitas portal dikembalikan ke default.',
      ar: 'تمت استعادة إعدادات الهوية الافتراضية.',
    },
    systemArchitecture: { id: 'Arsitektur Sistem', ar: 'هندسة النظام' },
    controlCenter: { id: 'Pusat Kontrol', ar: 'مركز التحكم' },
    operationalPreview: { id: 'Pratinjau Operasional', ar: 'معاينة تشغيلية' },
    settingsDesc: {
      id: 'Konfigurasi perilaku operasional inti, buat kerangka kerja identitas, dan pantau modifikasi sistem tingkat eksekutif.',
      ar: 'تكوين السلوكيات التشغيلية الأساسية، وإنشاء أُطر الهوية، ومراقبة التعديلات على مستوى النظام.',
    },
    accessSilos: { id: 'Akses Berdasarkan Peran', ar: 'صلاحيات حسب الأدوار' },
    futureArchitecture: { id: 'Arsitektur Mendatang', ar: 'هندسة مستقبلية' },
    permissionDesc: {
      id: 'Matriks izin terperinci di seluruh domain fungsional.',
      ar: 'مصفوفة أذونات دقيقة عبر النطاقات الوظيفية.',
    },
    btnEditSchemes: { id: 'Edit Skema', ar: 'تعديل المخططات' },
    tableHeaderArea: { id: 'Area Fungsional', ar: 'المنطقة الوظيفية' },
    roleAdmin: { id: 'Admin', ar: 'مشرف' },
    roleFaculty: { id: 'Pengajar', ar: 'معلم' },
    roleAuditor: { id: 'Auditor', ar: 'مدقق' },
    areaUserLifecycle: { id: 'Manajemen Siklus Pengguna', ar: 'إدارة دورة حياة المستخدم' },
    areaFinancial: { id: 'Rekonsiliasi Keuangan', ar: 'التسوية المالية' },
    areaCurriculum: { id: 'Perancangan Kurikulum', ar: 'تصميم المناهج' },
    areaTerminology: { id: 'Override Terminologi Sistem', ar: 'تعديل مصطلحات النظام' },
    vocabulary: { id: 'Kosakata', ar: 'المصطلحات' },
    vocabDesc: {
      id: 'Sesuaikan penamaan entitas global sesuai budaya organisasi.',
      ar: 'أعد تعريف تسمية الكيانات العامة لتتناسب مع ثقافة المؤسسة.',
    },
    btnModifyStrings: { id: 'Ubah String Global', ar: 'تعديل النصوص العامة' },
    vocabFromStudent: { id: 'Siswa', ar: 'الطالب' },
    vocabToParticipant: { id: 'Peserta', ar: 'المشارك' },
    vocabFromTeacher: { id: 'Guru', ar: 'المعلم' },
    vocabToFacilitator: { id: 'Fasilitator', ar: 'الميسر' },
    automation: { id: 'Otomasi', ar: 'الأتمتة' },
    tplOnboarding: { id: 'Sambutan Orientasi', ar: 'ترحيب التوجيه' },
    tplOverdue: { id: 'Tugas Terlambat', ar: 'مهمة متأخرة' },
    statusActive: { id: 'Aktif', ar: 'نشط' },
    btnManageTemplates: { id: 'Kelola Template', ar: 'إدارة القوالب' },
    auditFeed: { id: 'Catatan Audit Eksekutif', ar: 'سجل التدقيق التنفيذي' },
    auditDesc: { id: 'Pencatatan real-time modifikasi sistem.', ar: 'تسجيل لحظي لتعديلات النظام.' },
    btnExportCSV: { id: 'Unduh CSV', ar: 'تصدير CSV' },
    liveStream: { id: 'Siaran Langsung', ar: 'بث مباشر' },
    tableHeaderTimestamp: { id: 'Waktu', ar: 'الوقـت' },
    tableHeaderInitiator: { id: 'Inisiator', ar: 'المبادر' },
    tableHeaderStatus: { id: 'Status', ar: 'الحالة' },
    tableHeaderDetails: { id: 'Detail Modifikasi', ar: 'تفاصيل التعديل' },
    emailCenterTitle: {
      id: 'Pusat Email & Notifikasi (Mailketing)',
      ar: 'مركز البريد الإلكتروني والإشعارات (Mailketing)',
    },
    emailCenterDesc: {
      id: 'Kirim email transaksional melalui Mailketing dari no-reply@yts.web.id, pantau status layanan, dan uji template email.',
      ar: 'إرسال البريد الإلكتروني المعاملاتي عبر Mailketing من no-reply@yts.web.id، ومتابعة حالة الخدمة، واختبار القوالب.',
    },
    mailStatusTitle: { id: 'Status Layanan Mailketing', ar: 'حالة خدمة Mailketing' },
    mailConnected: { id: 'Konfigurasi API Aktif', ar: 'إعداد API نشط' },
    mailError: { id: 'Layanan Belum Terhubung', ar: 'الخدمة غير متوفرة' },
    btnCheckMail: { id: 'Periksa Layanan Mailketing', ar: 'فحص خدمة Mailketing' },
    mailConnectedTitle: { id: 'Konfigurasi Mailketing Aktif', ar: 'إعداد Mailketing نشط' },
    mailDisconnectedTitle: { id: 'Mailketing Belum Terhubung', ar: 'Mailketing غير متصل' },
    mailCheckFailedTitle: { id: 'Pemeriksaan Mailketing Gagal', ar: 'فشل فحص Mailketing' },
    mailConnectedFallback: {
      id: 'Token dan konfigurasi Mailketing tersedia.',
      ar: 'رمز وإعداد Mailketing متاحان.',
    },
    mailStatusScope: {
      id: 'Status ini memeriksa konfigurasi API, bukan jaminan email masuk inbox. Periksa riwayat Mailketing untuk blacklist, bounce, atau status pengantaran penerima.',
      ar: 'تتحقق هذه الحالة من إعداد API وليست ضمانًا لوصول البريد إلى صندوق الوارد. راجع سجل Mailketing لمعرفة القائمة السوداء أو الارتداد أو حالة التسليم.',
    },
    mailDisconnectedFallback: {
      id: 'Layanan Mailketing belum dapat digunakan. Email verifikasi, pemulihan kata sandi, dan notifikasi tidak dapat dikirim.',
      ar: 'خدمة Mailketing غير متاحة. لا يمكن إرسال رسائل التحقق واستعادة كلمة المرور والإشعارات.',
    },
    mailProviderLabel: { id: 'Penyedia Layanan:', ar: 'مزود الخدمة:' },
    mailFromAddressLabel: { id: 'Alamat Pengirim:', ar: 'عنوان المرسل:' },
    mailEndpointLabel: { id: 'Endpoint API:', ar: 'نقطة نهاية API:' },
    mailAuthenticationLabel: { id: 'Autentikasi:', ar: 'المصادقة:' },
    mailAuthenticationValue: {
      id: 'X-Api-Token (tersimpan aman)',
      ar: 'X-Api-Token (محفوظ بأمان)',
    },
    btnSendTestEmail: { id: 'Kirim Email Uji Coba', ar: 'إرسال بريد تجريبي' },
    testRecipientLabel: { id: 'Email Penerima Uji Coba', ar: 'البريد الإلكتروني للمستلم التجريبي' },
    testTemplateLabel: { id: 'Pilih Template Email', ar: 'اختر قالب البريد' },
    tplResetPassword: { id: 'Pemulihan Kata Sandi (Password Reset)', ar: 'استعادة كلمة المرور' },
    tplNewMaterial: { id: 'Informasi Materi Baru Tersedia', ar: 'إشعار مادة جديدة متاحة' },
    tplPendingEvaluation: {
      id: 'Pengingat Ujian / Evaluasi Belum Tuntas',
      ar: 'تذكير بالاختبار غير المكتمل',
    },
    tplInactivityReminder: {
      id: 'Pengingat 2 Pekan Tidak Aktif Belajar',
      ar: 'تذكير بعدم النشاط لمدة أسبوعين',
    },
    tplRegistrationApproved: {
      id: 'Persetujuan Pendaftaran & Undangan WA',
      ar: 'الموافقة على التسجيل ودعوة واتساب',
    },
    tplRegistrationSubmitted: {
      id: 'Bukti Pendaftaran Diterima & Estimasi Review (SLA)',
      ar: 'إيصال استلام التسجيل والمدة المتوقعة',
    },
    testEmailSuccess: {
      id: 'Permintaan email diterima Mailketing',
      ar: 'تم استلام طلب البريد من Mailketing',
    },
    testEmailAcceptedReceipt: {
      id: 'Permintaan diterima provider — periksa status inbox',
      ar: 'تم استلام الطلب من المزود — تحقق من حالة صندوق الوارد',
    },
    testEmailFailedReceipt: { id: 'Permintaan pengiriman gagal', ar: 'فشل طلب الإرسال' },
    mailDisconnected: { id: 'Layanan Terputus / Tidak Terhubung', ar: 'الخدمة غير متصلة' },
    mailChecking: { id: 'Memeriksa Layanan Mailketing...', ar: 'جار فحص خدمة Mailketing...' },
    btnReconnect: { id: 'Hubungkan Ulang & Uji Layanan', ar: 'إعادة الاتصال وفحص الخدمة' },
    lastDeliveryReceipt: { id: 'Tanda Terima Pengiriman (Receipt)', ar: 'إيصال الإرسال' },
    messageIdLabel: { id: 'ID Pesan Mailketing', ar: 'معرف رسالة Mailketing' },
    serverResponseLabel: { id: 'Respon Server', ar: 'استجابة الخادم' },
  },
  resetPassword: {
    invalidTokenTitle: { id: 'Token Tidak Valid', ar: 'رمز غير صالح' },
    invalidTokenDesc: {
      id: 'Link reset password tidak lengkap atau tidak valid. Silakan minta link baru di halaman login.',
      ar: 'رابط إعادة تعيين كلمة المرور غير مكتمل أو غير صالح. يرجى طلب رابط جديد من صفحة تسجيل الدخول.',
    },
    backToLogin: { id: 'Kembali ke Login', ar: 'العودة إلى تسجيل الدخول' },
    tokenNotFound: {
      id: 'Token reset password tidak ditemukan.',
      ar: 'لم يتم العثور على رمز إعادة تعيين كلمة المرور.',
    },
    generalError: { id: 'Gagal mereset password.', ar: 'فشل إعادة تعيين كلمة المرور.' },
    successTitle: { id: 'Password Diperbarui', ar: 'تم تحديث كلمة المرور' },
    successDesc: {
      id: 'Password Anda telah berhasil diubah. Silakan login kembali dengan password baru Anda.',
      ar: 'تم تغيير كلمة المرور الخاصة بك بنجاح. يرجى تسجيل الدخول مرة أخرى باستخدام كلمة المرور الجديدة.',
    },
    title: { id: 'Buat Password Baru', ar: 'إنشاء كلمة مرور جديدة' },
    subtitle: {
      id: 'Masukkan password baru Anda untuk akun Tarbiyah Sunnah Learning.',
      ar: 'أدخل كلمة المرور الجديدة لحساب Tarbiyah Sunnah Learning الخاص بك.',
    },
    newPasswordLabel: { id: 'Password Baru', ar: 'كلمة المرور الجديدة' },
    newPasswordPlaceholder: { id: 'Minimal 6 karakter', ar: 'على الأقل 6 أحرف' },
    confirmPasswordLabel: { id: 'Konfirmasi Password Baru', ar: 'تأكيد كلمة المرور الجديدة' },
    confirmPasswordPlaceholder: { id: 'Ulangi password baru', ar: 'كرر كلمة المرور الجديدة' },
    submitBtn: { id: 'Simpan Password', ar: 'حفظ كلمة المرور' },
    validation: {
      passwordMin: {
        id: 'Password minimal 6 karakter',
        ar: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
      },
      confirmPasswordMin: {
        id: 'Konfirmasi password minimal 6 karakter',
        ar: 'تأكيد كلمة المرور يجب أن يكون 6 أحرف على الأقل',
      },
      passwordsDontMatch: { id: 'Password tidak cocok', ar: 'كلمة المرور غير متطابقة' },
    },
  },
  programModal: {
    title: { id: 'Kalibrasi Matriks Program', ar: 'معايرة مصفوفة البرنامج' },
    subtitle: {
      id: 'Perbarui parameter operasional mendasar',
      ar: 'تحديث المعلمات التشغيلية الأساسية',
    },
    labelName: { id: 'Nama Entitas', ar: 'اسم الكيان' },
    labelCode: { id: 'Kunci Entitas (Kode)', ar: 'مفتاح الكيان (الرمز)' },
    labelDesc: { id: 'Cakupan Operasional (Deskripsi)', ar: 'النطاق التشغيلي (الوصف)' },
    labelStatus: { id: 'Status Operasional', ar: 'الحالة التشغيلية' },
    statusDraft: { id: 'DRAFT (Pemeliharaan)', ar: 'مسودة (صيانة)' },
    statusPublished: { id: 'PUBLISHED (Entri Aktif)', ar: 'منشور (إدخال نشط)' },
    statusArchived: { id: 'ARCHIVED (Riwayat)', ar: 'مؤرشف (تاريخي)' },
    labelAdmission: { id: 'Protokol Penerimaan', ar: 'بروتوكول القبول' },
    admissionManual: { id: 'MANUAL (Penerimaan Terkurasi)', ar: 'يدوي (قبول منسق)' },
    admissionAuto: { id: 'AUTO (Entri Mengalir)', ar: 'تلقائي (دخول سلس)' },
    labelRegistrationOpen: { id: 'Aktivasi Pendaftaran', ar: 'تفعيل التسجيل' },
    registrationOpenDesc: {
      id: 'Kontrol visibilitas formulir pendaftaran publik',
      ar: 'التحكم في رؤية نموذج التسجيل العام',
    },
    labelStartDate: { id: 'Tanggal Mulai', ar: 'تاريخ البدء' },
    labelEndDate: { id: 'Tanggal Berakhir', ar: 'تاريخ الانتهاء' },
    labelRegistrationOpenDate: { id: 'Pendaftaran Dibuka', ar: 'فتح التسجيل' },
    labelRegistrationCloseDate: { id: 'Pendaftaran Ditutup', ar: 'إغلاق التسجيل' },
    labelCoverImage: { id: 'Cover / Banner Program', ar: 'صورة غلاف / لافتة البرنامج' },
    uploadCoverPrompt: {
      id: 'Pilih gambar untuk mengunggah cover program',
      ar: 'اختر صورة لتحميل غلاف البرنامج',
    },
    coverRecommendation: {
      id: 'JPG, PNG, WEBP, atau GIF · Maks. 50 MB · Rasio 16:9 disarankan',
      ar: 'JPG أو PNG أو WEBP أو GIF · الحد الأقصى 50 ميجابايت · يوصى بنسبة 16:9',
    },
    changeCover: { id: 'Ganti Gambar', ar: 'تغيير الصورة' },
    removeCover: { id: 'Hapus Gambar', ar: 'حذف الصورة' },
    uploadingCover: { id: 'Mengunggah gambar...', ar: 'جارٍ رفع الصورة...' },
    coverPreviewUnavailable: {
      id: 'Cover saat ini tidak dapat dimuat. Pilih gambar baru untuk menggantinya.',
      ar: 'تعذر تحميل الغلاف الحالي. اختر صورة جديدة لاستبداله.',
    },
    formNavigationLabel: { id: 'Navigasi formulir', ar: 'التنقل في النموذج' },
    sectionIdentity: { id: 'Identitas', ar: 'الهوية' },
    sectionAppearance: { id: 'Tampilan', ar: 'المظهر' },
    sectionAccess: { id: 'Akses', ar: 'الوصول' },
    sectionSchedule: { id: 'Jadwal', ar: 'الجدول الزمني' },
    btnCancel: { id: 'Batalkan Kalibrasi', ar: 'إلغاء المعايرة' },
    btnSubmit: { id: 'Jalankan Sinkronisasi', ar: 'تنفيذ المزامنة' },
  },
  receiptModal: {
    title: { id: 'Kuitansi Pendaftaran', ar: 'إيصال التسجيل' },
    subtitle: { id: 'Dokumentasi Resmi Pendaftaran', ar: 'وثائق التسجيل الرسمية' },
    generatedOn: { id: 'Dibuat Pada', ar: 'تاريخ الإنشاء' },
    participantIdentity: { id: 'Identitas Peserta', ar: 'هوية المشارك' },
    instructionalTrack: { id: 'Jalur Pembelajaran', ar: 'مسار التعليم' },
    code: { id: 'Kode', ar: 'الرمز' },
    registration: { id: 'Pendaftaran', ar: 'التسجيل' },
    authorization: { id: 'Otorisasi', ar: 'الاعتماد' },
    financialStatus: { id: 'Status Keuangan', ar: 'الحالة المالية' },
    verified: { id: 'TERVERIFIKASI', ar: 'تم التحقق' },
    pendingApproval: { id: 'MENUNGGU PERSETUJUAN', ar: 'في انتظار الموافقة' },
    freeNoRecord: { id: 'GRATIS / TIDAK ADA CATATAN', ar: 'مجاني / لا يوجد سجل' },
    officialParticipantCode: { id: 'Kode Resmi Peserta', ar: 'رمز المشارك الرسمي' },
    awaitingId: { id: 'MENUNGGU ID', ar: 'في انتظار الرمز' },
    systemNotice: {
      id: 'Kuitansi ini dibuat secara otomatis dari Sistem Inti LMS BID V3.',
      ar: 'تم إنشاء هذا الإيصال تلقائيًا من النظام الأساسي.',
    },
    securityFingerprint: { id: 'Sidik Jari Keamanan', ar: 'بصمة الأمن' },
    btnClose: { id: 'Tutup Panel', ar: 'إغلاق اللوحة' },
    btnSave: { id: 'Simpan Data', ar: 'حفظ البيانات' },
    btnPrint: { id: 'Cetak Kuitansi', ar: 'طباعة الإيصال' },
  },
  profile: {
    administration: { id: 'ADMINISTRASI', ar: 'الإدارة' },
    title: { id: 'Profil Admin', ar: 'ملف المشرف' },
    subtitle: {
      id: 'Kelola data pribadi dan keamanan akun Anda.',
      ar: 'إدارة بياناتك الشخصية وأمان حسابك.',
    },
    passwordMismatch: {
      id: 'Konfirmasi password baru tidak cocok',
      ar: 'تأكيد كلمة المرور الجديدة غير متطابقة',
    },
    passwordTooShort: {
      id: 'Password baru minimal 6 karakter',
      ar: 'كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل',
    },
    successUpdate: { id: 'Profil berhasil diperbarui', ar: 'تم تحديث الملف الشخصي بنجاح' },
    failedUpdate: { id: 'Gagal memperbarui profil', ar: 'فشل تحديث الملف الشخصي' },
    personalData: { id: 'Data Diri', ar: 'البيانات الشخصية' },
    avatar: { id: 'Avatar', ar: 'الصورة الشخصية' },
    changePhoto: { id: 'Ganti Foto', ar: 'تغيير الصورة' },
    fullName: { id: 'Nama Lengkap', ar: 'الاسم الكامل' },
    emailDisabled: { id: 'Email (Tidak dapat diubah)', ar: 'البريد الإلكتروني (لا يمكن تعديله)' },
    phone: { id: 'Nomor Telepon', ar: 'رقم الهاتف' },
    gender: { id: 'Jenis Kelamin', ar: 'الجنس' },
    genderMale: { id: 'Laki-laki', ar: 'ذكر' },
    genderFemale: { id: 'Perempuan', ar: 'أنثى' },
    securityTitle: { id: 'Keamanan (Ubah Password)', ar: 'الأمان (تغيير كلمة المرور)' },
    securityDesc: {
      id: 'Biarkan kosong jika Anda tidak ingin mengubah password.',
      ar: 'اتركه فارغاً إذا كنت لا ترغب في تغيير كلمة المرور.',
    },
    currentPassword: { id: 'Password Saat Ini', ar: 'كلمة المرور الحالية' },
    newPassword: { id: 'Password Baru', ar: 'كلمة المرور الجديدة' },
    confirmNewPassword: { id: 'Konfirmasi Password Baru', ar: 'تأكيد كلمة المرور الجديدة' },
    btnSaving: { id: 'Menyimpan...', ar: 'جاري الحفظ...' },
    btnSave: { id: 'Simpan Perubahan', ar: 'حفظ التغييرات' },
  },
  notif: {
    notifApproveSuccessTitle: { id: 'Otorisasi Selesai', ar: 'اكتمل الاعتماد' },
    notifApproveSuccessMsg: {
      id: 'Peserta telah berhasil diotorisasi dan diberikan kredensial.',
      ar: 'تم اعتماد المشارك بنجاح ومنحه بيانات الاعتماد.',
    },
    notifApproveErrorTitle: { id: 'Otorisasi Gagal', ar: 'فشل الاعتماد' },
    notifApproveErrorMsg: {
      id: 'Terjadi kesalahan saat mengotorisasi peserta.',
      ar: 'حدث خطأ أثناء اعتماد المشارك.',
    },
    notifRejectSuccessTitle: { id: 'Pendaftaran Ditolak', ar: 'تم رفض الطلب' },
    notifRejectSuccessMsg: {
      id: 'Pendaftaran telah ditolak dan catatan diperbarui.',
      ar: 'تم رفض التسجيل وتحديث السجلات.',
    },
    notifRejectErrorTitle: { id: 'Tindakan Gagal', ar: 'فشلت العملية' },
    notifRejectErrorMsg: {
      id: 'Pendaftaran tidak dapat ditolak saat ini.',
      ar: 'تعذر رفض التسجيل في هذا الوقت.',
    },
    notifRevisionSuccessTitle: { id: 'Revisi Dikirim', ar: 'تم إرسال طلب المراجعة' },
    notifRevisionSuccessMsg: {
      id: 'Instruksi telah dikirim ke pemohon untuk koreksi data.',
      ar: 'تم إرسال التعليمات إلى مقدم الطلب لتصحيح البيانات.',
    },
    notifRevisionErrorTitle: { id: 'Pengiriman Gagal', ar: 'فشل الإرسال' },
    notifRevisionErrorMsg: {
      id: 'Tidak dapat mengirim instruksi revisi ke pemohon.',
      ar: 'تعذر إرسال تعليمات المراجعة إلى مقدم الطلب.',
    },
    notifAssignSuccessTitle: { id: 'Kohort Terkalibrasi', ar: 'تمت معايرة الدفعة' },
    notifAssignSuccessMsg: {
      id: 'Entitas berhasil diintegrasikan ke dalam saluran komunikasi.',
      ar: 'تم دمج الكيان بنجاح في قناة الاتصال.',
    },
    notifAssignErrorTitle: { id: 'Kalibrasi Gagal', ar: 'فشلت المعايرة' },
    notifAssignErrorMsg: {
      id: 'Gagal mengintegrasikan entitas ke dalam saluran komunikasi.',
      ar: 'فشل دمج الكيان في قناة الاتصال.',
    },
  },
  rejectionModal: {
    protocolLabel: { id: 'Protokol Penolakan', ar: 'بروتوكول الرفض' },
    title: { id: 'Penolakan Aplikasi', ar: 'رفض الطلب' },
    desc: {
      id: 'Tuliskan alasan penolakan pendaftaran untuk {name}. Tindakan ini akan mengirimkan pemberitahuan resmi.',
      ar: 'اكتب سبب رفض تسجيل {name}. سيؤدي هذا الإجراء إلى إرسال إشعار رسمي.',
    },
    labelRemarks: { id: 'Catatan Penolakan (Wajib)', ar: 'ملاحظات الرفض (مطلوب)' },
    placeholder: {
      id: 'Tulis alasan penolakan secara detail...',
      ar: 'اكتب سبب الرفض بالتفصيل...',
    },
    cancel: { id: 'Batal', ar: 'إلغاء' },
    confirm: { id: 'Tolak Pendaftaran', ar: 'رفض التسجيل' },
  },
  toast: {
    labelSuccess: { id: 'Berhasil', ar: 'نجاح' },
    labelError: { id: 'Gagal', ar: 'فشل' },
    labelWarning: { id: 'Peringatan', ar: 'تحذير' },
    labelInfo: { id: 'Informasi', ar: 'معلومات' },
    ariaDismiss: { id: 'Tutup notifikasi', ar: 'إغلاق الإشعار' },
  },
  materialModal: {
    uploadErrorFallback: { id: 'Gagal mengunggah file', ar: 'فشل تحميل الملف' },
    typeVideo: { id: 'VIDEO', ar: 'فيديو' },
    typeAudio: { id: 'AUDIO', ar: 'صوت' },
    typeDocument: { id: 'DOKUMEN', ar: 'مستند' },
    typeQuiz: { id: 'KUIS', ar: 'اختبار' },
    typeReflection: { id: 'REFLEKSI', ar: 'تأمل' },
    typeDiscussion: { id: 'DISKUSI', ar: 'نقاش' },
    modeEdit: { id: 'Mode Edit', ar: 'وضع التعديل' },
    modeCreate: { id: 'Materi Baru', ar: 'مادة جديدة' },
    deleteMaterial: { id: 'Hapus materi', ar: 'حذف المادة' },
    deleteMaterialTitle: { id: 'Hapus materi secara permanen?', ar: 'حذف المادة نهائياً؟' },
    deleteMaterialDescription: {
      id: 'Tindakan ini menghapus materi dan aset yang dikelola sistem. Periksa dampaknya sebelum melanjutkan.',
      ar: 'سيحذف هذا الإجراء المادة والأصول التي يديرها النظام. راجع التأثير قبل المتابعة.',
    },
    deleteCheckingImpact: { id: 'Memeriksa dampak penghapusan…', ar: 'جارٍ فحص تأثير الحذف…' },
    deleteImpactError: {
      id: 'Dampak penghapusan tidak dapat diperiksa. Penghapusan dibatalkan demi keamanan.',
      ar: 'تعذر فحص تأثير الحذف. تم إلغاء الحذف حفاظاً على الأمان.',
    },
    deleteImpactTitle: { id: 'Dampak penghapusan', ar: 'تأثير الحذف' },
    deleteImpactMedia: {
      id: '{count} media atau berkas akan dihapus.',
      ar: 'سيُحذف {count} من الوسائط أو الملفات.',
    },
    deleteImpactEvaluations: {
      id: '{count} evaluasi terkait akan dihapus.',
      ar: 'سيُحذف {count} من التقييمات المرتبطة.',
    },
    deleteImpactCompletions: {
      id: '{count} progres penyelesaian peserta akan dihapus.',
      ar: 'سيُحذف {count} من سجلات إكمال المشاركين.',
    },
    deleteImpactAttempts: {
      id: '{count} percobaan atau nilai evaluasi akan dihapus.',
      ar: 'سيُحذف {count} من محاولات أو درجات التقييم.',
    },
    deleteImpactReflections: {
      id: '{count} refleksi peserta dipertahankan, tetapi tidak lagi tertaut ke materi ini.',
      ar: 'سيتم الاحتفاظ بـ {count} من تأملات المشاركين، لكنها لن ترتبط بهذه المادة.',
    },
    deleteConfirmationRequired: {
      id: 'Materi ini sudah memiliki data peserta. Penghapusan tidak dapat dibatalkan.',
      ar: 'تحتوي هذه المادة على بيانات مشاركين. لا يمكن التراجع عن الحذف.',
    },
    deleteConfirmationLabel: { id: 'Ketik HAPUS untuk melanjutkan', ar: 'اكتب HAPUS للمتابعة' },
    deleteConfirmationPlaceholder: { id: 'HAPUS', ar: 'HAPUS' },
    deleteErrorFallback: {
      id: 'Materi tidak dapat dihapus. Coba lagi.',
      ar: 'تعذر حذف المادة. حاول مرة أخرى.',
    },
    learningContext: { id: 'Ditempatkan pada', ar: 'يوضع في' },
    ariaCloseEditor: { id: 'Tutup editor materi', ar: 'إغلاق محرر المادة' },
    editorNavigation: { id: 'Navigasi editor materi', ar: 'تنقل محرر المادة' },
    tabDetails: { id: 'Detail', ar: 'التفاصيل' },
    tabSource: { id: 'Sumber', ar: 'المصدر' },
    tabSettings: { id: 'Pengaturan', ar: 'الإعدادات' },
    tabAssessment: { id: 'Evaluasi', ar: 'التقييم' },
    tabAssets: { id: 'Aset', ar: 'الأصول' },
    stepOne: { id: 'Langkah 1 dari 3', ar: 'الخطوة ١ من ٣' },
    stepTwo: { id: 'Langkah 2 dari 3', ar: 'الخطوة ٢ من ٣' },
    stepThree: { id: 'Langkah 3 dari 3', ar: 'الخطوة ٣ من ٣' },
    stepFour: { id: 'Setelah materi tersimpan', ar: 'بعد حفظ المادة' },
    stepFive: { id: 'Aset pendukung', ar: 'الأصول المساندة' },
    detailsHeading: { id: 'Identitas materi', ar: 'هوية المادة' },
    detailsDescription: {
      id: 'Tentukan judul, jenis, dan ringkasan yang akan dibaca peserta.',
      ar: 'حدد العنوان والنوع والملخص الذي سيقرأه المشارك.',
    },
    titleHelper: {
      id: 'Gunakan judul singkat dan mudah dipindai.',
      ar: 'استخدم عنواناً مختصراً وسهل التصفح.',
    },
    descriptionHelper: {
      id: 'Jelaskan tujuan atau konteks materi bila diperlukan.',
      ar: 'اشرح هدف المادة أو سياقها عند الحاجة.',
    },
    sourceHeading: { id: 'Sumber pembelajaran', ar: 'مصدر التعلّم' },
    sourceDescription: {
      id: 'Gunakan satu tautan atau unggah berkas utama untuk materi ini.',
      ar: 'استخدم رابطاً واحداً أو حمّل الملف الأساسي لهذه المادة.',
    },
    labelSourceUrl: { id: 'Tautan sumber', ar: 'رابط المصدر' },
    urlHelper: {
      id: 'Tempel tautan yang dapat diakses peserta.',
      ar: 'الصق رابطاً يمكن للمشارك الوصول إليه.',
    },
    orUpload: { id: 'atau', ar: 'أو' },
    uploadSource: { id: 'Pilih berkas untuk diunggah', ar: 'اختر ملفاً للتحميل' },
    labelStorageProvider: { id: 'Lokasi penyimpanan', ar: 'موقع التخزين' },
    providerContabo: { id: 'Contabo — Singapura', ar: 'كونتابو — سنغافورة' },
    providerBiznetgio: { id: 'Biznetgio — Indonesia', ar: 'بيزنتجيو — إندونيسيا' },
    providerUnavailable: { id: 'Belum dikonfigurasi', ar: 'لم تتم تهيئته بعد' },
    browseStoredMedia: { id: 'Pilih dari media tersimpan', ar: 'اختيار من الوسائط المحفوظة' },
    storedMediaTitle: { id: 'Media yang sudah diunggah', ar: 'الوسائط المحمّلة سابقاً' },
    storedMediaDescription: {
      id: 'Cari dan gunakan kembali audio, video, atau dokumen tanpa mengunggah ulang.',
      ar: 'ابحث عن الصوت أو الفيديو أو المستند وأعد استخدامه دون تحميله مرة أخرى.',
    },
    storedMediaSearch: { id: 'Cari nama berkas media…', ar: 'ابحث عن اسم ملف الوسائط…' },
    storedMediaLoading: { id: 'Memuat media tersimpan…', ar: 'جارٍ تحميل الوسائط المحفوظة…' },
    storedMediaEmpty: { id: 'Tidak ada media yang cocok.', ar: 'لا توجد وسائط مطابقة.' },
    storedMediaSelect: { id: 'Gunakan media ini', ar: 'استخدم هذه الوسائط' },
    storedMediaMore: { id: 'Tampilkan media berikutnya', ar: 'عرض الوسائط التالية' },
    storedMediaLoadError: {
      id: 'Media tersimpan tidak dapat dimuat. Periksa konfigurasi penyimpanan.',
      ar: 'تعذر تحميل الوسائط المحفوظة. تحقق من إعدادات التخزين.',
    },
    previewFallbackTitle: { id: 'Pratinjau materi', ar: 'معاينة المادة' },
    previewEmptyTitle: { id: 'Pratinjau siap ditampilkan', ar: 'المعاينة جاهزة للعرض' },
    sourceNotRequiredTitle: {
      id: 'Jenis materi ini tidak memerlukan berkas utama',
      ar: 'هذا النوع من المواد لا يتطلب ملفاً رئيسياً',
    },
    sourceNotRequiredDescription: {
      id: 'Simpan materi terlebih dahulu, lalu hubungkan evaluasi atau kelola aset pendukung dari tab berikutnya.',
      ar: 'احفظ المادة أولاً، ثم اربط التقييم أو أدر الأصول المساندة من التبويب التالي.',
    },
    settingsHeading: { id: 'Atur urutan dan akses', ar: 'ضبط الترتيب والوصول' },
    settingsDescription: {
      id: 'Pengaturan ini menentukan posisi materi dan aturan penyelesaiannya.',
      ar: 'تحدد هذه الإعدادات موضع المادة وقواعد إكمالها.',
    },
    durationHelper: {
      id: 'Gunakan 0 bila durasi belum ditentukan.',
      ar: 'استخدم ٠ إذا لم تُحدد المدة بعد.',
    },
    orderHelper: {
      id: 'Nomor lebih kecil akan tampil lebih dahulu.',
      ar: 'ستظهر الأرقام الأصغر أولاً.',
    },
    requiredHelper: {
      id: 'Peserta perlu menuntaskan materi ini sebelum melanjutkan.',
      ar: 'يلزم المشارك إكمال هذه المادة قبل المتابعة.',
    },
    assessmentCreateHelper: {
      id: 'Buat evaluasi baru untuk materi ini, atau hubungkan evaluasi yang sudah tersedia.',
      ar: 'أنشئ تقييماً جديداً لهذه المادة أو اربط تقييماً متاحاً.',
    },
    labelAssetLink: { id: 'Tautan aset pendukung', ar: 'رابط الأصل المساند' },
    attachLink: { id: 'Tambahkan tautan', ar: 'إضافة الرابط' },
    deleteAsset: { id: 'Hapus aset', ar: 'حذف الأصل' },
    saveHelper: {
      id: 'Perubahan disimpan ke alur belajar program ini.',
      ar: 'تُحفظ التغييرات في مسار تعلّم هذا البرنامج.',
    },
    titleEdit: { id: 'Ubah Detail Materi', ar: 'تعديل تفاصيل المادة' },
    titleCreate: { id: 'Tambah Materi Baru', ar: 'إضافة مادة جديدة' },
    subtitleEdit: {
      id: 'Perbarui parameter operasional dan aset materi',
      ar: 'تحديث معلمات المادة وأصولها',
    },
    subtitleCreate: {
      id: 'Daftarkan materi pembelajaran baru ke alur program',
      ar: 'تسجيل مادة تعليمية جديدة في مسار البرنامج',
    },
    labelAssetTitle: { id: 'Judul Materi', ar: 'عنوان المادة' },
    placeholderTitle: { id: 'Misal: Pengantar Akidah', ar: 'مثال: مقدمة في العقيدة' },
    labelAssetType: { id: 'Tipe Materi', ar: 'نوع المادة' },
    labelOperationalContext: {
      id: 'Konteks Operasional (Deskripsi)',
      ar: 'السياق التشغيلي (الوصف)',
    },
    placeholderContext: {
      id: 'Tulis penjelasan singkat materi...',
      ar: 'اكتب وصفاً موجزاً للمادة...',
    },
    formatVideo: {
      id: 'Format: MP4, WebM (Maks. 500MB)',
      ar: 'الصيغة: MP4, WebM (الأقصى 500 ميجابايت)',
    },
    formatAudio: {
      id: 'Format: MP3, WAV (Maks. 50MB)',
      ar: 'الصيغة: MP3, WAV (الأقصى 50 ميجابايت)',
    },
    formatDocument: { id: 'Format: PDF (Maks. 20MB)', ar: 'الصيغة: PDF (الأقصى 20 ميجابايت)' },
    urlPlaceholderVideo: {
      id: 'Masukkan tautan video (YouTube, Vimeo, dll)...',
      ar: 'أدخل رابط الفيديو (يوتيوب، فيميو، إلخ)...',
    },
    urlPlaceholderAudio: { id: 'Masukkan tautan audio...', ar: 'أدخل رابط الصوت...' },
    urlPlaceholderDocument: { id: 'Masukkan tautan dokumen...', ar: 'أدخل رابط المستند...' },
    uploading: { id: 'Mengunggah...', ar: 'جاري التحميل...' },
    uploadLabel: { id: 'Unggah Berkas {type}', ar: 'تحميل ملف {type}' },
    uploadHintVideo: {
      id: 'Tarik & lepas file video di sini, atau klik untuk memilih',
      ar: 'اسحب وأفلت ملف الفيديو هنا، أو انقر للاختيار',
    },
    uploadHintAudio: {
      id: 'Tarik & lepas file audio di sini, atau klik untuk memilih',
      ar: 'اسحب وأفلت ملف الصوت هنا، أو انقر للاختيار',
    },
    uploadHintDocument: {
      id: 'Tarik & lepas file dokumen di sini, atau klik untuk memilih',
      ar: 'اسحب وأفلت ملف المستند هنا، atau انقر للاختيار',
    }, // wait, 'أو انقر للاختيار'
    livePreview: { id: 'Pratinjau Langsung', ar: 'معاينة مباشرة' },
    live: { id: 'AKTIF', ar: 'مباشر' },
    awaitingMedia: {
      id: 'Menunggu Berkas Media / Tautan...',
      ar: 'في انتظار ملف الوسائط / الرابط...',
    },
    evaluasiMateriTitle: { id: 'Evaluasi Materi (Kuis)', ar: 'تقييم المادة (اختبار)' },
    evaluasiMateriBadge: { id: 'KUIS', ar: 'اختبار' },
    evaluasiMateriDesc: {
      id: 'Hubungkan kuis evaluasi untuk mengukur pemahaman peserta pada materi ini.',
      ar: 'ربط اختبار تقييمي لقياس فهم المشارك لهذه المادة.',
    },
    evaluasiMateriBtn: { id: 'Hubungkan Evaluasi', ar: 'ربط التقييم' },
    linkedNodes: { id: 'Evaluasi Terhubung', ar: 'التقييمات المرتبطة' },
    unlinkTitle: { id: 'Putuskan Hubungan', ar: 'فصل الارتباط' },
    linkExisting: { id: 'Hubungkan Evaluasi yang Ada', ar: 'ربط تقييم موجود' },
    linkSelectPlaceholder: {
      id: 'Pilih evaluasi untuk dihubungkan...',
      ar: 'اختر التقييم لربطه...',
    },
    linkBtn: { id: 'Hubungkan', ar: 'ربط' },
    labelDuration: { id: 'Estimasi Durasi (Menit)', ar: 'المدة المقدرة (بالدقائق)' },
    placeholderDuration: { id: 'Misal: 15', ar: 'مثال: 15' },
    labelSequenceOrder: { id: 'Urutan Sekuensial (Nomor)', ar: 'الترتيب التسلسلي (الرقم)' },
    labelEnforcementNode: { id: 'Prasyarat Sekuensial', ar: 'المتطلبات التسلسلية' },
    labelMandatoryCompletion: {
      id: 'Wajib Diselesaikan sebelum materi berikutnya',
      ar: 'يجب إكماله قبل المادة التالية',
    },
    mediaRepoTitle: { id: 'Perpustakaan Aset Media', ar: 'مكتبة أصول الوسائط' },
    mediaRepoSubtitle: {
      id: 'Pilih aset dari penyimpanan awan atau unggah aset baru.',
      ar: 'اختر أصلاً من التخزين السحابي أو قم بتحميل أصل جديد.',
    },
    mediaRepoPlaceholder: {
      id: 'Cari berdasarkan nama berkas...',
      ar: 'البحث عن طريق اسم الملف...',
    },
    uploadFailedAlert: { id: 'Gagal mengunggah file: {error}', ar: 'فشل تحميل الملف: {error}' },
    mediaRepoUpload: { id: 'Unggah Aset Baru', ar: 'تحميل أصل جديد' },
    repoUntitled: { id: 'Aset Tanpa Judul', ar: 'أصل بدون عنوان' },
    deleteAssetConfirm: {
      id: 'Apakah Anda yakin ingin menghapus aset ini secara permanen?',
      ar: 'هل أنت متأكد من حذف هذا الأصل نهائياً؟',
    },
    repoEmpty: { id: 'Perpustakaan Aset Kosong', ar: 'مكتبة الأصول فارغة' },
    repoEmptyDesc: {
      id: 'Belum ada aset media yang diunggah ke penyimpanan cloud.',
      ar: 'لم يتم تحميل أي أصول وسائط إلى التخزين السحابي بعد.',
    },
    repoLocked: { id: 'Perpustakaan Aset Terkunci', ar: 'مكتبة الأصول مغلقة' },
    repoLockedDesc: {
      id: 'Unggah atau hubungkan media terlebih dahulu untuk mengaktifkan perpustakaan aset.',
      ar: 'قم بتحميل أو ربط الوسائط أولاً لتفعيل مكتبة الأصول.',
    },
    abortBtn: { id: 'Batal', ar: 'إلغاء' },
    submitEdit: { id: 'Simpan Perubahan', ar: 'حفظ التغييرات' },
    submitCreate: { id: 'Daftarkan Materi', ar: 'تسجيل المادة' },
  },
  evaluationModal: {
    title: { id: 'Parameter Evaluasi', ar: 'معلمات التقييم' },
    subtitle: {
      id: 'Atur detail, ambang batas kelulusan, dan batas waktu pengerjaan.',
      ar: 'ضبط التفاصيل، عتبة النجاح، والحد الزمني للحل.',
    },
    labelTitle: { id: 'Judul Evaluasi', ar: 'عنوان التقييم' },
    placeholderTitle: { id: 'Misal: Kuis Pertama Akidah', ar: 'مثال: الاختبار الأول في العقيدة' },
    labelContext: { id: 'Operational Context (Deskripsi)', ar: 'السياق التشغيلي (الوصف)' },
    placeholderContext: {
      id: 'Tulis deskripsi atau instruksi pengerjaan...',
      ar: 'اكتب وصفاً أو تعليمات للحل...',
    },
    labelCategory: { id: 'Kategori Penilaian', ar: 'فئة التقييم' },
    categoryQuiz: { id: 'Kuis Materi', ar: 'اختبار مادة' },
    categoryExam: { id: 'Ujian Akhir', ar: 'الامتحان النهائي' },
    categorySurvey: { id: 'Survei Umpan Balik', ar: 'استطلاع رأي' },
    labelPassing: { id: 'Ambang Kelulusan (%)', ar: 'عتبة النجاح (%)' },
    labelAttempts: { id: 'Batas Percobaan', ar: 'حد المحاولات' },
    labelTimeLimit: { id: 'Batas Waktu (Menit)', ar: 'الحد الزمني (بالدقائق)' },
    placeholderTimeLimit: { id: 'Bebas (Tanpa Batas)', ar: 'بدون حد' },
    labelRandomPool: {
      id: 'Ambil acak N soal dari bank',
      ar: 'اختيار عشوائي N سؤال من المستودع',
    },
    placeholderRandomPool: {
      id: 'Kosongkan = pakai semua soal',
      ar: 'اتركه فارغاً لاستخدام جميع الأسئلة',
    },
    descRandomPool: {
      id: 'Jika diisi, sistem akan mengambil soal secara acak dari bank soal sejumlah N untuk setiap percobaan.',
      ar: 'في حال التعبئة، سيقوم النظام بسحب عدد N من الأسئلة بشكل عشوائي من المستودع لكل محاولة.',
    },
    abort: { id: 'Batal', ar: 'إلغاء' },
    submit: { id: 'Simpan Konfigurasi', ar: 'حفظ التكوين' },
  },
  levelModal: {
    titleEdit: { id: 'Ubah Detail Level', ar: 'تعديل تفاصيل المستوى' },
    titleCreate: { id: 'Tambah Level Baru', ar: 'إضافة مستوى جديد' },
    subtitleEdit: { id: 'Sesuaikan konfigurasi untuk {name}', ar: 'ضبط التكوين لـ {name}' },
    subtitleCreate: {
      id: 'Buat level baru untuk alur belajar',
      ar: 'إنشاء مستوى جديد لمسار التعلم',
    },
    labelName: { id: 'Nama Level', ar: 'اسم المستوى' },
    placeholderName: { id: 'Misal: Mustawa 1', ar: 'مثال: المستوى 1' },
    labelDesc: { id: 'Deskripsi / Konteks Operasional', ar: 'الوصف / السياق التشغيلي' },
    placeholderDesc: { id: 'Tulis deskripsi level...', ar: 'اكتب وصف المستوى...' },
    labelAccessModel: { id: 'Model Akses Materi', ar: 'نموذج الوصول للمواد' },
    sequentialLabel: {
      id: 'Akses Sekuensial (Wajib berurutan)',
      ar: 'وصول متسلسل (مطلوب بالترتيب)',
    },
    abort: { id: 'Batal', ar: 'إلغاء' },
    submitPending: { id: 'Menyimpan...', ar: 'جاري الحفظ...' },
    submitEdit: { id: 'Simpan Perubahan', ar: 'حفظ التغييرات' },
    submitCreate: { id: 'Daftarkan Level', ar: 'تسجيل المستوى' },
  },
  shell: {
    ariaCloseSidebar: { id: 'Tutup bilah samping', ar: 'إغلاق الشريط الجانبي' },
    ariaOpenSidebar: { id: 'Buka bilah samping', ar: 'فتح الشريط الجانبي' },
    collapseSidebar: { id: 'Ciutkan Menu', ar: 'طي القائمة' },
    expandSidebar: { id: 'Perluas Menu', ar: 'توسيع القائمة' },
    defaultUserName: { id: 'Pengguna', ar: 'المستخدم' },
    defaultRoleName: { id: 'Pengguna Portal', ar: 'مستخدم البوابة' },
    notifications: { id: 'Notifikasi', ar: 'الإشعارات' },
  },
  inactivity: {
    warningTitle: { id: 'Peringatan Sesi Tidak Aktif', ar: 'تنبيه انتهاء الجلسة بسبب عدم النشاط' },
    warningDesc: {
      id: 'Sesi Anda akan otomatis diakhiri untuk menghemat sumber daya server dalam waktu:',
      ar: 'ستنتهي جلستك تلقائيًا لتوفير موارد الخادم في غضون:',
    },
    countdownSuffix: { id: 'detik', ar: 'ثانية' },
    stayLoggedIn: { id: 'Tetap Masuk', ar: 'البقاء مسجلاً' },
    logoutNow: { id: 'Keluar Sekarang', ar: 'تسجيل الخروج الآن' },
  },
  learningPathStudio: {
    tabTitle: { id: 'Kurikulum & Alur Belajar', ar: 'المنهج ومسار التعلم' },
    tabSubtitle: {
      id: 'Struktur bertingkat: Level > Tahapan/Tema > Mata Pelajaran > Pertemuan > Materi & Ujian',
      ar: 'هيكل متدرج: المستوى > المرحلة > المادة > الجلسة > المحتوى والاختبار',
    },
    addLevel: { id: 'Tambah Level', ar: 'إضافة مستوى' },
    addStage: { id: 'Tambah Tahapan / Tema', ar: 'إضافة مرحلة / موضوع' },
    addSubject: { id: 'Tambah Mata Pelajaran', ar: 'إضافة مادة دراسية' },
    addSession: { id: 'Tambah Pertemuan', ar: 'إضافة جلسة' },
    addSubjectEvaluation: { id: 'Evaluasi Mata Pelajaran', ar: 'تقييم المادة الدراسية' },
    addSessionEvaluation: { id: 'Kuis / Ujian Pertemuan', ar: 'اختبار / امتحان اللقاء' },
    sessionEvaluationOnly: { id: 'Sesi Kuis / Ujian', ar: 'جلسة اختبار / امتحان' },
    sessionEvaluationOnlyDescription: {
      id: 'Pertemuan ini dapat digunakan khusus untuk kuis atau ujian tanpa menambahkan materi.',
      ar: 'يمكن استخدام هذه الجلسة للاختبار أو الامتحان فقط دون إضافة مواد تعليمية.',
    },
    bulkGenerateSessions: { id: 'Generate Banyak Pertemuan', ar: 'إنشاء جلسات متعددة' },
    noLevels: {
      id: 'Belum ada Level pada program ini. Klik tombol Tambah Level di atas.',
      ar: 'لا توجد مستويات بعد في هذا البرنامج.',
    },
    noStages: {
      id: 'Belum ada Tahapan / Tema pelajaran di level ini.',
      ar: 'لا توجد مراحل بعد في هذا المستوى.',
    },
    noSubjects: {
      id: 'Belum ada Mata Pelajaran pada tahapan ini.',
      ar: 'لا توجد مواد دراسية بعد في هذه المرحلة.',
    },
    fixSubjectOrder: { id: 'Rapikan Urutan Mapel', ar: 'ترتيب المواد الدراسية' },
    fixSubjectOrderSuccess: {
      id: 'Urutan mata pelajaran pada tahapan ini berhasil dirapikan.',
      ar: 'تم ترتيب المواد الدراسية في هذه المرحلة بنجاح.',
    },
    fixSubjectOrderError: {
      id: 'Gagal merapikan urutan mata pelajaran. Silakan coba lagi.',
      ar: 'تعذّر ترتيب المواد الدراسية. يرجى المحاولة مجددًا.',
    },
    subjectOrder: { id: 'Urutan {order}', ar: 'الترتيب {order}' },
    moveSubjectEarlier: { id: 'Geser mata pelajaran ke atas', ar: 'انقل المادة إلى الأعلى' },
    moveSubjectLater: { id: 'Geser mata pelajaran ke bawah', ar: 'انقل المادة إلى الأسفل' },
    noSessions: {
      id: 'Belum ada Pertemuan pada mata pelajaran ini. Tambahkan pertemuan atau generate sekaligus.',
      ar: 'لا توجد جلسات بعد في هذه المادة.',
    },
    badgeSequential: { id: 'Berurutan', ar: 'متسلسل' },
    badgeFlexible: { id: 'Fleksibel', ar: 'مرن' },
    badgeEvaluationGate: { id: 'Evaluasi wajib lulus', ar: 'يلزم اجتياز التقييم' },
    badgePrerequisite: { id: 'Syarat:', ar: 'المتطلب:' },
    badgeKitab: { id: 'Kitab:', ar: 'الكتاب:' },
    badgeTeacher: { id: 'Pengajar:', ar: 'المعلم:' },
    deleteStageConfirm: {
      id: 'Apakah Anda yakin ingin menghapus tahapan ini beserta seluruh mata pelajaran dan pertemuan di dalamnya?',
      ar: 'هل أنت متأكد من حذف هذه المرحلة وجميع المواد والجلسات بداخلها؟',
    },
    deleteSubjectConfirm: {
      id: 'Apakah Anda yakin ingin menghapus mata pelajaran ini beserta seluruh pertemuan di dalamnya?',
      ar: 'هل أنت متأكد من حذف هذه المادة وجميع الجلسات بداخلها؟',
    },
    deleteSessionConfirm: {
      id: 'Apakah Anda yakin ingin menghapus pertemuan ini?',
      ar: 'هل أنت متأكد من حذف هذه الجلسة؟',
    },
  },
  stageModal: {
    titleCreate: { id: 'Tambah Tahapan / Tema Pelajaran', ar: 'إضافة مرحلة / موضوع دراسي' },
    titleEdit: { id: 'Edit Tahapan / Tema Pelajaran', ar: 'تعديل المرحلة / الموضوع الدراسي' },
    subtitleCreate: {
      id: 'Tahapan baru untuk Level {levelName}',
      ar: 'مرحلة جديدة للمستوى {levelName}',
    },
    subtitleEdit: {
      id: 'Perbarui konfigurasi tahapan pembelajaran',
      ar: 'تحديث إعدادات المرحلة التعليمية',
    },
    labelStageCode: {
      id: 'Kode Tahap (Opsional, misal: A, B, C, D)',
      ar: 'رمز المرحلة (اختياري، مثلاً: A, B, C, D)',
    },
    placeholderStageCode: { id: 'Contoh: A', ar: 'مثال: A' },
    labelName: { id: 'Nama Tahapan / Tema Pelajaran', ar: 'اسم المرحلة / الموضوع' },
    placeholderName: {
      id: 'Contoh: Tahap A - Adab & Manhaj Menuntut Ilmu',
      ar: 'مثال: المرحلة أ - آداب ومنهج طلب العلم',
    },
    labelDescription: {
      id: 'Deskripsi / Capaian Pembelajaran Tahap',
      ar: 'الوصف / مخرجات تعلم المرحلة',
    },
    placeholderDescription: {
      id: 'Tuliskan deskripsi atau ringkasan capaian tahap ini...',
      ar: 'اكتب وصفاً أو ملخصاً لمخرجات هذه المرحلة...',
    },
    labelSequential: { id: 'Wajib Selesai Berurutan', ar: 'يجب الإكمال بالتسلسل' },
    descSequential: {
      id: 'Jika aktif, mata pelajaran di dalam tahapan ini harus diselesaikan berurutan.',
      ar: 'إذا تم تفعيله، يجب إكمال المواد بالتسلسل.',
    },
    labelPassingScore: {
      id: 'KKM Kelulusan Tahapan (Nilai Minimal)',
      ar: 'درجة النجاح الدنيا للمرحلة (KKM)',
    },
    descPassingScore: {
      id: 'Standar nilai minimal kelulusan untuk menyelesaikan tahapan ini (ujian/evaluasi akhir tahap).',
      ar: 'الحد الأدنى لدرجة النجاح لاجتياز هذه المرحلة (الاختبار النهائي للمرحلة).',
    },
    btnSubmitCreate: { id: 'Simpan Tahapan', ar: 'حفظ المرحلة' },
    btnSubmitEdit: { id: 'Simpan Perubahan', ar: 'حفظ التعديلات' },
    btnSaving: { id: 'Menyimpan...', ar: 'جاري الحفظ...' },
  },
  subjectModal: {
    titleCreate: { id: 'Tambah Mata Pelajaran', ar: 'إضافة مادة دراسية' },
    titleEdit: { id: 'Edit Mata Pelajaran', ar: 'تعديل المادة الدراسية' },
    saveFailed: {
      id: 'Gagal menyimpan mata pelajaran. Periksa kembali urutan dan coba lagi.',
      ar: 'تعذّر حفظ المادة الدراسية. تحقّق من الترتيب ثم حاول مجددًا.',
    },
    labelName: { id: 'Nama Mata Pelajaran', ar: 'اسم المادة الدراسية' },
    placeholderName: { id: 'Contoh: Kendala Menuntut Ilmu (1)', ar: 'مثال: عوائق طلب العلم (1)' },
    labelCode: { id: 'Kode Mata Pelajaran (Opsional)', ar: 'رمز المادة (اختياري)' },
    placeholderCode: { id: 'Contoh: BID-L1-A1', ar: 'مثال: BID-L1-A1' },
    labelKitab: { id: 'Kitab Rujukan / Sumber', ar: 'الكتاب المعتمد / المرجع' },
    placeholderKitab: { id: 'Contoh: Awaiq ath-Thullab', ar: 'مثال: عوائق الطلب' },
    labelTeacher: { id: 'Pengajar / Asatidz', ar: 'المعلم / الأستاذ' },
    placeholderTeacher: {
      id: 'Contoh: Bpk. Dadang / Ustadz Sigit Santoso',
      ar: 'مثال: الشيخ فلان',
    },
    labelPassingScore: { id: 'KKM Kelulusan (Nilai Minimal)', ar: 'درجة النجاح الدنيا (KKM)' },
    labelSequential: { id: 'Alur Materi Berurutan', ar: 'تسلسل المواد الإلزامي' },
    descSequential: {
      id: 'Pertemuan/materi di dalam mata pelajaran ini wajib diselesaikan secara berurutan.',
      ar: 'يجب إكمال جلسات هذه المادة بشكل متسلسل.',
    },
    labelEvaluationGate: {
      id: 'Wajib Lulus Evaluasi sebelum Materi Berikutnya',
      ar: 'اجتياز التقييم مطلوب قبل المادة التالية',
    },
    descEvaluationGate: {
      id: 'Jika aktif, peserta wajib menyelesaikan materi dan lulus seluruh kuis/ujian pada materi atau pertemuan sebelumnya sebelum membuka materi berikutnya.',
      ar: 'عند التفعيل، يجب على المشارك إكمال المادة واجتياز جميع اختبارات المادة أو الجلسة السابقة قبل فتح المادة التالية.',
    },
    labelPrerequisite: {
      id: 'Mata Pelajaran Prasyarat (Opsional)',
      ar: 'المادة المتطلبة السابقة (اختياري)',
    },
    nonePrerequisite: {
      id: 'Tanpa prasyarat',
      ar: 'بدون متطلب سابق',
    },
    choosePrerequisite: { id: 'Pilih mata pelajaran', ar: 'اختر المادة الدراسية' },
    prerequisiteSaveMismatch: {
      id: 'Prasyarat belum tersimpan sesuai pilihan Anda. Pilihan di formulir tetap dipertahankan. Coba simpan kembali; jika masih sama, hubungi BID Support dengan nama program dan mata pelajaran.',
      ar: 'لم يُحفظ المتطلب السابق وفق اختيارك. يبقى اختيار النموذج محفوظًا. حاول الحفظ مجددًا؛ إذا استمرت المشكلة، تواصل مع دعم BID مع اسم البرنامج والمادة.',
    },
    prerequisiteSaveUnverified: {
      id: 'Perubahan sudah dikirim, tetapi hasil penyimpanan prasyarat belum dapat diperiksa. Pilihan Anda tetap dipertahankan. Periksa koneksi, lalu coba simpan kembali.',
      ar: 'أُرسلت التعديلات، لكن تعذر التحقق من حفظ المتطلب السابق. يبقى اختيارك محفوظًا. تحقق من الاتصال ثم حاول الحفظ مجددًا.',
    },
    noPrerequisiteHelp: {
      id: 'Tidak bergantung pada mata pelajaran lain. Aturan level, tahapan, urutan materi, dan evaluasi tetap berlaku.',
      ar: 'لا تعتمد على مادة أخرى. تبقى قواعد المستوى والمرحلة وتسلسل المواد والتقييم سارية.',
    },
    prerequisiteHelp: {
      id: 'Peserta harus memenuhi prasyarat mata pelajaran yang dipilih sebelum membuka mata pelajaran ini.',
      ar: 'يجب على المشارك استيفاء متطلبات المادة المحددة قبل فتح هذه المادة.',
    },
    searchPrerequisite: {
      id: 'Cari mata pelajaran prasyarat',
      ar: 'ابحث عن المادة المتطلبة السابقة',
    },
    searchPrerequisitePlaceholder: {
      id: 'Nama, kode, level, atau tahapan',
      ar: 'الاسم أو الرمز أو المستوى أو المرحلة',
    },
    selectedPrerequisite: { id: 'Prasyarat terpilih', ar: 'المتطلب السابق المحدد' },
    clearPrerequisiteSearch: { id: 'Hapus pencarian', ar: 'مسح البحث' },
    prerequisiteEmpty: {
      id: 'Belum ada mata pelajaran lain untuk dipilih. Gunakan Tanpa prasyarat atau tambahkan mata pelajaran terlebih dahulu.',
      ar: 'لا توجد مادة أخرى للاختيار. استخدم بدون متطلب سابق أو أضف مادة أولاً.',
    },
    prerequisiteNotFound: {
      id: 'Tidak ada mata pelajaran yang cocok. Ubah kata pencarian atau hapus pencarian.',
      ar: 'لا توجد مادة مطابقة. غيّر كلمات البحث أو امسح البحث.',
    },
    prerequisiteRequired: {
      id: 'Pilih satu mata pelajaran prasyarat atau gunakan Tanpa prasyarat sebelum menyimpan.',
      ar: 'اختر مادة متطلبة سابقة أو استخدم بدون متطلب سابق قبل الحفظ.',
    },
    prerequisiteUnavailable: {
      id: 'Prasyarat tersimpan tidak ada dalam daftar saat ini. Pilihan lama tetap dipertahankan; pilih pengganti atau Tanpa prasyarat untuk mengubahnya.',
      ar: 'المتطلب السابق المحفوظ غير موجود في القائمة الحالية. يُحتفظ بالاختيار القديم؛ اختر بديلاً أو بدون متطلب سابق لتغييره.',
    },
    closeEditor: { id: 'Tutup editor mata pelajaran', ar: 'إغلاق محرر المادة الدراسية' },
    subjectNumber: { id: 'Mata Pelajaran #{number}', ar: 'المادة الدراسية رقم {number}' },
    labelDescription: { id: 'Deskripsi / Pokok Bahasan', ar: 'الوصف / محاور الدرس' },
    placeholderDescription: {
      id: 'Tuliskan pokok bahasan atau ringkasan mata pelajaran...',
      ar: 'اكتب محاور المادة الدراسية...',
    },
    btnSubmitCreate: { id: 'Simpan Mata Pelajaran', ar: 'حفظ المادة' },
    btnSubmitEdit: { id: 'Simpan Perubahan', ar: 'حفظ التعديلات' },
    btnSaving: { id: 'Menyimpan...', ar: 'جاري الحفظ...' },
  },
  sessionModal: {
    titleCreate: { id: 'Tambah Pertemuan', ar: 'إضافة جلسة تعليمية' },
    titleEdit: { id: 'Edit Pertemuan', ar: 'تعديل الجلسة التعليمية' },
    modeCreate: { id: 'Pertemuan Baru', ar: 'جلسة جديدة' },
    modeEdit: { id: 'Mode Edit', ar: 'وضع التعديل' },
    subjectFallback: { id: 'Mata Pelajaran', ar: 'المادة الدراسية' },
    sessionLabel: { id: 'Pertemuan', ar: 'الجلسة' },
    editorDescription: {
      id: 'Atur informasi pertemuan, sesi tatap muka, dan materi belajar dalam alur yang ringkas.',
      ar: 'اضبط معلومات الجلسة واللقاء المباشر ومادة التعلّم في مسار مختصر.',
    },
    ariaCloseEditor: { id: 'Tutup editor pertemuan', ar: 'إغلاق محرر الجلسة' },
    editorNavigation: { id: 'Navigasi editor pertemuan', ar: 'تنقل محرر الجلسة' },
    tabDetails: { id: 'Detail', ar: 'التفاصيل' },
    tabLive: { id: 'Tatap Muka', ar: 'اللقاء المباشر' },
    tabMaterial: { id: 'Materi', ar: 'المادة' },
    tabReview: { id: 'Ringkasan', ar: 'الملخص' },
    stepOne: { id: 'Langkah 1 dari 4', ar: 'الخطوة ١ من ٤' },
    stepTwo: { id: 'Langkah 2 dari 4', ar: 'الخطوة ٢ من ٤' },
    stepThree: { id: 'Langkah 3 dari 4', ar: 'الخطوة ٣ من ٤' },
    stepFour: { id: 'Langkah 4 dari 4', ar: 'الخطوة ٤ من ٤' },
    detailsHeading: { id: 'Informasi pertemuan', ar: 'معلومات الجلسة' },
    detailsDescription: {
      id: 'Beri nama yang jelas agar peserta dapat mengenali urutan belajar.',
      ar: 'امنح اسماً واضحاً حتى يتعرّف المشارك على تسلسل التعلّم.',
    },
    liveHeading: { id: 'Sesi tatap muka', ar: 'الجلسة المباشرة' },
    liveDescription: {
      id: 'Opsional. Tambahkan tautan bila pertemuan ini memiliki kelas sinkron.',
      ar: 'اختياري. أضف الرابط إذا كانت لهذه الجلسة حصة متزامنة.',
    },
    liveHelper: {
      id: 'Peserta akan melihat tautan ini dari halaman pertemuan.',
      ar: 'سيرى المشارك هذا الرابط من صفحة الجلسة.',
    },
    testMeetingLink: { id: 'Uji tautan tatap muka', ar: 'اختبر رابط اللقاء المباشر' },
    genericLiveLink: { id: 'Tautan sesi langsung', ar: 'رابط الجلسة المباشرة' },
    materialHeading: { id: 'Materi pembelajaran', ar: 'مادة التعلّم' },
    materialDescription: {
      id: 'Tambahkan rekaman, video, audio, atau modul untuk dipelajari mandiri.',
      ar: 'أضف تسجيلاً أو فيديو أو صوتاً أو وحدة للدراسة الذاتية.',
    },
    materialsHeading: { id: 'Daftar materi pertemuan', ar: 'قائمة مواد الجلسة' },
    materialsDescription: {
      id: 'Satu pertemuan dapat memuat beberapa materi dengan jenis dan urutan berbeda.',
      ar: 'يمكن أن تضم الجلسة الواحدة عدة مواد بأنواع وترتيبات مختلفة.',
    },
    addMaterial: { id: 'Tambah materi', ar: 'إضافة مادة' },
    editMaterial: { id: 'Ubah materi', ar: 'تعديل المادة' },
    saveBeforeMaterialsTitle: { id: 'Simpan pertemuan terlebih dahulu', ar: 'احفظ الجلسة أولاً' },
    saveBeforeMaterialsDescription: {
      id: 'Setelah pertemuan tersimpan, Anda dapat menambahkan materi sebanyak yang diperlukan.',
      ar: 'بعد حفظ الجلسة، يمكنك إضافة عدد المواد التي تحتاجها.',
    },
    noMaterialsTitle: { id: 'Belum ada materi', ar: 'لا توجد مواد بعد' },
    noMaterialsDescription: {
      id: 'Tambahkan video, audio, dokumen, kuis, refleksi, atau diskusi untuk pertemuan ini.',
      ar: 'أضف فيديو أو صوتاً أو مستنداً أو اختباراً أو تأملاً أو نقاشاً لهذه الجلسة.',
    },
    mediaHelper: {
      id: 'Gunakan tautan yang dapat diakses peserta.',
      ar: 'استخدم رابطاً يمكن للمشارك الوصول إليه.',
    },
    pdfHelper: {
      id: 'Tambahkan modul atau dokumen pendukung bila tersedia.',
      ar: 'أضف وحدة أو مستنداً مسانداً عند توفره.',
    },
    materialPreview: { id: 'Pratinjau materi', ar: 'معاينة المادة' },
    showPreview: { id: 'Tampilkan', ar: 'إظهار' },
    hidePreview: { id: 'Sembunyikan', ar: 'إخفاء' },
    showPdfPreview: { id: 'Lihat pratinjau PDF', ar: 'عرض معاينة PDF' },
    hidePdfPreview: { id: 'Tutup pratinjau PDF', ar: 'إغلاق معاينة PDF' },
    previewEmptyTitle: { id: 'Pratinjau akan muncul di sini', ar: 'ستظهر المعاينة هنا' },
    previewEmptyDescription: {
      id: 'Tambahkan tautan video atau rekaman untuk memeriksanya sebelum disimpan.',
      ar: 'أضف رابط الفيديو أو التسجيل لمراجعته قبل الحفظ.',
    },
    externalMediaLink: { id: 'Tautan media eksternal', ar: 'رابط وسائط خارجي' },
    reviewHeading: { id: 'Periksa sebelum menyimpan', ar: 'راجع قبل الحفظ' },
    reviewDescription: {
      id: 'Pastikan informasi penting sudah tepat untuk peserta.',
      ar: 'تأكد من صحة المعلومات المهمة للمشارك.',
    },
    reviewSession: { id: 'Pertemuan', ar: 'الجلسة' },
    reviewLive: { id: 'Tatap muka', ar: 'اللقاء المباشر' },
    reviewMedia: { id: 'Media belajar', ar: 'وسائط التعلّم' },
    reviewMaterials: { id: 'Materi pertemuan', ar: 'مواد الجلسة' },
    reviewDocument: { id: 'Dokumen pendukung', ar: 'المستند المساند' },
    emptyValue: { id: 'Belum diberi judul', ar: 'لم يُضف عنوان بعد' },
    notSet: { id: 'Belum diatur', ar: 'لم يتم الإعداد' },
    minutesSuffix: { id: 'menit', ar: 'دقيقة' },
    saveHelper: {
      id: 'Simpan pertemuan, lalu kelola materi dari tab Materi.',
      ar: 'احفظ الجلسة، ثم أدر المواد من تبويب المواد.',
    },
    saveSuccess: {
      id: 'Perubahan pertemuan berhasil disimpan.',
      ar: 'تم حفظ تغييرات الجلسة بنجاح.',
    },
    labelSessionNumber: { id: 'Nomor Pertemuan', ar: 'رقم الجلسة' },
    labelTitle: { id: 'Judul Pertemuan', ar: 'عنوان الجلسة' },
    placeholderTitle: {
      id: 'Contoh: Pertemuan 1 - Pembukaan & Keutamaan Ilmu',
      ar: 'مثال: الجلسة 1 - الافتتاح وفضل العلم',
    },
    labelDuration: { id: 'Estimasi Durasi (Menit)', ar: 'المدة المقدرة (بالدقائق)' },
    placeholderDuration: { id: 'Contoh: 30', ar: 'مثال: 30' },
    labelMeetingUrl: {
      id: 'Tautan Tatap Muka (Google Meet / Zoom / Live Sync)',
      ar: 'رابط اللقاء المباشر (جوجل ميت / زووم)',
    },
    placeholderMeetingUrl: {
      id: 'https://meet.google.com/... atau https://zoom.us/j/...',
      ar: 'https://meet.google.com/... أو https://zoom.us/j/...',
    },
    labelMediaUrl: {
      id: 'Tautan Rekaman / Video (YouTube / Google Drive / Audio Stream)',
      ar: 'رابط التسجيل / الفيديو (يوتيوب / جوجل درايف / تسجيل صوتي)',
    },
    placeholderMediaUrl: {
      id: 'https://www.youtube.com/watch?v=... atau https://drive.google.com/...',
      ar: 'رابط يوتيوب أو جوجل درايف...',
    },
    labelPdfUrl: {
      id: 'Tautan Dokumen PDF / Modul / Diktat (Google Drive / Direct PDF)',
      ar: 'رابط ملف PDF / المذكرة (جوجل درايف / ملف مباشر)',
    },
    placeholderPdfUrl: {
      id: 'https://drive.google.com/... atau https://...',
      ar: 'رابط ملف PDF...',
    },
    previewVideo: { id: 'Pratinjau Video', ar: 'معاينة الفيديو' },
    previewPdf: { id: 'Pratinjau PDF', ar: 'معاينة PDF' },
    labelDescription: {
      id: 'Ringkasan Pokok Bahasan / Catatan Pertemuan',
      ar: 'ملخص محاور الجلسة / ملاحظات',
    },
    placeholderDescription: {
      id: 'Rangkuman poin penting pada pertemuan ini...',
      ar: 'ملخص النقاط المهمة في هذه الجلسة...',
    },
    btnSubmitCreate: { id: 'Simpan Pertemuan', ar: 'حفظ الجلسة' },
    btnSubmitEdit: { id: 'Simpan Perubahan', ar: 'حفظ التعديلات' },
    btnSaving: { id: 'Menyimpan...', ar: 'جاري الحفظ...' },
  },
  bulkSessionModal: {
    title: { id: 'Generate Banyak Pertemuan Sekaligus', ar: 'إنشاء جلسات متعددة تلقائياً' },
    subtitle: {
      id: 'Otomatis membuat sejumlah slot pertemuan (misal 10, 20, 40 pertemuan) secara instan.',
      ar: 'إنشاء مجموعة من الجلسات دفعة واحدة فورياً.',
    },
    labelCount: { id: 'Jumlah Pertemuan yang Dibuat', ar: 'عدد الجلسات المراد إنشاؤها' },
    descCount: {
      id: 'Misal masukkan 40 untuk membuat Pertemuan 1 s.d. 40',
      ar: 'مثال: أدخل 40 لإنشاء الجلسات من 1 إلى 40',
    },
    labelPrefix: { id: 'Awalan Judul (Prefix)', ar: 'بادئة العنوان' },
    placeholderPrefix: { id: 'Contoh: Pertemuan', ar: 'مثال: الجلسة' },
    btnSubmit: { id: 'Generate Pertemuan Sekarang', ar: 'إنشاء الجلسات الآن' },
    btnSaving: { id: 'Sedang Membuat...', ar: 'جاري الإنشاء...' },
  },
  crm: {
    pageTitle: { id: 'CRM & Retensi Peserta', ar: 'إدارة علاقات ومتابعة الدارسين' },
    pageSubtitle: {
      id: 'Pantau peserta yang tidak aktif, tindak lanjuti evaluasi yang belum diselesaikan, dan kelola interaksi pendampingan.',
      ar: 'متابعة الدارسين غير النشطين، ومتابعة الاختبارات المعلقة، وإدارة التواصل والتوجيه الأكاديمي.',
    },
    metricTotalAtRisk: { id: 'Total Peserta Berisiko', ar: 'إجمالي الدارسين المعرضين للانقطاع' },
    metricInactive7Days: { id: 'Tidak Login > 7 Hari', ar: 'لم يسجل دخول > 7 أيام' },
    metricInactive14Days: { id: 'Tidak Login > 14 Hari', ar: 'لم يسجل دخول > 14 يوماً' },
    metricInactive30Days: { id: 'Tidak Login > 30 Hari', ar: 'لم يسجل دخول > 30 يوماً' },
    metricMissingExams: { id: 'Belum Ikut Ujian', ar: 'اختبارات غير مكتملة' },
    metricContacted: { id: 'Sudah Di-Follow Up', ar: 'تم التواصل معهم' },
    metricResolved: { id: 'Kembali Aktif / Tuntas', ar: 'تمت المعالجة / عاد للنشاط' },
    filterInactivityAll: { id: 'Semua Status Keaktifan', ar: 'جميع حالات النشاط' },
    filterInactivity7Days: { id: 'Tidak Login > 7 Hari', ar: 'غير نشط > 7 أيام' },
    filterInactivity14Days: { id: 'Tidak Login > 14 Hari', ar: 'غير نشط > 14 يوماً' },
    filterInactivity30Days: { id: 'Tidak Login > 30 Hari', ar: 'غير نشط > 30 يوماً' },
    filterInactivityNoExam: { id: 'Belum Mengerjakan Ujian', ar: 'لم يؤدِ الاختبار' },
    filterInactivityStalled: { id: 'Progres Materi Terhenti', ar: 'توقف تقدم المادة' },
    filterOutreachAll: { id: 'Semua Status Kontak', ar: 'جميع حالات التواصل' },
    filterOutreachNever: { id: 'Belum Pernah Dikontak', ar: 'لم يتم التواصل بعد' },
    filterOutreachContacted: { id: 'Sudah Dikontak', ar: 'تم التواصل' },
    filterOutreachReplied: { id: 'Peserta Merespons', ar: 'تم الرد من الدارس' },
    filterOutreachResolved: { id: 'Selesai / Masalah Teratasi', ar: 'مكتمل / تم الحل' },
    filterOutreachNoResponse: { id: 'Tidak Ada Respons', ar: 'لا يوجد رد' },
    searchPlaceholder: {
      id: 'Cari nama, NIP, no WA, atau email...',
      ar: 'البحث بالاسم أو الرقم الأكاديمي أو الهاتف...',
    },
    tableHeaderParticipant: { id: 'Peserta', ar: 'الدارس' },
    tableHeaderPrograms: { id: 'Program & Progres', ar: 'البرنامج والتقدم' },
    tableHeaderInactivity: { id: 'Status Keaktifan', ar: 'حالة النشاط' },
    tableHeaderOutreach: { id: 'Riwayat Kontak Terakhir', ar: 'آخر تواصل' },
    tableHeaderActions: { id: 'Aksi Cepat', ar: 'إجراءات سريعة' },
    neverLoggedIn: { id: 'Belum pernah login', ar: 'لم يسجل دخول قط' },
    daysAgo: { id: 'hari lalu', ar: 'يوم مضت' },
    missedExamsCount: { id: 'ujian tertunda', ar: 'اختبار معلق' },
    stalledProgressBadge: { id: 'Progres terhenti', ar: 'توقف التقدم' },
    neverContacted: { id: 'Belum pernah dikontak', ar: 'لم يتم التواصل بعد' },
    btnQuickWa: { id: 'WhatsApp', ar: 'واتساب' },
    btnQuickEmail: { id: 'Email Reminder', ar: 'تذكير بريد' },
    btnLogOutreach: { id: 'Catat Hasil Kontak', ar: 'تسجيل تواصل' },
    btnViewHistory: { id: 'Riwayat Interaksi', ar: 'سجل المتابعة' },
    modalOutreachTitle: { id: 'Tindak Lanjut & Pendampingan Peserta', ar: 'متابعة وتوجيه الدارس' },
    tabWhatsApp: { id: 'Kirim WhatsApp', ar: 'إرسال واتساب' },
    tabEmail: { id: 'Kirim Email', ar: 'إرسال بريد' },
    tabManualLog: { id: 'Catat Panggilan / Interaksi', ar: 'تسجيل اتصال / ملاحظة' },
    labelTemplate: { id: 'Pilih Template Pesan', ar: 'اختر نموذج الرسالة' },
    templateInactivity: { id: 'Pengingat Tidak Aktif Login', ar: 'تذكير عدم تسجيل الدخول' },
    templateExam: { id: 'Pengingat Ujian / Evaluasi Tertunda', ar: 'تذكير الاختبار المعلق' },
    templateGeneral: { id: 'Sapaan & Tanya Kendala Umum', ar: 'استفسار عام عن الصعوبات' },
    labelPreviewMessage: { id: 'Pratinjau Pesan yang Dikirim', ar: 'معاينة نص الرسالة' },
    btnOpenWhatsApp: { id: 'Buka WhatsApp Web / App', ar: 'فتح تطبيق واتساب' },
    btnSendEmail: { id: 'Kirim Email Sekarang', ar: 'إرسال البريد الآن' },
    labelChannel: { id: 'Kanal Komunikasi', ar: 'وسيلة التواصل' },
    labelStatus: { id: 'Status Interaksi', ar: 'حالة التواصل' },
    labelNotes: { id: 'Catatan Notula / Respons Peserta', ar: 'ملاحظات / رد الدارس' },
    placeholderNotes: {
      id: 'Contoh: Peserta mengabarkan sedang sakit, akan melanjutkan modul pekan depan...',
      ar: 'مثال: أفاد الدارس بأنه مسافر وسيكمل الدراسة الأسبوع القادم...',
    },
    btnSaveLog: { id: 'Simpan Catatan Interaksi', ar: 'حفظ سجل التواصل' },
    historyModalTitle: {
      id: 'Riwayat Interaksi & Pendampingan',
      ar: 'سجل المتابعات والتواصل الأكاديمي',
    },
    noHistoryYet: {
      id: 'Belum ada catatan interaksi untuk peserta ini.',
      ar: 'لا توجد سجلات تواصل سابقة لهذا الدارس.',
    },
    emptyTitle: { id: 'Tidak Ada Peserta Berisiko', ar: 'لا يوجد دارسون غير نشطين' },
    emptyDesc: {
      id: 'Bagus sekali! Semua peserta dalam kondisi aktif belajar atau filter saat ini tidak menemukan data yang cocok.',
      ar: 'ممتاز! جميع الدارسين في حالة نشاط أو لا توجد نتائج مطابقة للمرشح المختار.',
    },
  },
};
